<template>
  <div class="editor-shell">

    <!-- 1. Editor actions — teleported INTO the shared app nav so the editor
         and the app share a single top bar. -->
    <Teleport to="#editor-nav-slot">
    <div class="editor-nav-actions" :data-editor-active="true">
      <nav class="breadcrumb">
        <span>{{ site?.name || 'Loading...' }}</span>
      </nav>
      <div class="topbar-divider"></div>
      <button class="btn btn-ghost flex items-center gap-1" v-if="site?.status === 'failed'" @click="retrySite" :disabled="retrying">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>
        {{ retrying ? 'Retrying…' : 'Retry' }}
      </button>
      <div class="topbar-actions">
        <button class="btn btn-ghost topbar-sidebar-toggle" @click="leftPanelOpen = !leftPanelOpen" title="Toggle sidebar" aria-label="Toggle sidebar">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" /></svg>
        </button>
        <!-- GitHub: link when synced, button to sync when not -->
        <a v-if="site?.repository_url" :href="site.repository_url" target="_blank" class="btn btn-ghost" title="GitHub">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
          </svg>
        </a>
        <button v-else-if="site?.status === 'active'" @click="handleSyncGitHub" :disabled="syncingGitHub" class="btn btn-glass">
          <svg class="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" />
          </svg>
          {{ syncingGitHub ? 'Connecting…' : 'Connect GitHub' }}
        </button>
        <!-- Settings -->
        <router-link :to="{ name: 'SiteSettings', params: { id: site?.id } }" class="btn btn-glass">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
          Site settings
        </router-link>
        <!-- SEO / AEO / GEO moved to Site Settings (the Overview). -->
        <!-- Publish pipeline: Draft → Sandbox → Live. Replaces the two bare
             buttons + status pill with one coherent staging model so the
             changes → sandbox → prod flow is legible at a glance. -->
        <div v-if="site?.status === 'active'" class="publish-pipeline" data-testid="publish-pipeline">
          <!-- Stage 1: your unpublished edits -->
          <div class="pl-stage" :class="{ 'pl-stage-active': recoveredPendingCount > 0 }">
            <span class="pl-stage-name">Draft</span>
            <span class="pl-stage-meta">{{ recoveredPendingCount > 0 ? `${recoveredPendingCount} unpublished` : 'Your edits' }}</span>
          </div>
          <svg class="pl-arrow" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          <!-- Stage 2: sandbox (staging preview) -->
          <div class="pl-stage">
            <span class="pl-stage-name">Sandbox</span>
            <div class="pl-stage-row">
              <button class="pl-btn" data-testid="publish-sandbox-btn" :disabled="publishingSandbox" @click="handlePublishSandbox">
                <svg v-if="publishingSandbox" class="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                {{ publishingSandbox ? 'Publishing…' : (site?.sandbox_url ? 'Update' : 'Publish') }}
              </button>
              <a v-if="site?.sandbox_url" :href="site.sandbox_url" target="_blank" rel="noopener" class="pl-link">View</a>
            </div>
          </div>
          <svg class="pl-arrow" :class="{ 'pl-arrow-muted': !site?.sandbox_url }" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          <!-- Stage 3: live (production) -->
          <div class="pl-stage">
            <span class="pl-stage-name">Live</span>
            <div class="pl-stage-row">
              <button
                class="pl-btn pl-btn-accent"
                data-testid="go-live-btn"
                :disabled="publishing || !site?.sandbox_url"
                :aria-label="!site?.sandbox_url ? 'Go live (publish to sandbox first to enable)' : 'Go live — publish to production'"
                :title="!site?.sandbox_url ? 'Publish to sandbox first' : ''"
                @click="handlePublish"
              >
                <svg v-if="publishing" class="animate-spin h-3 w-3" fill="none" viewBox="0 0 24 24" aria-hidden="true"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                {{ publishing ? 'Publishing…' : 'Go Live' }}
              </button>
              <span v-if="!site?.sandbox_url" class="pl-gate" data-testid="go-live-disabled-hint">needs sandbox</span>
            </div>
          </div>
        </div>
        <span v-else class="status-pill-other">{{ site?.status || '...' }}</span>
      </div>
    </div>
    </Teleport>

    <!-- 2. Left Sidebar -->
    <aside id="sidebar" :class="{ 'sidebar-hidden': !leftPanelOpen }">
      <div class="sidebar-tabs">
        <div
          class="tab"
          :class="(activeSheetTab === 'update' || activeSheetTab === 'activity') ? 'active' : ''"
          @click="setActiveSheetTab('update')"
        >Overview</div>
        <div
          class="tab"
          :class="activeSheetTab === 'pages' ? 'active' : ''"
          @click="setActiveSheetTab('pages')"
        >Pages</div>
      </div>

      <!-- Drag handle to resize the sidebar (200–600px). The handle is a
           thin invisible strip on the right edge that lights up on hover /
           drag. JS updates --sidebar-width on .editor-shell. -->
      <div
        class="sidebar-resize-handle"
        :class="{ 'sidebar-resize-handle-active': isResizingSidebar }"
        data-testid="sidebar-resize-handle"
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize sidebar"
        @mousedown="startSidebarResize"
      ></div>

      <div class="sidebar-body" :class="{ 'sidebar-body-chat': activeSheetTab === 'update' || activeSheetTab === 'activity' }">
        <!-- Overview Panel — restructured into a chat-style layout: the
             conversation log fills the available height (oldest at top,
             newest just above the input), and the AI Prompt + Quick Actions
             toggle live in a sticky pane at the bottom. -->
        <div v-show="activeSheetTab === 'update' || activeSheetTab === 'activity'" class="sidebar-chat-pane">

          <!-- Conversation pane (flex:1, scrollable) -->
          <div
            ref="conversationPaneEl"
            class="conversation-pane"
            data-testid="sidebar-conversation-pane"
            @scroll="handleConversationScroll"
          >
            <!-- Empty state shown when the conversation hasn't started yet -->
            <div v-if="!activitySteps.length && !conversation.length && !askError" class="conversation-empty" data-testid="conversation-empty">
              <p class="conversation-empty-title">Start a conversation</p>
              <p class="conversation-empty-body">Ask a question or describe a change. Past prompts appear here so you can scroll back through them.</p>
            </div>

            <!-- Live updates section — site-creation activity, creation
                 feedback prompt, and the ask-error banner. Change-mode
                 activity now renders inline inside its owning conversation
                 turn (orphan steps = activity that doesn't belong to a
                 conversation turn, i.e. site creation only).
                 Gated on the site still being in the creation lifecycle
                 (`creating` or `failed`) — once the site is `active`, the
                 section auto-hides so stale orphan events from cliOutput
                 don't keep it visible above the conversation. -->
            <template v-if="liveUpdatesVisible && (orphanActivitySteps.length > 0 || creationFeedbackPending || askError)">
              <div class="conversation-section-header">
                <div class="section-label" data-testid="live-updates-label" style="margin:0;">
                  Live updates
                  <span v-if="orphanActivitySteps.length > 0" data-testid="live-updates-status-inline" class="conversation-section-status-inline">
                    <span v-if="latestStep?.eventType === 'question_answered'" style="color:var(--success);">Question answered</span>
                    <span v-else-if="isActivityError" style="color:var(--danger);">Update blocked</span>
                    <span v-else-if="isActivityComplete" style="color:var(--success);">Update complete</span>
                    <span v-else>Applying changes…</span>
                  </span>
                </div>
                <button type="button" data-testid="live-updates-collapse-toggle"
                  @click="liveUpdatesCollapsed = !liveUpdatesCollapsed"
                  style="display:flex;align-items:center;gap:4px;font-size:11px;color:var(--text-muted);background:none;border:none;cursor:pointer;padding:2px 4px;border-radius:4px;"
                  :title="liveUpdatesCollapsed ? 'Expand' : 'Collapse'">
                  <svg :style="{ transform: liveUpdatesCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform .2s' }"
                    width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
              </div>
              <div v-if="(!liveUpdatesCollapsed && orphanActivitySteps.length > 0) || creationFeedbackPending || askError" class="conversation-section" data-testid="live-updates-section">
                <div v-if="!liveUpdatesCollapsed && orphanActivitySteps.length > 0" data-testid="activity-feed" style="display:flex;flex-direction:column;gap:4px;">
                  <div v-for="(step, i) in orphanActivitySteps" :key="i"
                    style="display:flex;align-items:flex-start;gap:8px;font-size:11px;line-height:1.5;color:var(--text-muted);">
                    <span style="flex-shrink:0;margin-top:3px;width:6px;height:6px;border-radius:50%;display:inline-block;"
                      :style="i === orphanActivitySteps.length - 1 && !isActivityComplete && !isActivityError
                        ? 'background:var(--accent);box-shadow:0 0 0 2px rgba(109,93,255,.25);animation:pulse-dot 1.2s ease-in-out infinite;'
                        : isActivityError && i === orphanActivitySteps.length - 1
                          ? 'background:var(--danger);'
                          : i === orphanActivitySteps.length - 1 && isActivityComplete
                            ? 'background:var(--success);'
                            : 'background:rgba(255,255,255,.2);'"></span>
                    <span :style="i === orphanActivitySteps.length - 1 ? 'color:var(--text-primary);' : ''">{{ step.message }}</span>
                  </div>
                  <div v-if="!isActivityComplete && !isActivityError" data-testid="activity-progress-bar" style="margin-top:8px;height:2px;border-radius:1px;background:rgba(255,255,255,.08);overflow:hidden;">
                    <div style="height:100%;border-radius:1px;background:var(--accent);transition:width .4s ease;" :style="{ width: activityProgress + '%' }"></div>
                  </div>
                  <button v-if="latestStep?.eventType === 'question_answered' && askHistory.length" type="button"
                    data-testid="ask-view-answer-button"
                    style="align-self:flex-start;margin-top:8px;font-size:11px;color:var(--accent);background:rgba(109,93,255,.12);border:1px solid rgba(109,93,255,.28);border-radius:999px;padding:4px 10px;"
                    @click="viewLatestAskAnswer">View answer</button>
                </div>
                <!-- Creation feedback prompt (replaces automated eval) -->
                <div v-if="creationFeedbackPending" style="margin-top:12px;border-radius:10px;padding:12px;background:rgba(109,93,255,.08);border:1px solid rgba(109,93,255,.25);">
                  <p style="font-size:12px;color:var(--text-primary);font-weight:600;margin:0 0 10px;">Does the preview look good?</p>
                  <div style="display:flex;gap:8px;margin-bottom:10px;">
                    <button
                      type="button"
                      style="flex:1;padding:8px;border-radius:8px;border:1px solid rgba(34,197,94,.4);background:rgba(34,197,94,.1);color:#4ade80;font-size:14px;font-weight:600;cursor:pointer;"
                      :disabled="creationFeedbackSubmitting"
                      @click="submitCreationFeedback(true)"
                    >👍 Looks great!</button>
                    <button
                      type="button"
                      style="flex:1;padding:8px;border-radius:8px;border:1px solid rgba(248,113,113,.4);background:rgba(248,113,113,.1);color:#f87171;font-size:14px;font-weight:600;cursor:pointer;"
                      :disabled="creationFeedbackSubmitting"
                      @click="showCreationFeedbackInput = !showCreationFeedbackInput"
                    >👎 Needs work</button>
                  </div>
                  <div v-if="showCreationFeedbackInput" style="display:flex;flex-direction:column;gap:6px;">
                    <textarea
                      v-model="creationFeedbackComment"
                      rows="2"
                      style="width:100%;resize:none;font-size:11px;padding:6px 8px;border-radius:6px;border:var(--glass-border);background:var(--glass-bg);color:var(--text-primary);"
                      placeholder="What needs improving? (optional)"
                    ></textarea>
                    <button
                      type="button"
                      class="context-apply-btn"
                      style="margin:0;"
                      :disabled="creationFeedbackSubmitting"
                      @click="submitCreationFeedback(false)"
                    >{{ creationFeedbackSubmitting ? 'Submitting...' : 'Submit feedback' }}</button>
                  </div>
                  <p v-if="creationFeedbackError" style="margin:6px 0 0;font-size:11px;color:var(--danger);">{{ creationFeedbackError }}</p>
                </div>

                <div v-if="orphanActivitySteps.length > 0 && askError" style="height:1px;background:rgba(255,255,255,.06);margin:10px 0;"></div>
                <div v-if="askError" data-testid="ask-error" role="alert" style="border-radius:8px;padding:8px 12px;background:rgba(248,113,113,.08);border:1px solid rgba(248,113,113,.25);color:var(--danger);font-size:12px;display:flex;align-items:flex-start;gap:8px;">
                  <span style="flex:1;">{{ askError }}</span>
                  <button v-if="canRetryAsk" type="button" data-testid="ask-retry-button" :disabled="submitting" style="font-size:11px;text-decoration:underline;opacity:.8;" @click="submitAsk">Retry</button>
                </div>
              </div>
            </template>

            <!-- Conversation section — chronological log of past Ask + Change
                 turns (oldest at top, newest just above the prompt input).
                 Each turn renders a mode pill, the prompt, and a mode-specific
                 response body. Collapsible so the user can hide history. -->
            <template v-if="conversation.length">
              <div class="conversation-section-header">
                <div class="section-label" data-testid="conversation-label" style="margin:0;">
                  Conversation
                  <span class="conversation-turn-count">{{ conversation.length }} turn{{ conversation.length === 1 ? '' : 's' }}</span>
                </div>
                <button type="button" data-testid="conversation-collapse-toggle"
                  @click="askHistoryCollapsed = !askHistoryCollapsed"
                  style="display:flex;align-items:center;gap:4px;font-size:11px;color:var(--text-muted);background:none;border:none;cursor:pointer;padding:2px 4px;border-radius:4px;"
                  :title="askHistoryCollapsed ? 'Expand' : 'Collapse'">
                  <svg :style="{ transform: askHistoryCollapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform .2s' }"
                    width="12" height="12" viewBox="0 0 12 12" fill="none">
                    <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                  </svg>
                </button>
                <button
                  type="button"
                  data-testid="conversation-reset-button"
                  class="conversation-reset-btn"
                  :disabled="submitting"
                  title="Start a fresh visible conversation without deleting history"
                  @click="resetConversation"
                >Reset</button>
              </div>
              <div v-if="!askHistoryCollapsed" class="conversation-section conversation-section-flat" data-testid="conversation-section">
                <div data-testid="conversation-log" class="conversation-log" style="display:flex;flex-direction:column;gap:12px;">
                <button
                  v-if="conversationHasMore"
                  type="button"
                  class="conversation-load-more"
                  data-testid="conversation-load-more"
                  :disabled="conversationLoadingMore"
                  @click="loadPreviousConversationTurns"
                >{{ conversationLoadingMore ? 'Loading…' : 'Load previous conversation' }}</button>
                <div
                  v-for="turn in conversation"
                  :key="turn.id"
                  data-testid="conversation-turn"
                  :data-mode="turn.mode"
                  :data-status="turn.status"
                  class="conversation-turn"
                  style="display:flex;flex-direction:column;gap:6px;"
                >
                  <!-- Prompt -->
                  <div style="display:flex;align-items:baseline;gap:8px;">
                    <span
                      data-testid="conversation-turn-mode"
                      class="conversation-pill"
                      :class="turnModeClass(turn)"
                    >{{ turnModeLabel(turn) }}</span>
                    <p data-testid="conversation-turn-prompt" style="color:var(--text-primary);word-break:break-word;font-size:12px;margin:0;line-height:1.5;">{{ turn.prompt }}</p>
                  </div>
                  <!-- Response -->
                  <div style="display:flex;align-items:baseline;gap:8px;">
                    <span style="font-size:11px;font-weight:700;color:var(--success);flex-shrink:0;">↳</span>
                    <div data-testid="conversation-turn-response" style="flex:1;min-width:0;">
                      <div v-if="turn.mode === 'assistant'" class="conversation-assistant-actions" data-testid="conversation-assistant-actions">
                        <template v-if="turn.assistantKind === 'confirm_change'">
                          <span class="conversation-assistant-hint">Reply yes to make this change, no to cancel, or tell me what to adjust.</span>
                        </template>
                        <template v-else-if="turn.assistantKind === 'clarification'">
                          <span class="conversation-assistant-hint">Type your answer below, or</span>
                          <button
                            type="button"
                            data-testid="conversation-turn-clarification-proceed"
                            class="conversation-turn-clarification-proceed"
                            :disabled="submitting"
                            @click="submitClarification(turn.sourceTurn, { proceedAnyway: true })"
                          >just proceed</button>
                          <span class="conversation-assistant-hint">with reasonable defaults.</span>
                        </template>
                        <template v-else-if="turn.assistantKind === 'review'">
                          <span class="conversation-assistant-hint">Reply yes if it looks right, no to revert, or describe what should change.</span>
                        </template>
                      </div>
                      <div v-else-if="turn.mode === 'ask'" class="ask-answer-body" v-html="renderMarkdown(turn.response.text)"></div>
                      <template v-else>
                        <p
                          v-if="turn.response.summary"
                          data-testid="conversation-turn-summary"
                          style="color:var(--text-primary);word-break:break-word;font-size:12px;margin:0;line-height:1.5;"
                        >{{ turn.response.summary }}</p>
                        <p
                          v-else-if="turn.status === 'failed'"
                          data-testid="conversation-turn-error"
                          style="color:var(--danger);font-size:12px;margin:0;line-height:1.5;"
                        >Change failed.</p>
                        <!-- In-flight activity feed for THIS turn. Collapsed
                             by default — only the latest step is visible.
                             User clicks "Show N earlier steps" to expand the
                             chronological list; "Collapse" hides it again.
                             When the turn completes, the steps stay (so the
                             user has a record of what happened) but the diff
                             auto-opens below it. -->
                        <div
                          v-if="activityStepsForTurn(turn.id).length > 0"
                          data-testid="conversation-turn-activity"
                          class="conversation-turn-activity"
                        >
                          <button
                            v-if="activityStepsForTurn(turn.id).length > 1"
                            type="button"
                            data-testid="conversation-turn-activity-toggle"
                            class="conversation-turn-activity-toggle"
                            :aria-expanded="isTurnActivityExpanded(turn.id) ? 'true' : 'false'"
                            @click="toggleTurnActivity(turn.id)"
                          >
                            <svg :style="{ transform: isTurnActivityExpanded(turn.id) ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .2s' }"
                              width="9" height="9" viewBox="0 0 12 12" fill="none">
                              <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            {{ isTurnActivityExpanded(turn.id)
                              ? 'Collapse'
                              : `Show ${activityStepsForTurn(turn.id).length - 1} earlier step${activityStepsForTurn(turn.id).length - 1 === 1 ? '' : 's'}` }}
                          </button>
                          <div
                            v-for="(step, i) in visibleActivityStepsForTurn(turn.id)"
                            :key="i"
                            class="conversation-turn-activity-row"
                          >
                            <span class="conversation-turn-activity-dot"
                              :class="{
                                'dot-active': i === visibleActivityStepsForTurn(turn.id).length - 1 && (turn.status === 'pending' || turn.status === 'implementing'),
                                'dot-done': i === visibleActivityStepsForTurn(turn.id).length - 1 && turn.status !== 'pending' && turn.status !== 'implementing' && turn.status !== 'failed',
                                'dot-error': i === visibleActivityStepsForTurn(turn.id).length - 1 && turn.status === 'failed'
                              }"
                            ></span>
                            <span :class="{ 'conversation-turn-activity-latest': i === visibleActivityStepsForTurn(turn.id).length - 1 }">{{ step.message }}</span>
                          </div>
                        </div>
                        <p
                          v-else-if="turn.status === 'pending' || turn.status === 'implementing'"
                          style="color:var(--text-muted);font-size:12px;margin:0;line-height:1.5;font-style:italic;"
                        >Working on this change…</p>
                        <p
                          v-if="turn.touchedFiles.length"
                          data-testid="conversation-turn-files"
                          style="color:var(--text-muted);font-size:11px;margin:4px 0 0;"
                        >Touched {{ turn.touchedFiles.length }} file{{ turn.touchedFiles.length === 1 ? '' : 's' }}</p>
                        <!-- Previous-turn revert: every prior Change turn
                             gets a "Revert to this version" button.
                             Hidden on the awaiting turn (uses Approve/Reject
                             instead). Turns submitted before the workflow
                        started persisting commit hashes have no
                             draftCommitSha — the button still renders but
                             is disabled with an explanatory title. -->
                        <div
                          v-if="turn.mode === 'change' && !isTurnAwaitingPublish(turn) && !isTurnAwaitingClarification(turn) && turn.status !== 'awaiting_user_confirmation'"
                          class="conversation-turn-revert-row"
                          data-testid="conversation-turn-revert-row"
                        >
                          <button
                            type="button"
                            data-testid="conversation-turn-revert-button"
                            class="conversation-turn-revert-btn"
                            :disabled="!turn.draftCommitSha || isTurnActionBusy(turn.id, 'revert')"
                            :title="turn.draftCommitSha
                              ? 'Roll the site back to the state right after this turn'
                              : 'No commit hash was recorded for this turn — revert isn\'t available'"
                            @click="revertToTurn(turn)"
                          >{{ isTurnActionBusy(turn.id, 'revert') ? 'Reverting…' : 'Revert to this version' }}</button>
                        </div>
                        <!-- Show diff toggle — fetches the unified diff on
                             first expand, caches the result so reopen is
                             instant. Hunks only (3 lines of context),
                             multi-hunk collapse + large-file guard handled
                             inside the ConversationDiff component. -->
                        <div class="conversation-turn-diff-row" data-testid="conversation-turn-diff-row">
                          <button
                            type="button"
                            data-testid="conversation-turn-diff-toggle"
                            class="conversation-diff-toggle"
                            :aria-expanded="isTurnDiffOpen(turn.id) ? 'true' : 'false'"
                            @click="toggleTurnDiff(turn.id)"
                          >
                            <svg :style="{ transform: isTurnDiffOpen(turn.id) ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform .2s' }"
                              width="10" height="10" viewBox="0 0 12 12" fill="none">
                              <path d="M2 4l4 4 4-4" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                            {{ isTurnDiffOpen(turn.id) ? 'Hide diff' : 'Show diff' }}
                          </button>
                        </div>
                        <div v-if="isTurnDiffOpen(turn.id)" data-testid="conversation-turn-diff-panel">
                          <div v-if="turnDiffState(turn.id).status === 'loading'" class="conversation-diff-loading" data-testid="conversation-diff-loading">
                            Loading diff…
                          </div>
                          <div v-else-if="turnDiffState(turn.id).status === 'error'" class="conversation-diff-error" data-testid="conversation-diff-error" role="alert">
                            {{ turnDiffState(turn.id).error }}
                            <button type="button" class="conversation-diff-retry" @click="toggleTurnDiff(turn.id)">Retry</button>
                          </div>
                          <ConversationDiff
                            v-else-if="turnDiffState(turn.id).status === 'loaded'"
                            :diff="turnDiffState(turn.id).diff"
                          />
                        </div>
                      </template>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            </template>
          </div><!-- /conversation-pane -->

          <!-- Prompt pane (sticky bottom) — AI Prompt + Quick Actions toggle.
               Quick Actions live behind a toggle so the conversation gets the
               visual primacy in the panel. -->
          <div class="prompt-pane" data-testid="sidebar-prompt-pane">
            <div class="sidebar-prompt-wrap">
              <div class="context-chips" data-testid="quick-action-chips">
                <button class="context-chip" @click="updateForm.prompt = 'Make this section more impactful with bold copy and a stronger visual hierarchy.'">More impactful</button>
                <button class="context-chip" @click="updateForm.prompt = 'Add social proof — customer logos, testimonials, or a stat banner — to build trust.'">Add social proof</button>
                <button class="context-chip" @click="updateForm.prompt = 'Simplify the copy — make it shorter, clearer, and easier to scan in 3 seconds.'">Simplify copy</button>
                <button class="context-chip" @click="updateForm.prompt = 'Update the color palette to feel more modern and on-brand.'">Change colors</button>
                <button class="context-chip" @click="updateForm.prompt = 'Optimize this section so it looks great on phones — adjust spacing, font sizes, and layout for small screens.'">Works on phones</button>
                <button class="context-chip" @click="updateForm.prompt = 'Make the CTA button more compelling — stronger action words, better contrast, and a hover animation.'">Improve CTA</button>
              </div>
              <label for="ai-prompt-input" class="sr-only">Describe a change or ask a question</label>
              <textarea
                id="ai-prompt-input"
                ref="promptInputEl"
                v-model="updateForm.prompt"
                class="context-textarea"
                :placeholder="assistantCopy.placeholder"
                aria-label="Describe a change or ask a question"
                rows="3"
              ></textarea>
              <div
                v-if="updateForm.prompt.trim()"
                class="prompt-intent-hint"
                :data-intent="classifyConversationPrompt(updateForm.prompt)"
                data-testid="prompt-intent-hint"
                aria-live="polite"
              >
                <span v-if="classifyConversationPrompt(updateForm.prompt) === 'change'">Edit — this will change your site.</span>
                <span v-else>Answer — this replies to your question; no changes are made.</span>
              </div>
              <div v-if="updateForm.attachments.length" class="sidebar-attachments">
                <div v-for="(a, idx) in updateForm.attachments" :key="a.name+idx" style="display:flex;align-items:center;gap:6px;">
                  <img v-if="a.type && a.type.startsWith('image/')" :src="a.dataUrl" alt="" style="height:28px;width:36px;object-fit:cover;border-radius:4px;border:var(--glass-border);" />
                  <span v-else style="height:28px;width:36px;display:flex;align-items:center;justify-content:center;border-radius:4px;border:var(--glass-border);background:var(--glass-bg);flex-shrink:0;font-size:14px;">📄</span>
                  <span style="font-size:11px;color:var(--text-secondary);flex:1;min-width:0;overflow:hidden;text-overflow:ellipsis;">{{ a.name }}</span>
                  <button type="button" style="font-size:11px;color:var(--danger);flex-shrink:0;" @click="removeAttachment(idx)">✕</button>
                </div>
              </div>
              <div style="display:flex;align-items:center;gap:6px;">
                <button
                  type="button"
                  class="prompt-attach-btn"
                  data-testid="prompt-attach-btn"
                  title="Attach files"
                  aria-label="Attach files"
                  @click="triggerAttachmentUpload"
                >
                  <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" /></svg>
                </button>
                <input ref="attachmentInput" type="file" accept="*" multiple class="sr-only" @change="handleAttachmentUpload" />
                <button
                  type="button"
                  class="context-apply-btn"
                  style="flex:1;margin:0;"
                  :disabled="!updateForm.prompt.trim() || submitting"
                  :title="isLockedByAnotherEditor ? `Locked by ${lockOwnerLabel}` : ''"
                  @click="submitAssistant"
                >{{ submitting ? 'Sending…' : 'Send' }}</button>
              </div>
            </div>
          </div><!-- /prompt-pane -->

        </div>

        <!-- Pages Panel -->
        <div v-show="activeSheetTab === 'pages'">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;margin-bottom:6px;">
            <div class="section-label" style="margin:0;">Pages</div>
            <button class="context-chip context-chip-accent" data-testid="new-page-button" @click="openCreatePageModal">+ New page</button>
          </div>
          <div class="wf-search-wrap">
            <span class="wf-search-icon">🔍</span>
            <input
              v-model="pagesSearchQuery"
              class="wf-search"
              placeholder="Search pages…"
              aria-label="Search pages"
            />
          </div>
          <div v-if="availablePreviewPages.length === 0" style="padding:8px 4px;color:var(--text-muted);font-size:12px;">Pages load as your site builds…</div>
          <div v-else-if="filteredPages.length === 0" style="padding:8px 4px;color:var(--text-muted);font-size:12px;">No pages match "{{ pagesSearchQuery }}"</div>
          <div
            v-for="(page, idx) in filteredPages"
            :key="page.path"
            class="tree-item page-row"
            :class="{ active: lastKnownPreviewPath === page.path }"
            :style="{ paddingLeft: `${((page.path.match(/\//g) || []).length - (page.path === '/' ? 0 : 1)) * 12 + 8}px` }"
            @click="lastKnownPreviewPath = page.path"
          >
            <span>{{ page.path === '/' ? '🏠' : (page.path.match(/\//g) || []).length > 1 ? '↳' : '📄' }}</span>
            <span class="page-row-label">{{
              page.path === '/'
                ? (page.label || 'Home')
                : (page.path.match(/\//g) || []).length > 1
                  ? (page.path.split('/').filter(Boolean).pop() || '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
                  : (page.label || page.path)
            }}</span>
            <span class="badge badge-live">Live</span>
            <button
              v-if="page.path !== '/'"
              class="page-delete-btn"
              :disabled="deletingPage === page.path"
              :title="`Delete ${page.label || page.path}`"
              @click.stop="deletePage(page)"
            >{{ deletingPage === page.path ? '…' : '×' }}</button>
          </div>
        </div>

        <!-- Workflows + History moved out of the editor: History now lives in
             Site Settings (the Overview). SEO moved to Site Settings too. -->
      </div>
    </aside>

    <!-- 3. Canvas -->
    <main id="canvas">
      <!-- Canvas toolbar -->
      <div class="canvas-toolbar">
        <button class="device-btn" :class="{ active: deviceMode === 'desktop' }" @click="setAspectFromDevice('desktop')" title="Desktop (16:9)" aria-label="Desktop preview" data-testid="device-btn-desktop">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v3m6-3v3m-8 0h10M4 5h16a1 1 0 011 1v9a1 1 0 01-1 1H4a1 1 0 01-1-1V6a1 1 0 011-1z" /></svg>
        </button>
        <button class="device-btn" :class="{ active: deviceMode === 'tablet' }" @click="setAspectFromDevice('tablet')" title="Tablet (3:4)" aria-label="Tablet preview" data-testid="device-btn-tablet">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><rect x="5" y="3" width="14" height="18" rx="2" stroke-width="2" /><path stroke-linecap="round" stroke-width="2" d="M11 18h2" /></svg>
        </button>
        <button class="device-btn" :class="{ active: deviceMode === 'mobile' }" @click="setAspectFromDevice('mobile')" title="Mobile (9:16)" aria-label="Mobile preview" data-testid="device-btn-mobile">
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><rect x="7" y="3" width="10" height="18" rx="2" stroke-width="2" /><path stroke-linecap="round" stroke-width="2" d="M11 18h2" /></svg>
        </button>
        <div class="custom-aspect-wrap">
          <button
            class="device-btn"
            :class="{ active: deviceMode === 'custom' }"
            @click="openCustomAspect"
            :title="deviceMode === 'custom' ? `Custom aspect (${customAspectInput})` : 'Custom aspect'"
            data-testid="device-btn-custom"
            style="font-size:11px;font-weight:600;"
          >{{ deviceMode === 'custom' ? customAspectInput : 'Custom' }}</button>
          <div v-if="showCustomAspectInput" class="custom-aspect-popover" data-testid="custom-aspect-popover">
            <label class="custom-aspect-label">Aspect ratio (W:H)</label>
            <div class="custom-aspect-row">
              <input
                v-model="customAspectInput"
                class="custom-aspect-input"
                data-testid="custom-aspect-input"
                placeholder="16:9"
                @keyup.enter="applyCustomAspect"
              />
              <button type="button" class="custom-aspect-apply" data-testid="custom-aspect-apply" @click="applyCustomAspect">Apply</button>
            </div>
            <p v-if="customAspectError" class="custom-aspect-error">{{ customAspectError }}</p>
          </div>
        </div>

        <button class="device-btn" :disabled="!canGoBack" @click="goBack" title="Back" style="font-size:14px;">&#8592;</button>
        <button class="device-btn" :disabled="!canGoForward" @click="goForward" title="Forward" style="font-size:14px;">&#8594;</button>

        <div class="canvas-url">
          <span class="canvas-url-lock">🔒</span>
          <span>{{ site?.preview_url ? (site.preview_url.replace(/^https?:\/\//, '').split('/')[0] + (lastKnownPreviewPath || '/')) : 'loading…' }}</span>
        </div>

        <div class="zoom-control">
          <span class="zoom-btn" @click="zoomOut">−</span>
          <span>{{ zoomLevel }}%</span>
          <span class="zoom-btn" @click="zoomIn">+</span>
        </div>

        <!-- Refresh -->
        <button class="btn btn-ghost" @click="handleRefreshPreview" :disabled="refreshingPreview" title="Refresh">
          <svg class="w-4 h-4" :class="{ 'animate-spin': refreshingPreview }" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>

        <!-- Edit mode toggle -->
        <button
          role="switch" :aria-checked="editMode" aria-label="Edit mode"
          :disabled="isLockedByAnotherEditor"
          :title="isLockedByAnotherEditor ? `Locked by ${lockOwnerLabel}` : (editMode ? 'Edit mode ON' : 'Edit mode OFF')"
          :class="['device-btn', editMode ? 'active' : '', 'disabled:opacity-50 disabled:cursor-not-allowed']"
          data-testid="edit-mode-toggle"
          @click="toggleEditMode" @keydown.enter.prevent="toggleEditMode" @keydown.space.prevent="toggleEditMode"
        >
          <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
        </button>

        <!-- Add page / Add blog — prominent quick actions -->
        <span class="canvas-toolbar-sep" aria-hidden="true"></span>
        <button class="btn btn-accent text-xs canvas-add-btn" data-testid="toolbar-add-page" title="Add a new page" @click="openCreatePageModal">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" /></svg>
          Page
        </button>
        <button class="btn btn-ghost text-xs canvas-add-btn" data-testid="toolbar-add-blog" title="Add a blog post" @click="showBlogModal = true">
          <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
          Blog
        </button>

        <!-- Pending draft commits -->
        <template v-if="hasPendingDraftCommits">
          <span class="text-xs font-medium" style="color:var(--accent)" data-testid="pending-unsaved-count">{{ recoveredPendingCount }} unsaved</span>
          <button class="btn btn-ghost text-xs disabled:opacity-50" :disabled="undoingInlineEdit || isLockedByAnotherEditor" :title="isLockedByAnotherEditor ? `Locked by ${lockOwnerLabel}` : 'Undo'" data-testid="undo-latest-inline-edit" @click="undoLatestAction">
            <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" /></svg>
            {{ undoingInlineEdit ? 'Undoing...' : 'Undo' }}
          </button>
          <button class="btn btn-accent text-xs disabled:opacity-50" :disabled="confirmingInlineEdits || isLockedByAnotherEditor" :title="isLockedByAnotherEditor ? `Locked by ${lockOwnerLabel}` : 'Confirm & push'" data-testid="confirm-and-push-inline-edits" @click="confirmAndPushAction">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7" /></svg>
            {{ confirmingInlineEdits ? 'Pushing...' : 'Confirm & push' }}
          </button>
        </template>
      </div>

      <!-- Error banner -->
      <div v-if="confirmInlineEditsError || undoInlineEditError" class="canvas-error-banner">
        <span>{{ confirmInlineEditsError || undoInlineEditError }}</span>
        <button
          type="button"
          class="canvas-error-reset"
          data-testid="reset-edit-session"
          :disabled="resettingEditSession"
          title="Discard pending drafts and clear the edit session"
          @click="resetEditSession"
        >{{ resettingEditSession ? 'Resetting…' : 'Reset' }}</button>
      </div>

      <!-- Canvas frame -->
      <div class="canvas-frame" ref="canvasFrameEl">
        <template v-if="site?.preview_url">
          <!-- Preview-shell carries a FIXED virtual viewport (1280/768/375)
               so the rendered HTML reflows the same way regardless of how
               much horizontal space the sidebar leaves. The visual size
               scales via the existing `zoom` property: zoom = auto-fit-scale
               × user-zoom, where auto-fit-scale = min(1, canvas-width /
               virtual-width). Resizing the sidebar changes the host width
               but not the iframe's internal viewport. -->
          <div class="preview-shell" :style="previewShellStyle">
            <iframe ref="previewIframeEl" :key="previewReloadKey" :src="previewIframeSrc" sandbox="allow-same-origin allow-scripts allow-popups allow-forms" style="width:100%;border:0;display:block;" @load="handlePreviewIframeLoad" />
          </div>
          <div v-if="isPreviewDeploying" class="canvas-deploying-toast">Deploying preview updates...</div>
          <!-- Anchors the current inline selection to the canvas so users don't
               lose track of what they're editing while the controls live in the
               right panel. (A chip pinned over the element itself needs the
               iframe bridge to report element bounds — a follow-up.) -->
          <div v-if="editMode && selectedElement" class="canvas-editing-banner" data-testid="canvas-editing-banner">
            <span class="canvas-editing-dot"></span>
            Editing: <strong>{{ friendlyTagLabel(selectedElement.tagName) }}</strong>
            <span class="canvas-editing-sep">·</span>
            <span class="canvas-editing-hint">controls in the right panel</span>
            <button type="button" class="canvas-editing-close" aria-label="Deselect element" title="Deselect" @click="clearSelection">✕</button>
          </div>
        </template>
        <div v-else class="canvas-no-preview">
          <svg class="w-12 h-12 mb-4" style="color:var(--text-muted)" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9" /></svg>
          <p class="text-lg font-medium" style="color:var(--text-primary)">{{ isPreviewDeploying ? 'Deploying Preview' : 'Website Loading' }}</p>
          <p class="text-sm mt-1" style="color:var(--text-secondary)">{{ isPreviewDeploying ? 'Cloudflare Pages is preparing your latest changes...' : 'Your website is being generated...' }}</p>
        </div>
      </div>
    </main>

    <!-- 4. Inline-edit popup: floats over the canvas while editing inline -->
    <aside id="context-panel" :class="{ 'panel-hidden': !rightPanelOpen }">
      <button class="context-popup-close" title="Close" data-testid="context-panel-close" @click="rightPanelOpen = false">
        <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" /></svg>
      </button>

      <!-- Edit panel: AI Editor at top + collapsible Inline Editor at bottom (visible only when edit mode ON) -->
      <div data-testid="inline-edit-panel" style="display:flex;flex-direction:column;flex:1;overflow:hidden;">

        <!-- Inline Editor collapsible -->
        <div class="inline-collapse" :class="{ expanded: inlineEditorExpanded }">
          <button class="inline-collapse-toggle" @click="inlineEditorExpanded = !inlineEditorExpanded">
            <span style="font-size:12px;font-weight:600;color:var(--text-primary);">Inline Editor</span>
            <span v-if="selectedElement" style="color:var(--accent);font-size:11px;font-weight:500;">{{ selectedElement.tagName }}</span>
            <span v-else style="color:var(--text-muted);font-size:11px;">Click an element</span>
            <svg style="width:14px;height:14px;margin-left:auto;transition:transform .2s;flex-shrink:0;" :style="{ transform: inlineEditorExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" /></svg>
          </button>
          <div v-if="inlineEditorExpanded" class="context-body">
            <!-- inlineBridgeFailed banner -->
            <div v-if="inlineBridgeFailed" data-testid="inline-bridge-unavailable" class="context-chip" style="color:var(--warning);border-color:rgba(251,191,36,0.3)">
              Inline editing unavailable ({{ inlineBridgeFailureReason || 'no response' }}). You can still use the AI prompt bar.
            </div>
            <!-- no element selected — coach the flow explicitly -->
            <div v-else-if="!selectedElement" data-testid="inline-editor-empty" style="padding:12px;font-size:12px;color:var(--text-secondary);line-height:1.6;">
              <div style="font-weight:600;color:var(--text-primary);margin-bottom:6px;">Edit an element directly</div>
              <div style="display:flex;gap:8px;"><span style="color:var(--accent);font-weight:700;">1.</span><span>Click any text, button, or section in the preview.</span></div>
              <div style="display:flex;gap:8px;"><span style="color:var(--accent);font-weight:700;">2.</span><span>Change its text, font, or color here.</span></div>
              <div style="display:flex;gap:8px;"><span style="color:var(--accent);font-weight:700;">3.</span><span>Hit <strong style="color:var(--text-primary)">Apply</strong>, then <strong style="color:var(--text-primary)">Confirm &amp; push</strong> (top bar) to save.</span></div>
            </div>
            <!-- No manual-styling controls for this tag — but ANY element is AI-editable -->
            <div v-else-if="!showContextualOptionRow" data-testid="unsupported-selection-notice">
              <div class="inline-editing-header" style="margin-bottom:10px;">
                <span class="inline-editing-dot"></span>
                <div style="min-width:0;">
                  <div style="font-size:12px;font-weight:600;color:var(--text-primary);">Editing: {{ friendlyTagLabel(selectedElement.tagName) }}</div>
                  <div class="truncate" style="font-size:11px;color:var(--text-muted);">{{ selectedElement.cssSelector || selectedElement.selector }}</div>
                </div>
              </div>
              <div class="element-ai-edit" data-testid="element-ai-edit">
                <div class="element-ai-label">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
                  <span>Ask AI to change this <strong>{{ friendlyTagLabel(selectedElement.tagName) }}</strong></span>
                </div>
                <textarea
                  v-model="elementAiPrompt"
                  class="context-textarea"
                  rows="2"
                  placeholder="e.g. swap this image for a bold headline, or restyle this section"
                  aria-label="Describe a change to this element"
                  @keydown.enter.exact.prevent="submitElementAiEdit"
                ></textarea>
                <button
                  type="button"
                  class="context-apply-btn"
                  data-testid="element-ai-submit"
                  :disabled="!elementAiPrompt.trim() || elementAiSubmitting || isLockedByAnotherEditor"
                  :title="isLockedByAnotherEditor ? `Locked by ${lockOwnerLabel}` : ''"
                  @click="submitElementAiEdit"
                >{{ elementAiSubmitting ? 'Editing…' : 'Edit with AI' }}</button>
              </div>
              <p style="font-size:11px;color:var(--text-muted);margin-top:8px;">Manual font/color controls aren't available for &lt;{{ selectedElement?.tagName }}&gt; yet — describe the change above and AI will edit it.</p>
            </div>
            <!-- full inline editor -->
            <div v-else data-testid="contextual-option-row" class="space-y-3">
              <div class="inline-editing-header" data-testid="inline-editing-header">
                <span class="inline-editing-dot"></span>
                <div style="min-width:0;">
                  <div style="font-size:12px;font-weight:600;color:var(--text-primary);">Editing: {{ friendlyTagLabel(selectedElement.tagName) }}</div>
                  <div class="truncate" style="font-size:11px;color:var(--text-muted);">{{ selectedElement.cssSelector || selectedElement.selector }}</div>
                </div>
              </div>
              <div v-if="recoveredPendingCount > 0" class="inline-staged-note" data-testid="inline-staged-note">
                {{ recoveredPendingCount }} edit{{ recoveredPendingCount === 1 ? '' : 's' }} staged — <strong>Confirm &amp; push</strong> in the top toolbar to save them.
              </div>
              <div class="element-ai-edit" data-testid="element-ai-edit">
                <div class="element-ai-label">
                  <svg class="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09z" /></svg>
                  <span>Ask AI to change this <strong>{{ friendlyTagLabel(selectedElement.tagName) }}</strong></span>
                </div>
                <textarea
                  v-model="elementAiPrompt"
                  class="context-textarea"
                  rows="2"
                  placeholder="e.g. make this a rounded outline button"
                  aria-label="Describe a change to this element"
                  @keydown.enter.exact.prevent="submitElementAiEdit"
                ></textarea>
                <button
                  type="button"
                  class="context-apply-btn"
                  data-testid="element-ai-submit"
                  :disabled="!elementAiPrompt.trim() || elementAiSubmitting || isLockedByAnotherEditor"
                  :title="isLockedByAnotherEditor ? `Locked by ${lockOwnerLabel}` : ''"
                  @click="submitElementAiEdit"
                >{{ elementAiSubmitting ? 'Editing…' : 'Edit with AI' }}</button>
              </div>
              <button
                v-if="selectedElement.sectionTarget && selectedElement.sourceId !== selectedElement.sectionTarget.sourceId"
                type="button"
                class="context-chip context-chip-accent"
                data-testid="edit-parent-section-btn"
                @click="promoteToSectionTarget"
              >
                Edit parent {{ selectedElement.sectionTarget.tagName || 'section' }}
              </button>
              <div v-if="isSectionStyleTarget" data-testid="section-style-controls" style="border:var(--glass-border);border-radius:10px;padding:10px;display:flex;flex-direction:column;gap:8px;">
                <div style="font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:.06em;">Section colors</div>
                <label style="display:flex;align-items:center;justify-content:space-between;gap:8px;font-size:11px;color:var(--text-secondary);">
                  Background
                  <input v-model="pendingStyles.backgroundColor" type="color" style="width:36px;height:26px;cursor:pointer;border-radius:4px;border:var(--glass-border);" @input="livePreviewStyles" />
                </label>
                <label style="display:flex;align-items:center;justify-content:space-between;gap:8px;font-size:11px;color:var(--text-secondary);">
                  Text
                  <input v-model="pendingStyles.color" type="color" style="width:36px;height:26px;cursor:pointer;border-radius:4px;border:var(--glass-border);" @input="livePreviewStyles" />
                </label>
              </div>
              <!-- Font family row -->
              <div v-if="!isSectionStyleTarget" class="custom-select-wrap">
                <button type="button" class="custom-select-trigger wf-search"
                  @click.stop="toggleDropdown('fontFamily')">
                  <span>{{ pendingStyles.fontFamily || '— Font family —' }}</span>
                  <svg class="custom-select-chevron" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 3.5l3 3 3-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                </button>
                <div v-if="isDropdownOpen('fontFamily')" class="custom-select-menu">
                  <button type="button" class="custom-select-option" :class="{ active: pendingStyles.fontFamily === '' }"
                    @click.stop="pendingStyles.fontFamily = ''; closeAllDropdowns(); livePreviewStyles()">— Font family —</button>
                  <button type="button" v-for="font in fontFamilyOptions" :key="font"
                    class="custom-select-option" :class="{ active: pendingStyles.fontFamily === font }"
                    @click.stop="pendingStyles.fontFamily = font; closeAllDropdowns(); livePreviewStyles()">{{ font }}</button>
                </div>
              </div>
              <!-- Size + style + formatting row -->
              <div v-if="!isSectionStyleTarget" style="display:flex;flex-wrap:wrap;align-items:center;gap:6px;">
                <input v-model="pendingStyles.fontSize" list="inline-font-size-options" class="wf-search" style="width:64px;" placeholder="Size" @input="livePreviewStyles" />
                <datalist id="inline-font-size-options">
                  <option v-for="size in fontSizeOptions" :key="size" :value="size" />
                </datalist>
                <div class="custom-select-wrap">
                  <button type="button" class="custom-select-trigger wf-search" style="width:auto;"
                    @click.stop="toggleDropdown('fontStyle')">
                    <span>{{ { regular: 'Regular', bold: 'Bold', italic: 'Italic', boldItalic: 'Bold italic' }[fontStyleChoice] || 'Regular' }}</span>
                    <svg class="custom-select-chevron" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 3.5l3 3 3-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
                  </button>
                  <div v-if="isDropdownOpen('fontStyle')" class="custom-select-menu">
                    <button type="button" v-for="opt in [['regular','Regular'],['bold','Bold'],['italic','Italic'],['boldItalic','Bold italic']]" :key="opt[0]"
                      class="custom-select-option" :class="{ active: fontStyleChoice === opt[0] }"
                      @click.stop="fontStyleChoice = opt[0]; closeAllDropdowns()">{{ opt[1] }}</button>
                  </div>
                </div>
                <button type="button" class="apply-btn font-bold" @mousedown.prevent="formatRichText('bold')">B</button>
                <button type="button" class="apply-btn italic" @mousedown.prevent="formatRichText('italic')">I</button>
                <button type="button" class="apply-btn underline" @mousedown.prevent="formatRichText('underline')">U</button>
                <button type="button" class="apply-btn" @mousedown.prevent="formatRichText('removeFormat')" style="color:var(--text-muted)">Clear</button>
                <!-- Color picker -->
                <div class="relative flex items-center">
                  <button type="button" class="apply-btn flex items-center pl-1.5 pr-0.5" title="Apply color" @mousedown.prevent="applyCurrentColor">
                    <span style="display:inline-block;width:14px;height:14px;border-radius:3px;border:var(--glass-border);flex-shrink:0;" :style="{backgroundColor:currentTextColor}"></span>
                  </button>
                  <button type="button" class="apply-btn px-1" :class="showColorPicker ? 'ring-1 ring-[var(--accent)]' : ''" title="Choose color" @mousedown.prevent="toggleColorPicker">
                    <svg class="w-3 h-3" style="color:var(--text-muted)" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M19 9l-7 7-7-7" /></svg>
                  </button>
                  <!-- Color popover -->
                  <div v-if="showColorPicker" style="position:absolute;z-index:50;left:0;top:100%;margin-top:4px;width:220px;background:var(--surface-1);backdrop-filter:var(--glass-blur);border:var(--glass-border-hi);border-radius:12px;box-shadow:var(--elev-lg);padding:12px;display:flex;flex-direction:column;gap:10px;">
                    <div>
                      <p style="font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:.06em;margin-bottom:6px;">Theme</p>
                      <div style="display:flex;flex-wrap:wrap;gap:6px;">
                        <button v-for="color in paletteAccents" :key="color" type="button" style="width:22px;height:22px;border-radius:4px;border:var(--glass-border);cursor:pointer;transition:transform .1s;" :style="{backgroundColor:color}" :title="color" @mousedown.prevent="applyTextColor(color,true)"></button>
                      </div>
                    </div>
                    <div v-if="paletteNeutrals.length">
                      <p style="font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:.06em;margin-bottom:4px;">Neutral</p>
                      <div style="display:flex;gap:6px;">
                        <button v-for="color in paletteNeutrals" :key="color" type="button" style="width:22px;height:22px;border-radius:4px;border:var(--glass-border);cursor:pointer;" :style="{backgroundColor:color}" @mousedown.prevent="applyTextColor(color,true)"></button>
                      </div>
                    </div>
                    <div v-if="recentColors.length">
                      <p style="font-size:11px;color:var(--text-muted);text-transform:uppercase;letter-spacing:.06em;margin-bottom:4px;">Recent</p>
                      <div style="display:flex;gap:6px;">
                        <button v-for="color in recentColors" :key="color" type="button" style="width:22px;height:22px;border-radius:4px;border:var(--glass-border);cursor:pointer;" :style="{backgroundColor:color}" @mousedown.prevent="applyTextColor(color,true)"></button>
                      </div>
                    </div>
                    <div style="border-top:var(--glass-border);padding-top:8px;display:flex;flex-direction:column;gap:6px;">
                      <div style="display:flex;align-items:center;gap:8px;">
                        <input v-model="customColor" type="color" style="width:36px;height:28px;cursor:pointer;border-radius:4px;border:var(--glass-border);flex-shrink:0;" @mousedown="saveRichTextSelection" />
                        <span style="font-size:11px;color:var(--text-secondary);flex:1;">Custom color</span>
                      </div>
                      <button type="button" class="context-apply-btn" @mousedown.prevent="applyTextColor(customColor,true)">Apply color</button>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Contenteditable -->
              <div v-if="!isSectionStyleTarget" ref="inlineEditorEl" data-testid="inline-edit-text-input" contenteditable="true" class="context-textarea" style="min-height:80px;resize:none;" @mouseup="saveRichTextSelection" @keyup="saveRichTextSelection" @input="handleRichTextInput" @blur="handleRichTextInput" @click="showColorPicker=false"></div>
              <p v-if="!isSectionStyleTarget" style="font-size:11px;color:var(--text-muted);padding-top:4px;">Select text, then apply formatting above.</p>
              <!-- Action buttons -->
              <div style="display:flex;flex-wrap:wrap;align-items:center;gap:8px;padding-top:12px;border-top:var(--glass-border);">
                <button type="button" class="apply-btn" style="border:1px solid var(--accent);color:var(--accent);" @click="resetStylesAction">Reset</button>
                <button type="button" class="btn btn-ghost text-xs px-2 py-1" @click="cancelStylesAction">Cancel</button>
                <button type="button" class="context-apply-btn" style="margin-left:auto;width:auto;padding:5px 14px;" :disabled="inlineEditSubmitting" @click="applyStylesAction">{{ inlineEditSubmitting ? 'Applying...' : 'Apply' }}</button>
                <span v-if="inlineEditError" style="width:100%;font-size:11px;color:var(--danger);">{{ inlineEditError }}</span>
              </div>
            </div>

            <!-- Lock banner -->
            <div v-if="isLockedByAnotherEditor" data-testid="edit-lock-banner" role="status" style="margin-top:12px;border-radius:8px;padding:8px 12px;background:rgba(251,191,36,.08);border:1px solid rgba(251,191,36,.25);color:#fbbf24;font-size:12px;">
              <div class="font-medium" data-testid="edit-lock-owner">Locked by {{ lockOwnerLabel }}</div>
              <div v-if="lockExpiresAtLabel" style="font-size:11px;opacity:.75;margin-top:2px;" data-testid="edit-lock-expires">Expires around {{ lockExpiresAtLabel }}</div>
            </div>
            <div v-if="lockConflictMessage" data-testid="edit-lock-conflict-message" role="alert" style="margin-top:8px;border-radius:8px;padding:8px 12px;background:rgba(248,113,113,.08);border:1px solid rgba(248,113,113,.25);color:var(--danger);font-size:12px;display:flex;align-items:flex-start;justify-content:space-between;gap:8px;">
              <span style="flex:1;">{{ lockConflictMessage }}</span>
              <button type="button" style="font-size:11px;font-weight:600;text-decoration:underline;" data-testid="edit-lock-conflict-dismiss" @click="siteStore.clearLockConflict()">Dismiss</button>
            </div>

          </div>
        </div>
      </div>

    </aside>

    <!-- Undo template button (keep as-is) -->

    <!-- Undo template button -->
    <div v-if="showPromptUndo" style="position:fixed;bottom:100px;left:50%;transform:translateX(-50%);z-index:110;">
      <button type="button" class="btn btn-ghost text-xs" @click="undoSuggestion">↩ Undo template</button>
    </div>

    <!-- 6. Status bar -->
    <div id="statusbar">
      <span class="statusbar-brand"><img :src="brandLogo" alt="BeyondWebsite.ai" /></span>
      <span class="statusbar-sep">|</span>
      <span>{{ site?.name || '…' }}</span>
      <span class="statusbar-sep">|</span>
      <span v-if="activitySteps.length">{{ latestSummary }}</span>
      <span v-else style="color:var(--success);">Ready</span>
      <span class="statusbar-sep" v-if="cliOutput.length">|</span>
      <button v-if="cliOutput.length" @click="showCli=!showCli" style="color:var(--text-muted);background:none;border:none;cursor:pointer;font-size:11px;">{{ showCli ? 'Hide' : 'Show' }} Details</button>
    </div>

    <!-- Modals -->
    <BlogCreateModal
      v-if="showBlogModal"
      @close="showBlogModal = false"
      @create="handleBlogCreate"
    />
    <SeoOptimizationModal
      v-if="showSeoModal"
      @close="showSeoModal = false"
      @create="handleSeoOptimization"
    />
    <LandingPageWizardModal
      v-if="showLandingPageModal"
      @close="showLandingPageModal = false"
      @create="handleLandingPageCreate"
    />
    <SearchEngineSubmissionModal
      v-if="showSearchEngineModal"
      :site="site"
      @close="showSearchEngineModal = false"
      @create="handleSearchEngineSubmission"
    />
    <div v-if="showCreatePageModal" class="editor-modal-overlay" data-testid="create-page-modal" @keydown.esc.stop.prevent="closeCreatePageModal">
      <div class="editor-modal-backdrop" @click="closeCreatePageModal"></div>
      <div class="editor-modal-card">
        <div class="editor-modal-header">
          <div>
            <div class="context-subtitle">Pages</div>
            <div class="context-section-name">Create new page</div>
          </div>
          <button class="context-close" type="button" aria-label="Close create page modal" @click="closeCreatePageModal">×</button>
        </div>
        <div class="editor-modal-body">
          <label class="editor-modal-label" for="new-page-title">Page title</label>
          <input id="new-page-title" v-model="createPageForm.title" class="context-textarea editor-inline-input" data-testid="new-page-title" placeholder="Careers" @input="syncCreatePageSlug" />

          <label class="editor-modal-label" for="new-page-slug">URL slug</label>
          <div style="display:flex;align-items:center;gap:8px;">
            <span style="color:var(--text-muted);font-size:12px;">/</span>
            <input id="new-page-slug" v-model="createPageForm.slug" class="context-textarea editor-inline-input" data-testid="new-page-slug" placeholder="careers" @input="createPageTouchedSlug = true" />
          </div>

          <label class="editor-modal-label" for="new-page-brief">What should this page include?</label>
          <textarea id="new-page-brief" v-model="createPageForm.brief" class="context-textarea" rows="4" maxlength="500" data-testid="new-page-brief" placeholder="Hiring info, open roles, culture, application CTA..."></textarea>

          <label class="editor-modal-label" for="new-page-nav">Where should visitors find it?</label>
          <div class="custom-select-wrap" style="width:100%;">
            <button type="button" class="custom-select-trigger editor-inline-select" style="width:100%;" data-testid="new-page-nav-placement"
              @click.stop="toggleDropdown('navPlacement')">
              <span>{{ { header: 'Header navigation', footer: 'Footer navigation', both: 'Header and footer', none: 'Do not add to navigation' }[createPageForm.navPlacement] || 'Header navigation' }}</span>
              <svg class="custom-select-chevron" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 3.5l3 3 3-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div v-if="isDropdownOpen('navPlacement')" class="custom-select-menu">
              <button type="button" v-for="opt in [['header','Header navigation'],['footer','Footer navigation'],['both','Header and footer'],['none','Do not add to navigation']]" :key="opt[0]"
                class="custom-select-option" :class="{ active: createPageForm.navPlacement === opt[0] }"
                @click.stop="createPageForm.navPlacement = opt[0]; closeAllDropdowns()">{{ opt[1] }}</button>
            </div>
          </div>

          <label class="editor-modal-label" for="new-page-position">Position</label>
          <div class="custom-select-wrap" style="width:100%;">
            <button type="button" class="custom-select-trigger editor-inline-select" style="width:100%;" data-testid="new-page-nav-position"
              @click.stop="toggleDropdown('navAfter')">
              <span>{{ createPageForm.navAfter ? `After ${availablePreviewPages.find(p => p.path === createPageForm.navAfter)?.label || createPageForm.navAfter}` : 'Best fit' }}</span>
              <svg class="custom-select-chevron" width="10" height="10" viewBox="0 0 10 10" fill="none"><path d="M2 3.5l3 3 3-3" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/></svg>
            </button>
            <div v-if="isDropdownOpen('navAfter')" class="custom-select-menu">
              <button type="button" class="custom-select-option" :class="{ active: createPageForm.navAfter === '' }"
                @click.stop="createPageForm.navAfter = ''; closeAllDropdowns()">Best fit</button>
              <button type="button" v-for="page in availablePreviewPages" :key="page.path"
                class="custom-select-option" :class="{ active: createPageForm.navAfter === page.path }"
                @click.stop="createPageForm.navAfter = page.path; closeAllDropdowns()">After {{ page.label || page.path }}</button>
            </div>
          </div>

          <p v-if="createPageError" class="canvas-error-banner" style="position:static;padding:8px;border-radius:8px;border:1px solid rgba(248,113,113,.2);" data-testid="new-page-error">{{ createPageError }}</p>
          <p style="font-size:11px;color:var(--text-muted);line-height:1.5;">This uses the website repo as context. Codex will inspect existing pages, components, and CSS, then create the new page in the same visual style.</p>
        </div>
        <div class="editor-modal-footer">
          <button type="button" class="btn btn-ghost text-xs px-3 py-2" @click="closeCreatePageModal">Cancel</button>
          <button type="button" class="btn btn-accent text-xs px-3 py-2 disabled:opacity-50" :disabled="creatingPage" data-testid="create-page-submit" @click="submitCreatePage">
            {{ creatingPage ? 'Creating...' : 'Create page' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, watch, nextTick, onMounted, onUnmounted } from 'vue'
import { useRoute } from 'vue-router'
import { useSitesStore } from '../stores/sites'
import { format } from 'date-fns'
import { useToast } from 'vue-toastification'
import api from '../utils/api'
import BlogCreateModal from '../components/BlogCreateModal.vue'
import SeoOptimizationModal from '../components/SeoOptimizationModal.vue'
import SearchEngineSubmissionModal from '../components/SearchEngineSubmissionModal.vue'
import LandingPageWizardModal from '../components/LandingPageWizardModal.vue'
import ConversationDiff from '../components/ConversationDiff.vue'
import brandLogo from '../assets/bw-logo-wordmark.svg'
import { track } from '../plugins/amplitude'

export default {
  name: 'Editor',
  components: {
    BlogCreateModal,
    SeoOptimizationModal,
    SearchEngineSubmissionModal,
    LandingPageWizardModal,
    ConversationDiff
  },
  setup() {
    const route = useRoute()
    const siteStore = useSitesStore()
    const toast = useToast()

    const site = ref(null)
    const updates = ref([])
    const submitting = ref(false)
    const sseConnection = ref(null)
    const refreshingPreview = ref(false)
    const showCli = ref(false)

    // Custom dropdown — single open tracker to ensure only one is open at a time
    const openDropdownId = ref(null)
    const toggleDropdown = (id) => { openDropdownId.value = openDropdownId.value === id ? null : id }
    const closeAllDropdowns = () => { openDropdownId.value = null }
    const isDropdownOpen = (id) => openDropdownId.value === id
    const retrying = ref(false)
    const leftPanelOpen = ref(true)
    const rightPanelOpen = ref(false)
    // The right context panel shows either the element 'edit' flow or the 'seo' editor.
    const rightPanelMode = ref('edit')
    const openSeoPanel = () => {
      // Toggle: a second click on the top-bar SEO button closes the panel.
      if (rightPanelOpen.value && rightPanelMode.value === 'seo') {
        rightPanelOpen.value = false
        return
      }
      rightPanelMode.value = 'seo'
      rightPanelOpen.value = true
      if (!seoPages.value.length) loadSeoPages()
    }
    const zoomLevel = ref(100)
    const zoomIn  = () => { zoomLevel.value = Math.min(zoomLevel.value + 10, 150) }
    const zoomOut = () => { zoomLevel.value = Math.max(zoomLevel.value - 25, 25)  }

    // Preview-viewport sizing: the iframe fills as much of the canvas frame
    // as it can while honoring a chosen aspect ratio. Device-mode buttons
    // are just aspect-ratio presets — they don't dictate a fixed virtual
    // width. The custom mode lets the user type any "W:H" ratio. Resizing
    // the sidebar changes the canvas dimensions; the preview recomputes
    // its (width, height) to maximize fit at the locked aspect.
    const canvasFrameEl = ref(null)
    const canvasFrameWidth = ref(1280)
    const canvasFrameHeight = ref(800)
    let canvasFrameResizeObserver = null

    const ASPECT_PRESETS = {
      desktop: 16 / 9,
      tablet:  3 / 4,   // tablet portrait — natural device shape
      mobile:  9 / 16,  // phone portrait
    }

    const aspectRatio = ref(ASPECT_PRESETS.desktop)
    const customAspectInput = ref('16:9')
    const customAspectError = ref('')
    const showCustomAspectInput = ref(false)

    const setAspectFromDevice = (mode) => {
      deviceMode.value = mode
      if (ASPECT_PRESETS[mode]) aspectRatio.value = ASPECT_PRESETS[mode]
      showCustomAspectInput.value = false
      customAspectError.value = ''
    }

    const openCustomAspect = () => {
      deviceMode.value = 'custom'
      showCustomAspectInput.value = !showCustomAspectInput.value
      customAspectError.value = ''
    }

    const applyCustomAspect = () => {
      const raw = (customAspectInput.value || '').trim()
      const m = raw.match(/^(\d+(?:\.\d+)?)\s*[:\/x]\s*(\d+(?:\.\d+)?)$/i)
      if (!m) {
        customAspectError.value = 'Use "W:H" (e.g. 16:9 or 4:3).'
        return
      }
      const w = parseFloat(m[1])
      const h = parseFloat(m[2])
      if (!Number.isFinite(w) || !Number.isFinite(h) || w <= 0 || h <= 0) {
        customAspectError.value = 'Both numbers must be positive.'
        return
      }
      aspectRatio.value = w / h
      customAspectError.value = ''
    }

    // Largest rectangle inside (canvasW, canvasH) that matches aspect = W/H.
    // scale = min(canvasW / aspectNumerator, canvasH / aspectDenominator)
    // We use W=aspect, H=1 internally so the same formula works for any ratio.
    const previewDimensions = computed(() => {
      const cw = Math.max(0, canvasFrameWidth.value)
      const ch = Math.max(0, canvasFrameHeight.value)
      const ar = aspectRatio.value
      if (!cw || !ch || !ar || !Number.isFinite(ar)) {
        return { width: cw, height: ch }
      }
      // Solve: width / height = ar, width ≤ cw, height ≤ ch — maximize the
      // pair. Width is the binding dimension if cw / ar ≤ ch, otherwise
      // height binds and width = ch × ar.
      const widthIfWidthBound = cw
      const heightIfWidthBound = cw / ar
      if (heightIfWidthBound <= ch) {
        return { width: Math.floor(widthIfWidthBound), height: Math.floor(heightIfWidthBound) }
      }
      const heightIfHeightBound = ch
      const widthIfHeightBound = ch * ar
      return { width: Math.floor(widthIfHeightBound), height: Math.floor(heightIfHeightBound) }
    })

    const previewShellStyle = computed(() => ({
      width: previewDimensions.value.width + 'px',
      height: previewDimensions.value.height + 'px',
      zoom: zoomLevel.value / 100
    }))

    // Sidebar resize (drag handle on the right edge). Updates --sidebar-width
    // on .editor-shell directly so the grid column reacts instantly without
    // Vue re-rendering on every pixel. Min 200 / max 600 keeps the
    // conversation usable and prevents the canvas from collapsing.
    const SIDEBAR_MIN = 200
    const SIDEBAR_MAX = 600
    const isResizingSidebar = ref(false)

    const setSidebarWidth = (px) => {
      const clamped = Math.max(SIDEBAR_MIN, Math.min(SIDEBAR_MAX, px))
      const shell = document.querySelector('.editor-shell')
      if (shell) shell.style.setProperty('--sidebar-width', clamped + 'px')
    }

    const startSidebarResize = (downEvent) => {
      downEvent.preventDefault()
      isResizingSidebar.value = true
      document.body.classList.add('sidebar-resizing')
      const sidebar = document.querySelector('#sidebar')
      const startX = downEvent.clientX
      const startWidth = sidebar ? sidebar.getBoundingClientRect().width : 260
      const onMove = (moveEvent) => {
        const delta = moveEvent.clientX - startX
        setSidebarWidth(startWidth + delta)
      }
      const onUp = () => {
        isResizingSidebar.value = false
        document.body.classList.remove('sidebar-resizing')
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
      }
      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    }
    const inlineEditorExpanded = ref(false)
    const deviceMode = ref('desktop')
    const activeSheetTab = ref('update')
    const previewStatus = ref('idle')
    const siteCommits = ref([])
    const historySearchQuery = ref('')
    const filteredCommits = computed(() => {
      const q = historySearchQuery.value.trim().toLowerCase()
      if (!q) return siteCommits.value
      return siteCommits.value.filter(c =>
        (c.message || '').toLowerCase().includes(q) ||
        (c.shortSha || '').toLowerCase().includes(q)
      )
    })
    const loadingCommits = ref(false)
    const commitsError = ref('')
    const restoringCommit = ref(null)
    const restoreSuccess = ref(false)
    const restoreError = ref('')
    const publishing = ref(false)
    const publishingSandbox = ref(false)
    const syncingGitHub = ref(false)
    const showSuggestions = ref(false)
    const showBlogModal = ref(false)
    const showSeoModal = ref(false)
    const showLandingPageModal = ref(false)
    const showSearchEngineModal = ref(false)
    const showCreatePageModal = ref(false)
    const creatingPage = ref(false)
    const createPageError = ref('')
    const createPageTouchedSlug = ref(false)
    const createPageForm = reactive({
      title: '',
      slug: '',
      brief: '',
      navPlacement: 'header',
      navAfter: ''
    })
    // Track the iframe's current pathname for context-aware generation. The
    // preview bridge (handleInlineEditMessage) maintains this; we fall back to
    // the preview_url's pathname when the bridge hasn't reported yet.
    const currentPreviewRoute = ref('')
    // Full path+query+hash last reported by the preview bridge (AC006).
    // Used to restore the current page on refresh instead of always going to /.
    const lastKnownPreviewPath = ref('')
    const previewHistoryStack = ref([])
    const previewHistoryIndex = ref(-1)
    const isNavigatingHistory = ref(false)
    const canGoBack = computed(() => previewHistoryIndex.value > 0)
    const canGoForward = computed(() => previewHistoryIndex.value < previewHistoryStack.value.length - 1)
    const availablePreviewPages = ref([])
    const pagesSearchQuery = ref('')
    const filteredPages = computed(() => {
      const q = pagesSearchQuery.value.trim().toLowerCase()
      if (!q) return availablePreviewPages.value
      return availablePreviewPages.value.filter(p =>
        (p.label || '').toLowerCase().includes(q) ||
        (p.path || '').toLowerCase().includes(q)
      )
    })
    const showPromptUndo = ref(false)
    const previousPrompt = ref('')
    const attachmentInput = ref(null)
    const promptInputEl = ref(null)
    const previewReloadKey = ref(0)
    const previewRuntime = ref(null)
    const previewRuntimePoller = ref(null)
    const autoPreviewRefreshInFlight = ref(false)
    const leftPanelWasOpenBeforeEdit = ref(false)

    // Creation feedback pause state — derived from latest activity step so it
    // survives page refresh (cliOutput is persisted; in-memory refs are not).
    const creationFeedbackSubmitted = ref(false)
    const creationFeedbackPending = computed(() =>
      !creationFeedbackSubmitted.value &&
      latestStep.value?.eventType === 'awaiting_creation_feedback'
    )
    const creationFeedbackSubmitting = ref(false)
    const creationFeedbackComment = ref('')
    const creationFeedbackError = ref('')
    const showCreationFeedbackInput = ref(false)

    const editMode = ref(false)
    const selectedElement = ref(null)
    const SUPPORTED_EDITABLE_TAGS = new Set([
      'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'span', 'a', 'li', 'button', 'label', 'strong', 'em',
      'div', 'small', 'time', 'cite', 'address',
      'blockquote', 'dt', 'dd', 'figcaption',
      'section', 'article', 'header', 'footer', 'main', 'aside'
    ])
    const isSupportedEditableTag = (tagName) => {
      if (!tagName || typeof tagName !== 'string') return false
      return SUPPORTED_EDITABLE_TAGS.has(tagName.toLowerCase())
    }
    // Human-readable name for a selected element so the inline editor reads
    // "Editing: Heading" instead of "Editing: H2".
    const friendlyTagLabel = (tagName) => {
      const t = (tagName || '').toLowerCase()
      const map = {
        h1: 'Heading', h2: 'Heading', h3: 'Heading', h4: 'Heading', h5: 'Heading', h6: 'Heading',
        p: 'Text', span: 'Text', a: 'Link', button: 'Button', li: 'List item',
        img: 'Image', label: 'Label', blockquote: 'Quote',
        section: 'Section', article: 'Section', header: 'Header', footer: 'Footer', div: 'Block'
      }
      return map[t] || (t ? t.toUpperCase() : 'Element')
    }
    const showContextualOptionRow = computed(() =>
      editMode.value
      && !!selectedElement.value
      && !inlineBridgeFailed.value
      && isSupportedEditableTag(selectedElement.value.tagName)
    )
    const SECTION_STYLE_TAGS = new Set(['section', 'article', 'header', 'footer', 'main', 'aside', 'div'])
    const isSectionStyleTarget = computed(() =>
      !!selectedElement.value?.isSectionTarget
      && SECTION_STYLE_TAGS.has((selectedElement.value.tagName || '').toLowerCase())
    )
    const pendingStyles = reactive({
      fontFamily: '',
      fontSize: '',
      bold: false,
      italic: false,
      color: '',
      backgroundColor: ''
    })
    const pendingText = ref('')
    const pendingHtml = ref('')
    const inlineEditorEl = ref(null)
    const savedRichTextSelection = ref(null)
    const siteThemeColors = ref({ accents: /** @type {string[]} */ ([]), neutrals: /** @type {string[]} */ ([]) })
    const customColor = ref('#111827')
    const showColorPicker = ref(false)
    const currentTextColor = ref('#111827')
    const lastCustomColor = ref((() => { try { return sessionStorage.getItem('bwai_inline_last_color') || '#111827' } catch { return '#111827' } })())
    const recentColors = ref(/** @type {string[]} */ ([]))

    const FALLBACK_ACCENTS = ['#7C3AED', '#4F46E5', '#2563EB', '#059669', '#DC2626', '#F59E0B', '#E85D04', '#0891B2']
    const FALLBACK_NEUTRALS = ['#111827', '#6B7280']

    const paletteAccents = computed(() =>
      siteThemeColors.value.accents.length ? siteThemeColors.value.accents : FALLBACK_ACCENTS
    )
    const paletteNeutrals = computed(() =>
      siteThemeColors.value.neutrals.length ? siteThemeColors.value.neutrals : FALLBACK_NEUTRALS
    )
    const fontFamilyOptions = computed(() => {
      const current = pendingStyles.fontFamily ? [pendingStyles.fontFamily] : []
      return Array.from(new Set([
        ...current,
        '"Fraunces", serif',
        'Inter, sans-serif',
        'Arial, sans-serif',
        'Helvetica, sans-serif',
        'Georgia, serif',
        'Times New Roman, serif',
        'Courier New, monospace',
        'Montserrat, sans-serif',
        'Poppins, sans-serif',
        'Playfair Display, serif',
        'Merriweather, serif'
      ]))
    })
    const fontSizeOptions = computed(() => {
      const current = pendingStyles.fontSize ? [pendingStyles.fontSize] : []
      return Array.from(new Set([...current, '12px', '14px', '16px', '18px', '20px', '24px', '32px', '48px', '1rem', '1.25rem', '1.5rem', '2rem']))
    })
    const fontStyleChoice = computed({
      get() {
        if (pendingStyles.bold && pendingStyles.italic) return 'boldItalic'
        if (pendingStyles.bold) return 'bold'
        if (pendingStyles.italic) return 'italic'
        return 'regular'
      },
      set(value) {
        pendingStyles.bold = value === 'bold' || value === 'boldItalic'
        pendingStyles.italic = value === 'italic' || value === 'boldItalic'
        livePreviewStyles()
      }
    })
    // Session-scoped persisted edits (BUI-US-32 AC#5). Reset on iframe reload.
    const committedSessionEdits = reactive({})
    // Draft commits applied locally but not yet pushed. Each entry: { updateRequestId, draftCommitHash, selector }
    const pendingDraftCommits = ref([])
    // AC010: server is authoritative for pending state — after refresh/reopen
    // the in-memory pendingDraftCommits array is empty, but the server still
    // knows there are pending_confirmation drafts. Reflect server's count via
    // the lock-status response so the unsaved indicator + confirm/undo buttons
    // appear immediately on reload instead of waiting for the next mutation.
    const serverPendingCount = computed(() => siteStore.inlineEditLockStatus?.pendingCount || 0)
    const recoveredPendingCount = computed(() =>
      Math.max(pendingDraftCommits.value.length, serverPendingCount.value)
    )
    const hasPendingDraftCommits = computed(() => recoveredPendingCount.value > 0)
    const inlineEditSubmitting = ref(false)
    const confirmingInlineEdits = ref(false)
    const confirmInlineEditsError = ref('')
    const undoingInlineEdit = ref(false)
    const undoInlineEditError = ref('')
    const previewIframeEl = ref(null)
    const inlineEditError = ref('')
    const inlineBridgeReady = ref(false)
    const inlineBridgeFailed = ref(false)
    const inlineBridgeFailureReason = ref('')
    const inlineBridgeHandshakeTimer = ref(null)
    const INLINE_BRIDGE_HANDSHAKE_TIMEOUT_MS = 5000

    // ── Edit-lock UI state (AC009 / AC010 / AC012) ───────────────────────
    // The authoritative lock status comes from /sites/:id/edit-lock and is
    // refreshed on mount, after every mutation, and after every 409. While
    // `isLockedByAnotherEditor` is true the UI disables edit-mode toggle,
    // assistant-submit, and the apply/undo/confirm controls — so partial
    // state can't drive a mutation that would just bounce as a 409.
    const isLockedByAnotherEditor = computed(() => {
      const s = siteStore.inlineEditLockStatus
      return !!(s && s.locked && !s.isOwner)
    })
    const lockOwnerLabel = computed(() => {
      const s = siteStore.inlineEditLockStatus
      return s?.lock?.ownerEmail || 'another editor'
    })
    const lockExpiresAtLabel = computed(() => {
      const s = siteStore.inlineEditLockStatus
      if (!s?.lock?.expiresAt) return null
      try { return new Date(s.lock.expiresAt).toLocaleTimeString() } catch { return null }
    })
    const lockConflictMessage = computed(() => {
      const c = siteStore.lastLockConflict
      if (!c) return null
      return formatLockActionMessage(c.code, c.heldByEmail, c.message)
    })

    // AC012: shared formatter so confirm + undo + every other lock-conflict
    // surface in the UI maps the same backend code to the same actionable
    // copy. Keyed by `code`; falls back to the backend message when unknown.
    const formatLockActionMessage = (code, heldByEmail, fallbackMsg) => {
      const who = heldByEmail || 'another editor'
      switch (code) {
        case 'edit_lock_conflict':
          return `Locked by ${who}. Wait for them to finish or for the lock to expire, then try again.`
        case 'edit_lock_action_race':
        case 'edit_lock_stale':
          return 'Another action just completed. Refresh to see the latest state, then retry.'
        case 'edit_lock_missing':
          return 'No active edit session for this action — there is nothing to confirm or undo.'
        case 'inline_edit_undo_conflict':
          return 'Undo cannot be applied because later changes depend on this edit. Please confirm or manually adjust.'
        default:
          return fallbackMsg || 'Action could not complete due to a lock conflict.'
      }
    }

    const updateForm = reactive({
      prompt: '',
      attachments: []
    })

    // Ask-mode session state. askHistory holds the in-session Q/A pairs so
    // they remain visible without a refresh (AC004). askError surfaces the
    // most recent submission error so the UX can render an actionable
    // message and the typed question can be retried (AC009).
    const askHistory = ref([])
    const askError = ref('')
    const askHistoryCollapsed = ref(false)
    const lastSubmittedPrompt = ref('')
    const conversationPaneEl = ref(null)
    const conversationResetAt = ref(0)

    // Live-updates section collapsed by default — the user sees a status line
    // in the header and can expand to see the verbose step-by-step feed.
    // Actionable transient signals (askError, creationFeedbackPending) bypass
    // the collapse and always render, since the user can't act on them
    // without seeing them.
    const liveUpdatesCollapsed = ref(true)

    const parseStructuredPayload = (update) => {
      const raw = update?.structured_payload
      if (!raw) return null
      if (typeof raw === 'string') {
        try { return JSON.parse(raw) } catch (_) { return null }
      }
      return typeof raw === 'object' ? raw : null
    }

    const conversationResetStorageKey = computed(() =>
      route.params.id ? `bwai:conversation-reset-at:${route.params.id}` : 'bwai:conversation-reset-at'
    )

    const loadConversationResetAt = () => {
      try {
        const raw = window.localStorage?.getItem(conversationResetStorageKey.value)
        const value = Number(raw || 0)
        conversationResetAt.value = Number.isFinite(value) ? value : 0
      } catch (_) {
        conversationResetAt.value = 0
      }
    }

    const isAfterConversationReset = (value) => {
      const resetAt = Number(conversationResetAt.value || 0)
      if (!resetAt) return true
      const time = new Date(value || 0).getTime()
      return Number.isFinite(time) && time >= resetAt
    }

    const makeAskHistoryEntry = (update) => {
      const payload = parseStructuredPayload(update)
      const question = payload?.question || update?.prompt || ''
      const answer = payload?.answer || ''
      if (!question || !answer) return null
      return {
        id: update?.id || payload?.generatedAt || `${question}:${answer.length}`,
        question,
        answer,
        generatedAt: payload?.generatedAt || update?.completed_at || update?.updated_at || update?.created_at
      }
    }

    const mergeAskHistoryEntries = (entries) => {
      const merged = [...askHistory.value]
      const seen = new Set(merged.map(entry => entry.id))
      for (const entry of entries) {
        if (!entry || seen.has(entry.id)) continue
        if (!isAfterConversationReset(entry.generatedAt)) continue
        merged.push(entry)
        seen.add(entry.id)
      }
      askHistory.value = merged
    }

    // Unified conversation log: Ask + Change turns interleaved chronologically.
    // Both modes share the bwai_update_requests table — the only discriminator
    // is request_type. conversationTurns is a separate fetch (wider window, no
    // type filter) so the existing per-mode flows (updates[] limit:3, askHistory
    // hydration) stay untouched.
    const conversationTurns = ref([])
    const pendingChangeTurns = ref([])
    const conversationLimit = 10
    const conversationOffset = ref(0)
    const conversationHasMore = ref(false)
    const conversationLoadingMore = ref(false)

    const parseFileChanges = (raw) => {
      if (!raw) return []
      if (Array.isArray(raw)) return raw
      if (typeof raw === 'string') {
        try { const parsed = JSON.parse(raw); return Array.isArray(parsed) ? parsed : [] } catch (_) { return [] }
      }
      return []
    }

    const normalizeUpdateToTurn = (row) => {
      if (!row || !row.id) return null
      const isAsk = row.request_type === 'question'
      const structured = (() => {
        const raw = row.structured_payload
        if (!raw) return null
        if (typeof raw === 'string') { try { return JSON.parse(raw) } catch (_) { return null } }
        return typeof raw === 'object' ? raw : null
      })()
      const isAnswer = isAsk || (row.status === 'answered' && typeof structured?.answer === 'string')
      const fileChanges = parseFileChanges(row.file_changes)
      const touchedFiles = Array.isArray(row.touched_files) ? row.touched_files : []
      const createdAt = row.created_at || row.createdAt || null
      const completedAt = row.completed_at || row.completedAt || row.updated_at || row.updatedAt || createdAt
      // AI Change turns expose their per-edit selectors via structured_payload.
      // Pulling them out at normalize-time means the highlight pipeline can
      // treat AI selectors and inline-edit selectors identically.
      const editSelectors = []
      if (!isAsk && structured && Array.isArray(structured.edits)) {
        for (const edit of structured.edits) {
          if (edit && typeof edit.selector === 'string' && edit.selector) {
            editSelectors.push(edit.selector)
          }
        }
      }
      return {
        id: row.id,
        mode: isAnswer ? 'ask' : 'change',
        prompt: (isAsk && structured?.question) || row.prompt || '',
        status: row.status || 'pending',
        requestType: row.request_type || row.requestType || null,
        structuredPayload: structured,
        clarificationQuestion: structured?.clarificationQuestion || structured?.clarification?.question || null,
        // The workflow persists the commit hash inside structured_payload.
        // We accept it from either location (top-level column for inline
        // edits, structured_payload for AI Change turns) so the Revert
        // button works for both flows.
        draftCommitSha: row.draft_commit_sha || row.draftCommitSha || structured?.draftCommitHash || null,
        touchedFiles,
        touchedSelectors: [],
        editSelectors,
        response: isAnswer
          ? { kind: 'ask', text: structured?.answer || '' }
          : {
              kind: 'change',
              summary: structured?.changeSummary || row.summary || '',
              fileChanges,
              warnings: structured?.warnings || []
            },
        createdAt,
        completedAt
      }
    }

    const mergeConversationRows = (rows, { prepend = false } = {}) => {
      const turns = rows
        .map(normalizeUpdateToTurn)
        .filter(Boolean)
        .filter(turn => isAfterConversationReset(turn.createdAt))
      const merged = prepend
        ? [...turns, ...conversationTurns.value]
        : turns
      const byId = new Map()
      for (const turn of merged) byId.set(turn.id, turn)
      const sorted = [...byId.values()]
      sorted.sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
      conversationTurns.value = sorted
    }

    const refreshConversationTurns = async ({ reset = true } = {}) => {
      try {
        const offset = reset ? 0 : conversationOffset.value
        const payload = await siteStore.fetchUpdateRequests(route.params.id, {
          limit: conversationLimit,
          offset
        })
        const rows = Array.isArray(payload?.updates)
          ? payload.updates
          : Array.isArray(payload)
            ? payload
            : []
        mergeConversationRows(rows, { prepend: !reset })
        conversationOffset.value = offset + rows.length
        conversationHasMore.value = rows.length === conversationLimit
      } catch (error) {
        console.warn('Failed to refresh conversation turns:', error)
      }
    }

    const resetConversation = async () => {
      if (submitting.value) return
      const now = Date.now()
      conversationResetAt.value = now
      try {
        window.localStorage?.setItem(conversationResetStorageKey.value, String(now))
      } catch (_) {}
      conversationTurns.value = []
      pendingChangeTurns.value = []
      askHistory.value = []
      askError.value = ''
      conversationOffset.value = 0
      conversationHasMore.value = false
      expandedActivityTurnIds.value = new Set()
      turnDiffs.value = {}
      askHistoryCollapsed.value = false
      updateForm.prompt = ''
      updateForm.attachments = []
    }

    const loadPreviousConversationTurns = async () => {
      if (conversationLoadingMore.value || !conversationHasMore.value) return
      conversationLoadingMore.value = true
      try {
        await refreshConversationTurns({ reset: false })
      } finally {
        conversationLoadingMore.value = false
      }
    }

    const handleConversationScroll = () => {
      const el = conversationPaneEl.value
      if (!el || el.scrollTop > 24) return
      loadPreviousConversationTurns()
    }

    const buildAssistantTurn = (sourceTurn, assistantKind, prompt) => ({
      id: `assistant:${assistantKind}:${sourceTurn.id}`,
      mode: 'assistant',
      assistantKind,
      sourceTurn,
      sourceTurnId: sourceTurn.id,
      prompt,
      status: 'assistant_question',
      requestType: assistantKind,
      draftCommitSha: null,
      touchedFiles: [],
      touchedSelectors: [],
      editSelectors: [],
      response: { kind: 'assistant_question', text: '' },
      createdAt: sourceTurn.createdAt,
      completedAt: null
    })

    const summarizeChangeForConfirmation = (rawPrompt) => {
      const prompt = typeof rawPrompt === 'string' ? rawPrompt.trim() : ''
      if (!prompt) {
        return 'You want me to update the website preview. Should I go ahead?'
      }
      const cleaned = prompt.replace(/\s+/g, ' ')
      const lower = cleaned.toLowerCase()
      if (/\bcontact\b/.test(lower) && /\b(page|form)\b/.test(lower)) {
        return 'You want me to add a contact experience using this site’s existing style, and make it easy for visitors to find from the main navigation. Should I go ahead?'
      }
      if (/\b(nav|navigation|menu|header)\b/.test(lower)) {
        return `You want me to update the site navigation so visitors can find this more easily: “${cleaned}”. Should I go ahead?`
      }
      if (/\b(color|palette|style|font|design|redesign|restyle)\b/.test(lower)) {
        return `You want me to adjust the visual design of the website based on this direction: “${cleaned}”. Should I go ahead?`
      }
      if (/\b(page|section|hero|pricing|faq|testimonial|footer)\b/.test(lower)) {
        return `You want me to add or update website content/layout based on this direction: “${cleaned}”. Should I go ahead?`
      }
      return `You want me to make this website update: “${cleaned}”. Should I go ahead?`
    }

    const conversation = computed(() => {
      const pending = pendingChangeTurns.value.filter(turn => turn.status !== 'cancelled')
      const baseTurns = [...conversationTurns.value, ...pending]
        .sort((a, b) => new Date(a.createdAt || 0) - new Date(b.createdAt || 0))
      const rendered = []
      for (const turn of baseTurns) {
        rendered.push(turn)
        if (turn.mode !== 'change') continue

        if (turn.status === 'awaiting_user_confirmation') {
          rendered.push(buildAssistantTurn(
            turn,
            'confirm_change',
            summarizeChangeForConfirmation(turn.prompt)
          ))
          continue
        }

        if (isTurnAwaitingClarification(turn) && !isTurnAwaitingPublish(turn)) {
          rendered.push(buildAssistantTurn(
            turn,
            'clarification',
            clarificationQuestionForTurn(turn.id) || 'Could you provide more detail?'
          ))
          continue
        }

        if (isTurnAwaitingPublish(turn)) {
          rendered.push(buildAssistantTurn(
            turn,
            'review',
            'I updated the preview. Does it look ok?'
          ))
        }
      }
      return rendered
    })

    const pendingConfirmationTurn = computed(() =>
      pendingChangeTurns.value.find(turn => turn.status === 'awaiting_user_confirmation') || null
    )

    const awaitingReviewTurn = computed(() => {
      const turns = [...conversationTurns.value]
        .filter(turn => turn.mode === 'change' && isTurnAwaitingPublish(turn))
        .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
      return turns[0] || null
    })

    const turnModeLabel = (turn) => {
      if (turn?.mode === 'assistant') return 'Assistant'
      return 'User'
    }

    const turnModeClass = (turn) => {
      if (turn?.mode === 'assistant') return 'pill-assistant'
      return 'pill-change'
    }

    // Per-turn diff state — populated lazily when the user expands a turn's
    // diff. Keyed by turn id; each entry tracks loading / error / data so the
    // UI can render the right state without flicker.
    const turnDiffs = ref({})

    const turnDiffState = (turnId) => {
      const entry = turnDiffs.value[turnId]
      return entry || { status: 'idle', diff: null, error: '' }
    }

    const isTurnDiffOpen = (turnId) => {
      const entry = turnDiffs.value[turnId]
      return !!(entry && entry.status !== 'idle' && entry.open !== false)
    }

    const toggleTurnDiff = async (turnId) => {
      if (!turnId) return
      const current = turnDiffs.value[turnId]
      // Toggle closed if already open — keep cached data so reopen is instant.
      if (current && current.status !== 'idle' && current.open !== false) {
        turnDiffs.value = { ...turnDiffs.value, [turnId]: { ...current, open: false } }
        return
      }
      // Reopen previously-fetched diff without re-fetching.
      if (current && current.status === 'loaded') {
        turnDiffs.value = { ...turnDiffs.value, [turnId]: { ...current, open: true } }
        return
      }
      // First open — fetch.
      turnDiffs.value = { ...turnDiffs.value, [turnId]: { status: 'loading', diff: null, error: '', open: true } }
      try {
        const response = await siteStore.fetchUpdateDiff(route.params.id, turnId)
        const diff = response?.diff || { hasContent: false, files: [] }
        turnDiffs.value = { ...turnDiffs.value, [turnId]: { status: 'loaded', diff, error: '', open: true } }
      } catch (error) {
        const message = error?.response?.data?.error || error?.message || 'Failed to load diff'
        turnDiffs.value = { ...turnDiffs.value, [turnId]: { status: 'error', diff: null, error: message, open: true } }
      }
    }

    const viewLatestAskAnswer = () => {
      askHistoryCollapsed.value = false
    }

    const renderMarkdown = (text) => {
      if (!text) return ''
      const escape = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      const inline = (s) => escape(s)
        .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>')
        .replace(/\*([^*\n]+)\*/g, '<em>$1</em>')
        .replace(/`([^`\n]+)`/g, '<code class="ask-inline-code">$1</code>')
      const lines = text.split('\n')
      const parts = []
      let listType = null
      let listItems = []
      const flushList = () => {
        if (!listType) return
        parts.push(`<${listType} class="ask-list">${listItems.map(li => `<li>${li}</li>`).join('')}</${listType}>`)
        listType = null; listItems = []
      }
      for (const line of lines) {
        const ol = line.match(/^(\d+)\.\s+(.*)/)
        const ul = line.match(/^[-*]\s+(.*)/)
        const h = line.match(/^#{1,3}\s+(.*)/)
        if (ol) { if (listType !== 'ol') { flushList(); listType = 'ol' }; listItems.push(inline(ol[2])) }
        else if (ul) { if (listType !== 'ul') { flushList(); listType = 'ul' }; listItems.push(inline(ul[1])) }
        else if (h) { flushList(); parts.push(`<p class="ask-heading">${inline(h[1])}</p>`) }
        else if (line.trim() === '') { flushList() }
        else { flushList(); parts.push(`<p class="ask-p">${inline(line)}</p>`) }
      }
      flushList()
      return parts.join('')
    }

    const assistantCopy = computed(() => {
      // When an open clarification is waiting, repurpose the chat input as
      // the reply box — make that obvious in the placeholder + submit label
      // so users don't think they're submitting a brand-new request.
      if (clarifyingTurn.value) {
        return {
          label: 'Answer the question above',
          placeholder: 'Type your answer to the AI’s question…',
          helperText: 'Your answer is sent to the in-flight change — not a new request.',
          submitLabel: 'Send answer',
          processingLabel: 'Sending…'
        }
      }
      return {
        label: 'Continue the conversation',
        placeholder: 'Ask a question, describe a website change, or reply to the latest message…',
        helperText: 'I’ll answer questions, ask for missing details, or update the website when the request is clear.',
        submitLabel: 'Send',
        processingLabel: 'Sending...'
      }
    })

    const maxAttachmentBytes = 5 * 1024 * 1024

    const promptSuggestions = ref([
      {
        id: 'seo-optimization',
        icon: '🚀',
        title: 'SEO Optimization',
        description: 'Improve search engine rankings and page performance',
        isModal: true,
        template: ''
      },
      {
        id: 'landing-page',
        icon: '📄',
        title: 'Landing Page Creation',
        description: 'Create a high-converting landing page for your product/service',
        isModal: true,
        template: ''
      },
      {
        id: 'design-refresh',
        icon: '🎨',
        title: 'Design & Style Updates',
        description: 'Modernize the visual design and user experience',
        template: 'Modernize the website design by:\n- Updating color scheme and typography\n- Improving layout and spacing\n- Adding modern UI components and interactions\n- Enhancing visual hierarchy\n- Making it more mobile-friendly'
      },
      {
        id: 'content-update',
        icon: '📝',
        title: 'Content Enhancement',
        description: 'Improve and add compelling content sections',
        template: 'Enhance the website content by:\n- Updating the about section with compelling copy\n- Adding testimonials and case studies\n- Creating an FAQ section\n- Improving product/service descriptions\n- Adding a blog or news section'
      },
      {
        id: 'conversion-optimization',
        icon: '📈',
        title: 'Conversion Optimization',
        description: 'Add elements to increase visitor engagement and conversions',
        template: 'Optimize for conversions by adding:\n- Clear and prominent call-to-action buttons\n- Lead capture forms (newsletter, contact)\n- Trust indicators (security badges, certifications)\n- Urgency elements (limited time offers)\n- Exit-intent popups or banners'
      },
      {
        id: 'performance-boost',
        icon: '⚡',
        title: 'Performance Enhancement',
        description: 'Optimize loading speed and technical performance',
        template: 'Improve website performance by:\n- Optimizing images for faster loading\n- Implementing lazy loading\n- Minifying CSS and JavaScript\n- Adding caching headers\n- Improving Core Web Vitals scores'
      },
      {
        id: 'search-engine-submission',
        icon: '🔍',
        title: 'Submit to Search Engines',
        description: 'Submit website to Google, Bing, and other search engines',
        isModal: true,
        template: ''
      },
      {
        id: 'add-blog-post',
        icon: '📝',
        title: 'Add Blog Post',
        description: 'Create a new blog article with guided workflow',
        isModal: true,
        template: ''
      }
    ])

    const workflowSearchQuery = ref('')
    const filteredWorkflowSuggestions = computed(() => {
      const q = workflowSearchQuery.value.trim().toLowerCase()
      if (!q) return promptSuggestions.value
      return promptSuggestions.value.filter(s =>
        (s.title || '').toLowerCase().includes(q) ||
        (s.description || '').toLowerCase().includes(q)
      )
    })

    const statusClass = computed(() => {
      const status = site.value?.status
      return {
        'bg-green-100 text-green-800': status === 'active',
        'bg-yellow-100 text-yellow-800': status === 'creating',
        'bg-red-100 text-red-800': status === 'failed',
        'bg-gray-100 text-gray-800': !status
      }
    })

    const formatDate = (dateString) => {
      const date = new Date(dateString)
      if (Number.isNaN(date.getTime())) {
        return '—'
      }
      return format(date, 'MMM d, yyyy h:mm a')
    }

    const loadSite = async () => {
      try {
        site.value = await siteStore.fetchSite(route.params.id)
        updates.value = await siteStore.fetchUpdateRequests(route.params.id, { limit: 3 })
        await loadPreviewRuntimeStatus({ syncPreviewUrl: true })
        await refreshPreview({ silent: true })
        if (site.value?.preview_url) {
          previewStatus.value = 'ready'
        }
        // AC010: fetch authoritative edit-lock state on (re)load so the UI
        // mirrors server state instead of relying only on transient in-memory
        // refs that would reset after a browser refresh.
        try { await siteStore.fetchInlineEditLockStatus(route.params.id) } catch (_) { /* best-effort */ }
      } catch (error) {
        console.error('Failed to load site:', error)
      }
    }

    const loadPreviewRuntimeStatus = async ({ syncPreviewUrl = false } = {}) => {
      try {
        const response = await api.get(`/sites/${route.params.id}/preview/runtime`)
        previewRuntime.value = response.data?.runtime || null
        if (syncPreviewUrl && previewRuntime.value?.previewUrl && !site.value?.preview_url) {
          site.value = { ...site.value, preview_url: previewRuntime.value.previewUrl }
          previewReloadKey.value++
        }
      } catch (error) {
        console.error('Failed to load preview runtime status:', error)
      }
    }

    const maybeAutoRefreshPreview = async () => {
      if (autoPreviewRefreshInFlight.value) return
      if (!site.value || site.value.status !== 'creating') return
      if (site.value.preview_url) return

      autoPreviewRefreshInFlight.value = true
      try {
        await refreshPreview({ silent: true })
      } catch (_) {
        // best-effort only
      } finally {
        autoPreviewRefreshInFlight.value = false
      }
    }

    const refreshPreview = async ({ silent = false, _retryCount = 0 } = {}) => {
      if (!silent) {
        refreshingPreview.value = true
      }

      try {
        const result = await siteStore.refreshSitePreview(route.params.id, { silent })
        const updatedSite = result?.site
        if (updatedSite) {
          site.value = updatedSite
        } else if (siteStore.currentSite?.id === route.params.id) {
          site.value = siteStore.currentSite
        }
        if (!result?.restarting) {
          previewReloadKey.value++
        } else if (_retryCount < 8) {
          // Dev server is picking up new files — retry until it's ready (max 32s)
          setTimeout(() => refreshPreview({ silent: true, _retryCount: _retryCount + 1 }), 4000)
        }
      } catch (error) {
        console.error('Failed to refresh preview:', error)
        if (!silent) {
          toast.error('Failed to refresh preview. Please try again.')
        }
      } finally {
        if (!silent) {
          refreshingPreview.value = false
        }
      }
    }

    const handleRefreshPreview = () => refreshPreview({ silent: false })

    const retrySite = async () => {
      if (!site.value?.id) return
      retrying.value = true
      try {
        const response = await api.post(`/sites/${site.value.id}/retry`)
        if (response.data.success) {
          toast.success(`Resuming from "${response.data.resumedFrom}" step`)
          await loadSite()
        } else {
          toast.error(response.data.error || 'Failed to retry')
        }
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to retry site creation')
      } finally {
        retrying.value = false
      }
    }

    const handleSyncGitHub = async () => {
      if (!site.value?.id) return
      syncingGitHub.value = true
      try {
        const response = await api.post(`/sites/${site.value.id}/sync-github`)
        if (response.data.success) {
          toast.success('Connected to GitHub!')
          await loadSite()
        } else {
          toast.error(response.data.message || 'Failed to connect to GitHub')
        }
      } catch (error) {
        const msg = error.response?.data?.message || error.response?.data?.error || 'Failed to connect to GitHub. Check that GITHUB_PERSONAL_ACCESS_TOKEN is configured.'
        toast.error(msg)
      } finally {
        syncingGitHub.value = false
      }
    }

    const handlePublishSandbox = async () => {
      if (!site.value?.id) return
      publishingSandbox.value = true
      try {
        const response = await api.post(`/sites/${site.value.id}/publish/sandbox`)
        if (response.data.success) {
          // Like production, sandbox deploys asynchronously on Cloudflare —
          // the API call only triggers it. Don't claim it's already done.
          toast.success('Publishing to sandbox — your preview will update shortly.')
          await loadSite()
        } else {
          toast.error(response.data.message || 'Failed to publish to sandbox')
        }
      } catch (error) {
        const msg = error.response?.data?.message || 'Failed to publish to sandbox. Please try again.'
        toast.error(msg)
      } finally {
        publishingSandbox.value = false
      }
    }

    const handlePublish = async () => {
      if (!site.value?.id) return

      // Going live updates the public production site — a high-impact, outward
      // action. Confirm first (matching the app's confirm() pattern for other
      // consequential actions like delete/restore), and set expectations that
      // the Cloudflare deploy is asynchronous.
      const confirmed = confirm(
        `Publish “${site.value.name || 'this site'}” to production?\n\n` +
        `This updates your live public website with the current sandbox version. ` +
        `The deploy runs on Cloudflare and can take a few minutes to go live.\n\n` +
        `Tip: review your sandbox first if you haven't already.`
      )
      if (!confirmed) return

      publishing.value = true
      try {
        const response = await api.post(`/sites/${site.value.id}/publish`)

        if (response.data.success) {
          track('site_published', { siteId: site.value.id, siteName: site.value.name })
          // The API only *triggers* the production deploy; Cloudflare finishes
          // building asynchronously. Don't claim it's already live.
          toast.success('Publishing to production — your live site will update in a few minutes.')
          await loadSite()
        } else {
          toast.error(response.data.message || 'Failed to publish website')
        }
      } catch (error) {
        console.error('Failed to publish website:', error)
        const errorMessage = error.response?.data?.message || 'Failed to publish website. Please try again.'
        toast.error(errorMessage)
      } finally {
        publishing.value = false
      }
    }

    const handleAttachmentUpload = (event) => {
      const files = Array.from(event.target.files || [])
      if (!files.length) return

      const validFiles = files.filter((file) => {
        if (file.size > maxAttachmentBytes) {
          return false
        }
        return true
      })

      if (!validFiles.length) {
        event.target.value = ''
        return
      }

      validFiles.forEach((file) => {
        const reader = new FileReader()
        reader.onload = () => {
          updateForm.attachments.push({
            name: file.name,
            type: file.type,
            size: file.size,
            dataUrl: reader.result
          })
        }
        reader.readAsDataURL(file)
      })

      event.target.value = ''
    }

    const triggerAttachmentUpload = () => {
      attachmentInput.value?.click()
    }

    const removeAttachment = (index) => {
      updateForm.attachments.splice(index, 1)
    }

    const formatBytes = (bytes) => {
      if (!bytes) return '0 B'
      const sizes = ['B', 'KB', 'MB', 'GB']
      const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1)
      const value = bytes / Math.pow(1024, index)
      return `${value.toFixed(index === 0 ? 0 : 1)} ${sizes[index]}`
    }

    const applySuggestion = (suggestion) => {
      if (suggestion.isModal) {
        if (suggestion.id === 'add-blog-post') {
          showBlogModal.value = true
        } else if (suggestion.id === 'landing-page') {
          showLandingPageModal.value = true
        } else if (suggestion.id === 'seo-optimization') {
          showSeoModal.value = true
        } else if (suggestion.id === 'search-engine-submission') {
          showSearchEngineModal.value = true
        }
        return
      }

      previousPrompt.value = updateForm.prompt
      updateForm.prompt = suggestion.template
      showPromptUndo.value = true

      activeSheetTab.value = 'update'

      document.getElementById('prompt')?.focus()
    }

    const setActiveSheetTab = (tab) => {
      activeSheetTab.value = tab
      if (editMode.value) {
        editMode.value = false
        clearSelection()
        clearInlineBridgeHandshakeTimer()
        postToIframe({ type: 'bwai:edit-mode', enabled: false })
      }
    }

    const formatRelativeTime = (dateStr) => {
      const diff = Date.now() - new Date(dateStr).getTime()
      const mins = Math.floor(diff / 60000)
      if (mins < 1) return 'just now'
      if (mins < 60) return `${mins}m ago`
      const hrs = Math.floor(mins / 60)
      if (hrs < 24) return `${hrs}h ago`
      const days = Math.floor(hrs / 24)
      if (days < 30) return `${days}d ago`
      return new Date(dateStr).toLocaleDateString()
    }

    const loadSiteCommits = async () => {
      if (!site.value?.id || loadingCommits.value) return
      loadingCommits.value = true
      commitsError.value = ''
      try {
        const res = await api.get(`/sites/${site.value.id}/commits`)
        siteCommits.value = res.data.commits || []
      } catch (err) {
        commitsError.value = err.response?.data?.error || 'Failed to load history'
      } finally {
        loadingCommits.value = false
      }
    }

    const restoreToVersion = async (sha, message) => {
      if (!confirm(`Restore to commit ${sha.slice(0, 7)}?\n\n"${message}"\n\nThis will create a new commit reverting your site to this version.`)) return
      restoringCommit.value = sha
      restoreError.value = ''
      restoreSuccess.value = false
      try {
        await api.post(`/sites/${site.value.id}/commits/${sha}/restore`)
        restoreSuccess.value = true
        await loadSiteCommits()
        setTimeout(() => refreshPreview({ silent: true }), 3000)
        setTimeout(() => { restoreSuccess.value = false }, 6000)
      } catch (err) {
        restoreError.value = err.response?.data?.error || 'Restore failed'
      } finally {
        restoringCommit.value = null
      }
    }

    const openLeftPanel = () => {
      leftPanelOpen.value = true
      if (editMode.value) {
        editMode.value = false
        clearSelection()
        clearInlineBridgeHandshakeTimer()
        postToIframe({ type: 'bwai:edit-mode', enabled: false })
      }
    }

    const undoSuggestion = () => {
      updateForm.prompt = previousPrompt.value
      showPromptUndo.value = false
      document.getElementById('prompt')?.focus()
    }

    const appendSubmittedActivity = (updateRequest) => {
      const updateRequestId = updateRequest?.id
      if (!updateRequestId) return
      siteStore.appendCliOutput(route.params.id, {
        eventType: 'request_submitted',
        stream: 'request_submitted',
        plainMessage: 'Website update queued',
        message: '[request_submitted] Website update queued',
        timestamp: Date.now(),
        progress: 5,
        updateRequestId,
        sessionId: updateRequestId
      })
    }

    const appendLocalActivity = ({ eventType, plainMessage, progress = null, updateRequestId = null, sessionId = null }) => {
      siteStore.appendCliOutput(route.params.id, {
        eventType,
        stream: eventType,
        plainMessage,
        message: `[${eventType}] ${plainMessage}`,
        timestamp: Date.now(),
        progress,
        updateRequestId,
        sessionId: sessionId || updateRequestId
      })
    }

    const CHANGE_REQUEST_PATTERNS = [
      /\b(add|create|build|make|generate|insert|include|put|place)\b.*\b(page|section|button|form|menu|nav|navigation|link|blog|post|content|image|logo|footer|header|hero|pricing|faq|testimonial|color|style|font|text)\b/i,
      /\b(change|update|edit|replace|rename|remove|delete|hide|show|move|reorder|resize|redesign|restyle|publish|index|submit)\b/i,
      /\b(can you|please|pls)\s+(add|create|build|make|change|update|edit|replace|remove|delete|hide|show|move|redesign|restyle)\b/i
    ]

    const ENQUIRY_PATTERNS = [
      /^\s*(what|why|how|where|when|who|which|does|do|did|can|could|should|would|is|are|am)\b/i,
      /\?\s*$/,
      /\b(explain|tell me|check whether|can you check|analyse|analyze|review|what do you think|why is|where is)\b/i
    ]

    const classifyConversationPrompt = (raw) => {
      const text = typeof raw === 'string' ? raw.trim() : ''
      if (!text) return 'empty'
      const looksLikeChange = CHANGE_REQUEST_PATTERNS.some(pattern => pattern.test(text))
      if (looksLikeChange) return 'change'
      const looksLikeEnquiry = ENQUIRY_PATTERNS.some(pattern => pattern.test(text))
      return looksLikeEnquiry ? 'ask' : 'ask'
    }

    const addPendingChangeTurn = (prompt, attachments = []) => {
      const id = `pending-change:${Date.now()}:${Math.random().toString(36).slice(2, 8)}`
      pendingChangeTurns.value = [
        ...pendingChangeTurns.value,
        {
          id,
          mode: 'change',
          prompt,
          status: 'awaiting_user_confirmation',
          requestType: 'pending_confirmation',
          draftCommitSha: null,
          touchedFiles: [],
          touchedSelectors: [],
          editSelectors: [],
          attachments,
          response: {
            kind: 'change',
            summary: '',
            fileChanges: [],
            warnings: []
          },
          createdAt: new Date().toISOString(),
          completedAt: null
        }
      ]
      askHistoryCollapsed.value = false
    }

    const removePendingChangeTurn = (turnId) => {
      pendingChangeTurns.value = pendingChangeTurns.value.filter(turn => turn.id !== turnId)
    }

    const revisePendingChangeTurn = (turn, prompt) => {
      if (!turn?.id || !prompt) return
      pendingChangeTurns.value = pendingChangeTurns.value.map(item =>
        item.id === turn.id
          ? {
              ...item,
              prompt,
              createdAt: new Date().toISOString()
            }
          : item
      )
    }

    const CONFIRM_CHANGE_PATTERNS = [
      /^(yes|yeah|yep|yup|sure|ok|okay|please do|go ahead|proceed|do it|confirm|looks good|sounds good)\b/i,
      /\b(go ahead|proceed|do it|make the change|make this change|yes please)\b/i
    ]

    const CANCEL_CHANGE_PATTERNS = [
      /^(no|nope|nah|cancel|stop|don'?t|do not|never mind|nevermind)\b/i,
      /\b(cancel|stop|don'?t proceed|do not proceed|never mind|nevermind)\b/i
    ]

    const classifyPendingConfirmationReply = (raw) => {
      const text = typeof raw === 'string' ? raw.trim() : ''
      if (!text) return 'empty'
      if (CONFIRM_CHANGE_PATTERNS.some(pattern => pattern.test(text))) return 'confirm'
      if (CANCEL_CHANGE_PATTERNS.some(pattern => pattern.test(text))) return 'cancel'
      return 'revise'
    }

    const REVIEW_REJECT_PATTERNS = [
      /^(no|nope|nah|reject|revert|discard|undo|not ok|not okay|doesn'?t look good|needs changes?)\b/i,
      /\b(revert it|discard it|undo it|reject it)\b/i
    ]

    const REVIEW_REVISION_PATTERNS = [
      /\b(change|update|edit|replace|make|add|remove|move|hide|show|increase|decrease|use|try|adjust|fix)\b/i
    ]

    const classifyReviewReply = (raw) => {
      const text = typeof raw === 'string' ? raw.trim() : ''
      if (!text) return 'empty'
      if (CONFIRM_CHANGE_PATTERNS.some(pattern => pattern.test(text))) return 'approve'
      const explicitReject = REVIEW_REJECT_PATTERNS.some(pattern => pattern.test(text))
      const hasRevisionInstruction = REVIEW_REVISION_PATTERNS.some(pattern => pattern.test(text))
      if (explicitReject && !hasRevisionInstruction) return 'reject'
      return 'revise'
    }

    const submitUpdate = async (override = {}) => {
      const prompt = typeof override.prompt === 'string' ? override.prompt : updateForm.prompt.trim()
      const attachments = Array.isArray(override.attachments) ? override.attachments : updateForm.attachments
      if (!prompt) return
      submitting.value = true
      try {
        leftPanelOpen.value = true
        activeSheetTab.value = 'activity'
        lastSubmittedPrompt.value = prompt
        const response = await siteStore.createUpdateRequest(route.params.id, {
          prompt,
          attachments: attachments.map((attachment) => ({
            name: attachment.name,
            type: attachment.type,
            dataUrl: attachment.dataUrl,
            size: attachment.size
          }))
        })
        appendLocalActivity({
          eventType: 'request_submitted',
          plainMessage: 'Website update queued',
          progress: 5,
          sessionId: response?.updateRequest?.id || `local:${Date.now()}`
        })
        appendSubmittedActivity(response?.updateRequest)
        if (!override.prompt || updateForm.prompt.trim() === prompt) {
          updateForm.prompt = ''
          updateForm.attachments = []
        }
        updates.value = await siteStore.fetchUpdateRequests(route.params.id, { limit: 3 })
        await refreshConversationTurns()
      } catch (error) {
        console.error('Failed to submit update:', error)
      } finally {
        submitting.value = false
      }
    }

    // ── Element-scoped AI edit ────────────────────────────────────────────────
    // "Select any element → describe a change → AI edits just that element."
    // Reuses the standard update→codex→preview pipeline; scoping is achieved by
    // composing a prompt around the element's stable data-bwai-source-id (baked
    // into the source by the inline-source-map Vite plugin), so codex can locate
    // and edit exactly that element without restructuring the page.
    const elementAiPrompt = ref('')
    const elementAiSubmitting = ref(false)

    // Keep the prompt short and instruction-first so, if it ever surfaces in the
    // conversation log, it reads cleanly. The element is located by its stable
    // data-bwai-source-id (baked into the source), so codex doesn't need the
    // full HTML dumped into the prompt.
    const buildElementEditPrompt = (el, instruction) => {
      const src = el?.sourceId
        ? `data-bwai-source-id="${el.sourceId}"`
        : (el?.cssSelector || el?.selector || 'the selected element')
      const tag = el?.tagName || 'element'
      return `${instruction}\n\nScope: change ONLY the <${tag}> at ${src} — locate it in the source, keep that attribute, and don't touch any other element. Stay consistent with the site's existing design.`
    }

    // Element edits are a right-panel interaction — they must NOT hijack the left
    // conversation panel the way a free-text page update does. Dispatch the codex
    // update directly (preview refreshes over SSE) without opening the left panel
    // or appending the verbose prompt to the activity log.
    const submitElementAiEdit = async () => {
      const instruction = (elementAiPrompt.value || '').trim()
      if (!instruction || !selectedElement.value || submitting.value || elementAiSubmitting.value || isLockedByAnotherEditor.value) return
      const composed = buildElementEditPrompt(selectedElement.value, instruction)
      elementAiPrompt.value = ''
      elementAiSubmitting.value = true
      try {
        await siteStore.createUpdateRequest(route.params.id, { prompt: composed, requestType: 'element_ai_edit' }, { quiet: true })
      } catch (error) {
        // store surfaces the toast + lock-conflict handling
        console.error('Element AI edit failed:', error)
      } finally {
        elementAiSubmitting.value = false
      }
    }

    // ── Per-page SEO / AEO / GEO tab ──────────────────────────────────────────
    const seoPages = ref([])
    const seoDefaults = ref({})
    const seoLoading = ref(false)
    const seoError = ref('')
    const selectedSeoPath = ref(null)
    const seoSaving = ref(false)
    const seoSavedPath = ref(null)
    const seoForm = reactive({ title: '', description: '', ogImage: '', canonical: '', keywords: '', index: true, follow: true })

    const loadSeoPages = async () => {
      seoLoading.value = true
      seoError.value = ''
      try {
        const data = await siteStore.fetchPageSeo(route.params.id)
        seoPages.value = data.pages || []
        seoDefaults.value = data.defaults || {}
      } catch (e) {
        seoError.value = e?.response?.data?.error || 'Failed to load SEO'
      } finally {
        seoLoading.value = false
      }
    }

    const openSeoPage = (p) => {
      if (selectedSeoPath.value === p.path) { selectedSeoPath.value = null; return }
      selectedSeoPath.value = p.path
      seoSavedPath.value = null
      // Prefill with the page's saved custom SEO first; where nothing custom is
      // set, fall back to the CURRENT effective values rendered on the live page
      // (then to a suggested keyword list) so users edit real content, not blanks.
      const s = p.seo || {}
      const eff = p.effective || {}
      seoForm.title = s.title || eff.title || ''
      seoForm.description = s.description || eff.description || ''
      seoForm.ogImage = s.ogImage || ''
      seoForm.canonical = s.canonical || ''
      seoForm.keywords = s.keywords || eff.keywords || (p.keywords || []).join(', ')
      seoForm.index = s.robots?.index !== false
      seoForm.follow = s.robots?.follow !== false
    }

    // Traffic-light color for a 0–100 SEO/AEO/GEO score.
    const seoScoreColor = (n) => {
      if (n == null) return 'var(--text-muted)'
      if (n >= 80) return 'var(--success, #22c55e)'
      if (n >= 50) return 'var(--warning, #f59e0b)'
      return 'var(--danger, #ef4444)'
    }

    const saveSeoPage = async () => {
      if (!selectedSeoPath.value || seoSaving.value) return
      const pagePath = selectedSeoPath.value
      const seo = {
        title: (seoForm.title || '').trim(),
        description: (seoForm.description || '').trim(),
        ogImage: (seoForm.ogImage || '').trim(),
        canonical: (seoForm.canonical || '').trim(),
        keywords: (seoForm.keywords || '').trim(),
        robots: { index: seoForm.index, follow: seoForm.follow },
      }
      seoSaving.value = true
      try {
        const res = await siteStore.savePageSeo(route.params.id, pagePath, seo)
        const row = seoPages.value.find((p) => p.path === pagePath)
        if (row) row.seo = res.seo || seo
        seoSavedPath.value = pagePath
        toast.success('SEO saved')
      } catch (e) {
        toast.error(e?.response?.data?.error || 'Failed to save SEO')
      } finally {
        seoSaving.value = false
      }
    }

    const confirmPendingChange = async (turn) => {
      if (!turn || submitting.value) return
      const prompt = turn.prompt || ''
      const attachments = Array.isArray(turn.attachments) ? turn.attachments : []
      removePendingChangeTurn(turn.id)
      track('ai_update_confirmed', { siteId: route.params.id, promptLength: prompt.length })
      await submitUpdate({ prompt, attachments })
    }

    const cancelPendingChange = (turn) => {
      if (!turn) return
      removePendingChangeTurn(turn.id)
      appendLocalActivity({
        eventType: 'request_cancelled',
        plainMessage: 'Change request cancelled before implementation',
        progress: 100,
        sessionId: turn.id
      })
    }

    // Ask-mode client validation. Bounds mirror AskService on the backend so
    // the user gets the same accept/reject decision before any network call
    // (Ask-mode AC008). Returns either an error string or null when valid.
    const ASK_MIN_LENGTH = 1
    const ASK_MAX_LENGTH = 2000
    const validateAskQuestion = (raw) => {
      const text = typeof raw === 'string' ? raw.trim() : ''
      if (text.length === 0) return 'Enter a question before submitting.'
      if (text.length < ASK_MIN_LENGTH) {
        return `Question must be at least ${ASK_MIN_LENGTH} characters. Add more detail.`
      }
      if (text.length > ASK_MAX_LENGTH) {
        return `Question must be ${ASK_MAX_LENGTH} characters or fewer. Shorten your question.`
      }
      return null
    }

    // Computed validation gate for the submit button when Ask mode is active.
    // Change-mode is unaffected — it keeps its existing non-empty rule.
    const askInvalidReason = computed(() => {
      if (classifyConversationPrompt(updateForm.prompt) !== 'ask') return null
      return validateAskQuestion(updateForm.prompt)
    })

    // Map a backend Ask error code to an actionable user-facing message so
    // server-side validation failures (e.g. bypassed client-side) read the
    // same as the client-side errors above (AC008).
    const askErrorCopyForCode = (code) => {
      switch (code) {
        case 'empty_question':
        case 'invalid_question_type':
          return 'Enter a question before submitting.'
        case 'question_too_short':
          return `Question must be at least ${ASK_MIN_LENGTH} characters. Add more detail.`
        case 'question_too_long':
          return `Question must be ${ASK_MAX_LENGTH} characters or fewer. Shorten your question.`
        default:
          return null
      }
    }

    // Normalize transport-level Ask failures (network outage, 5xx) into
    // stable retry-oriented messages (Ask-mode AC009). Returns null for
    // anything that isn't transient — those messages fall through to the
    // server's own copy.
    const askTransientErrorCopy = (error) => {
      if (!error) return null
      const code = error.code || error?.normalizedSource
      if (code === 'ERR_NETWORK' || code === 'NETWORK_ERROR' || code === 'ECONNABORTED') {
        return 'We couldn’t reach the server. Check your connection and retry.'
      }
      const status = error?.response?.status
      if (typeof status === 'number' && status >= 500) {
        return 'The server hit an error answering your question. Try again in a moment.'
      }
      return null
    }

    // Retry is offered for transient failures and validation rejects where
    // the user's question is still loaded in the textarea. The button is
    // hidden while a submit is in-flight to avoid double-fires.
    const canRetryAsk = computed(() => {
      if (!askError.value) return false
      if (classifyConversationPrompt(updateForm.prompt) !== 'ask') return false
      // Require a non-empty question — if the user cleared the textarea we
      // don't show retry because there's nothing to resubmit.
      return typeof updateForm.prompt === 'string' && updateForm.prompt.trim().length > 0
    })

    // Ask-mode submission. Posts to /sites/:id/ask via the store action so
    // the Change-mode update endpoint is never invoked for an Ask (AC002).
    // Successful answers append to askHistory so the editor can display the
    // Q/A pair in-session without a refresh (AC004). Failures preserve the
    // typed question so the user can retry without retyping (AC009).
    const submitAsk = async () => {
      // Local validation runs first — invalid Asks never reach the network
      // (AC008).
      const reason = validateAskQuestion(updateForm.prompt)
      if (reason) {
        askError.value = reason
        return
      }
      const question = updateForm.prompt.trim()
      submitting.value = true
      askError.value = ''
      try {
        const askPayload = { question }
        if (updateForm.attachments.length) {
          askPayload.attachments = updateForm.attachments.map(a => ({ name: a.name, dataUrl: a.dataUrl }))
        }
        const response = await siteStore.submitAskRequest(route.params.id, askPayload)
        const answer = response?.answer
        if (answer && typeof answer.question === 'string' && typeof answer.answer === 'string') {
          const askRequestId = response?.askRequest?.id || answer.generatedAt
          mergeAskHistoryEntries([{
            id: askRequestId,
            question: answer.question,
            answer: answer.answer,
            generatedAt: answer.generatedAt
          }])
          appendLocalActivity({
            eventType: 'question_answered',
            plainMessage: 'Answered a question (no changes made)',
            progress: 100,
            updateRequestId: askRequestId,
            sessionId: askRequestId
          })
          askHistoryCollapsed.value = false
        }
        // Clear the input and attachments only on success — failure path preserves them.
        updateForm.prompt = ''
        updateForm.attachments = []
        await refreshConversationTurns()
      } catch (error) {
        console.error('Failed to submit ask question:', error)
        const payload = error?.response?.data || {}
        const transient = askTransientErrorCopy(error)
        const mapped = askErrorCopyForCode(payload.code || error?.code)
        // Transport-level failures (network / 5xx) take priority over the
        // server's raw error text so the user sees a retry-oriented message
        // (Ask-mode AC009). Validation codes (4xx) take the mapped copy.
        // Everything else falls back to the server message, then the error
        // object's own message.
        askError.value = transient
          || mapped
          || payload.error
          || error?.message
          || 'Could not get an answer. Please try again.'
      } finally {
        submitting.value = false
      }
    }

    // Form submit dispatcher: routes enquiries to Ask, but holds apparent
    // website mutations as local pending confirmations before any files are
    // changed. This keeps the sidebar as an open conversation rather than
    // treating every message as an implementation request.
    const submitAssistant = async () => {
      const clarifyTurn = clarifyingTurn.value
      if (clarifyTurn) {
        await submitClarification(clarifyTurn, { proceedAnyway: false })
        return
      }
      const prompt = updateForm.prompt.trim()
      const reviewTurn = awaitingReviewTurn.value
      if (reviewTurn) {
        const replyType = classifyReviewReply(prompt)
        if (replyType === 'empty') return
        if (replyType === 'approve') {
          await approveTurn(reviewTurn)
        } else if (replyType === 'reject') {
          await rejectTurn(reviewTurn)
        } else {
          await submitReviewRevision(reviewTurn, prompt)
        }
        if (updateForm.prompt.trim() === prompt) updateForm.prompt = ''
        updateForm.attachments = []
        return
      }
      if (!prompt) return
      track('ai_conversation_submitted', { siteId: route.params.id, promptLength: prompt.length })
      await submitUpdate({ prompt, attachments: [...updateForm.attachments] })
    }

    // Send the user's answer (or proceedAnyway) for the open clarification.
    // Mirrors the chat-input flow: clears the prompt on success and lets the
    // SSE stream drive the rest. A null answer with proceedAnyway=true means
    // "go ahead with reasonable defaults".
    const submitClarification = async (turn, { proceedAnyway = false } = {}) => {
      if (!turn || !turn.id || submitting.value) return
      const answer = updateForm.prompt.trim() || null
      if (!proceedAnyway && !answer) return
      submitting.value = true
      try {
        await siteStore.provideClarification(route.params.id, turn.id, {
          answer,
          proceedAnyway
        })
        appendLocalActivity({
          eventType: 'implementation_progress',
          plainMessage: proceedAnyway ? 'Proceeding without further clarification' : 'Clarification received — continuing',
          progress: 30,
          updateRequestId: turn.id
        })
        updateForm.prompt = ''
        updateForm.attachments = []
        await refreshConversationTurns()
      } catch (error) {
        const message = error?.response?.data?.error || error?.message || 'Failed to send clarification'
        toast.error(message)
      } finally {
        submitting.value = false
      }
    }

    const approveFeedback = async (updateId, approved) => {
      try {
        await siteStore.provideFeedback(route.params.id, updateId, {
          approved,
          feedback: approved ? 'Approved' : 'Rejected'
        })
        updates.value = await siteStore.fetchUpdateRequests(route.params.id, { limit: 3 })
      } catch (error) {
        console.error('Failed to provide feedback:', error)
      }
    }

    // Per-turn busy state so the Approve / Reject / Revert buttons can each
    // disable independently while their request is in flight. Keyed by turn
    // id × action so a slow Approve doesn't block a Revert on another turn.
    const turnActionBusy = ref({})
    const isTurnActionBusy = (turnId, action) =>
      !!(turnActionBusy.value[turnId] && turnActionBusy.value[turnId][action])
    const setTurnActionBusy = (turnId, action, value) => {
      const next = { ...turnActionBusy.value }
      next[turnId] = { ...(next[turnId] || {}), [action]: value }
      turnActionBusy.value = next
    }

    // Approve the currently awaiting-publish turn. Pushes the row through
    // the workflow's PublishChange path (provideFeedback approved=true) and
    // appends a local "approved" activity step so the inline feed reflects
    // the change immediately — the SSE-driven "Preview ready — awaiting
    // your approval" step stays in history but is no longer the latest.
    const approveTurn = async (turn) => {
      if (!turn || !turn.id || isTurnActionBusy(turn.id, 'approve')) return
      setTurnActionBusy(turn.id, 'approve', true)
      try {
        await siteStore.provideFeedback(route.params.id, turn.id, {
          approved: true,
          feedback: 'Approved'
        })
        appendLocalActivity({
          eventType: 'published',
          plainMessage: 'Changes published to sandbox',
          progress: 100,
          updateRequestId: turn.id
        })
        await refreshConversationTurns()
      } catch (error) {
        const message = error?.response?.data?.error || error?.message || 'Failed to approve'
        toast.error(message)
      } finally {
        setTurnActionBusy(turn.id, 'approve', false)
      }
    }

    // Reject the currently awaiting-publish turn. Workflow runs RevertChange
    // (git reset --hard baseBranch on the preview branch) so the change is
    // discarded. We append a local "reverted" step so the feed shows the
    // outcome immediately even before SSE emits its own.
    const rejectTurn = async (turn) => {
      if (!turn || !turn.id || isTurnActionBusy(turn.id, 'reject')) return
      setTurnActionBusy(turn.id, 'reject', true)
      try {
        await siteStore.provideFeedback(route.params.id, turn.id, {
          approved: false,
          feedback: 'Rejected'
        })
        appendLocalActivity({
          eventType: 'reverted',
          plainMessage: 'Rejected — changes reverted',
          progress: 100,
          updateRequestId: turn.id
        })
        await refreshConversationTurns()
      } catch (error) {
        const message = error?.response?.data?.error || error?.message || 'Failed to reject'
        toast.error(message)
      } finally {
        setTurnActionBusy(turn.id, 'reject', false)
      }
    }

    const submitReviewRevision = async (turn, feedback) => {
      if (!turn || !feedback) return
      const originalPrompt = turn.prompt || 'the previous website change'
      await rejectTurn(turn)
      const revisionPrompt = [
        'Revise the website update based on the user review.',
        '',
        `Original request: ${originalPrompt}`,
        `User feedback: ${feedback}`,
        '',
        'Apply the revision directly to the website preview using the current site style.'
      ].join('\n')
      await submitUpdate({ prompt: revisionPrompt, attachments: [] })
    }

    // Restore the site to the commit produced by a previous turn. Reuses the
    // existing POST /sites/:id/commits/:sha/restore route (same one used by
    // the History tab). Click on Turn N's "Revert to this version" → site
    // looks like it did right after Turn N completed.
    const revertToTurn = async (turn) => {
      if (!turn || !turn.id || isTurnActionBusy(turn.id, 'revert')) return
      const sha = turn.draftCommitSha
      if (!sha) {
        toast.error('No commit hash recorded for this turn — cannot revert.')
        return
      }
      const promptText = (turn.prompt || '').slice(0, 60)
      if (!confirm(`Restore the site to the state after this turn?\n\n"${promptText}${turn.prompt && turn.prompt.length > 60 ? '…' : ''}"\n\nA new commit will be created on top of history.`)) return
      setTurnActionBusy(turn.id, 'revert', true)
      try {
        await api.post(`/sites/${route.params.id}/commits/${sha}/restore`)
        appendLocalActivity({
          eventType: 'reverted',
          plainMessage: `Reverted to "${promptText || 'earlier'}"`,
          progress: 100,
          updateRequestId: turn.id
        })
        setTimeout(() => refreshPreview({ silent: true }), 2500)
        await refreshConversationTurns()
      } catch (error) {
        const message = error?.response?.data?.error || error?.message || 'Failed to revert'
        toast.error(message)
      } finally {
        setTurnActionBusy(turn.id, 'revert', false)
      }
    }

    const setupSSE = () => {
      sseConnection.value = siteStore.connectSSE(route.params.id, (eventType, data) => {
        console.log('Editor received SSE event:', eventType, data)

        const reloadEvents = [
          'creation_completed', 'creation_failed',
          'implementation_completed', 'preview_ready', 'published', 'reverted',
        ]
        if (reloadEvents.includes(eventType)) {
          loadSite()
          // Refresh the unified conversation log so completed Change turns
          // pick up their summary/touched_files server-side fields.
          refreshConversationTurns()
        }

        if (eventType === 'preview_deploying' || eventType === 'implementation_started') {
          previewStatus.value = 'deploying'
        }

        if (eventType === 'preview_ready') {
          previewStatus.value = 'ready'
          loadPreviewRuntimeStatus({ syncPreviewUrl: true })
          refreshPreview({ silent: true })
          loadPages()
        }

        if (eventType === 'awaiting_creation_feedback') {
          creationFeedbackSubmitted.value = false
          creationFeedbackComment.value = ''
          creationFeedbackError.value = ''
          showCreationFeedbackInput.value = false
        }
        if (eventType === 'creation_feedback_approved' || eventType === 'creation_feedback_rejected') {
          creationFeedbackSubmitted.value = true
          showCreationFeedbackInput.value = false
        }
      })
    }

    const loadPages = async () => {
      try {
        const res = await api.get(`/sites/${route.params.id}/pages`)
        if (res.data?.pages?.length) {
          availablePreviewPages.value = res.data.pages
        }
      } catch (_) {}
    }

    const submitCreationFeedback = async (approved) => {
      if (creationFeedbackSubmitting.value) return
      creationFeedbackSubmitting.value = true
      creationFeedbackError.value = ''
      try {
        await api.post(`/sites/${route.params.id}/creation/feedback`, {
          approved,
          comment: creationFeedbackComment.value.trim()
        })
        track('site_creation_feedback', { siteId: route.params.id, approved, hasComment: !!creationFeedbackComment.value.trim() })
        creationFeedbackSubmitted.value = true
        showCreationFeedbackInput.value = false
      } catch (err) {
        creationFeedbackError.value = err?.response?.data?.message || 'Failed to submit feedback'
      } finally {
        creationFeedbackSubmitting.value = false
      }
    }

    const deletingPage = ref('')

    const slugifyPageTitle = (value) => String(value || '')
      .trim()
      .toLowerCase()
      .replace(/['"]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')

    const resetCreatePageForm = () => {
      createPageForm.title = ''
      createPageForm.slug = ''
      createPageForm.brief = ''
      createPageForm.navPlacement = 'header'
      createPageForm.navAfter = ''
      createPageTouchedSlug.value = false
      createPageError.value = ''
    }

    const openCreatePageModal = () => {
      createPageError.value = ''
      showCreatePageModal.value = true
    }

    const closeCreatePageModal = () => {
      if (creatingPage.value) return
      showCreatePageModal.value = false
      resetCreatePageForm()
    }

    const syncCreatePageSlug = () => {
      if (createPageTouchedSlug.value) return
      createPageForm.slug = slugifyPageTitle(createPageForm.title)
    }

    const buildCreatePagePrompt = ({ title, slug, brief, navPlacement, navAfter }) => {
      const routePath = `/${slug}`
      const navInstruction = navPlacement === 'none'
        ? 'Do not add this page to header or footer navigation.'
        : `Add this page to ${navPlacement === 'both' ? 'both components/AppHeader.vue and components/AppFooter.vue' : `components/App${navPlacement === 'header' ? 'Header' : 'Footer'}.vue`}.`
      const positionInstruction = navAfter
        ? `If updating navigation, place it immediately after the existing "${navAfter}" link when that link exists; otherwise use the best-fit position.`
        : 'If updating navigation, choose the best-fit position based on the existing navigation order.'

      return `Create a new Nuxt page at pages/${slug}.vue for route ${routePath}.

PAGE DETAILS
- Title: ${title}
- Route: ${routePath}
- User brief: ${brief || 'Create a polished page using reasonable content for this title.'}
- Navigation placement: ${navPlacement}
- Navigation position: ${navAfter || 'best fit'}

IMPLEMENTATION REQUIREMENTS
1. Inspect the existing website source before editing, especially pages/*.vue, components/AppHeader.vue, components/AppFooter.vue, layouts/default.vue, assets/css/main.css, and tailwind.config.js.
2. Create pages/${slug}.vue so it matches the current website's visual system exactly: typography, spacing, colors, section treatments, buttons, cards, borders, and responsive behavior.
3. Reuse existing components, CSS variables, utility classes, and layout patterns where possible.
4. Do not introduce a generic template style. The page should feel native to this existing website.
5. ${navInstruction}
6. ${positionInstruction}
7. Do not modify unrelated pages or global styles unless strictly required for this page to render correctly.
8. Keep all changes inside this website repo. Do not install dependencies.
9. Ensure the new route works in Nuxt and the page is mobile responsive.

EXPECTED FILES
- Required: pages/${slug}.vue
- Optional only if navigation placement requires it: components/AppHeader.vue and/or components/AppFooter.vue`
    }

    const deletePage = async (page) => {
      const pageName = page.path.replace(/^\//, '') || 'index'
      if (!confirm(`Delete "${page.label || page.path}"? This cannot be undone.`)) return
      deletingPage.value = page.path
      try {
        await api.delete(`/sites/${route.params.id}/pages/${pageName}`)
        availablePreviewPages.value = availablePreviewPages.value.filter(p => p.path !== page.path)
        if (lastKnownPreviewPath.value === page.path) {
          lastKnownPreviewPath.value = '/'
        }
        // Server restarts after deletion — show deploying state until preview_ready fires
        previewStatus.value = 'deploying'
      } catch (err) {
        toast.error(err?.response?.data?.message || 'Failed to delete page')
      } finally {
        deletingPage.value = ''
      }
    }

    const submitCreatePage = async () => {
      createPageError.value = ''
      const title = createPageForm.title.trim()
      const slug = slugifyPageTitle(createPageForm.slug || title)
      if (!title) {
        createPageError.value = 'Page title is required.'
        return
      }
      if (!slug || !/^[a-z0-9][a-z0-9-]*$/.test(slug)) {
        createPageError.value = 'Use a URL slug like careers or case-studies.'
        return
      }
      if (slug === 'index') {
        createPageError.value = 'Use a non-homepage slug.'
        return
      }
      const newPath = `/${slug}`
      if (availablePreviewPages.value.some(p => p.path === newPath)) {
        createPageError.value = `A page already exists at ${newPath}.`
        return
      }

      creatingPage.value = true
      try {
        leftPanelOpen.value = true
        activeSheetTab.value = 'activity'
        previewStatus.value = 'deploying'
        const response = await siteStore.createUpdateRequest(route.params.id, {
          prompt: buildCreatePagePrompt({
            title,
            slug,
            brief: createPageForm.brief.trim(),
            navPlacement: createPageForm.navPlacement,
            navAfter: createPageForm.navAfter
          }),
          requestType: 'page_creation'
        })
        appendSubmittedActivity(response?.updateRequest)
        toast.success(`Creating ${newPath}`)
        showCreatePageModal.value = false
        resetCreatePageForm()
        updates.value = await siteStore.fetchUpdateRequests(route.params.id, { limit: 3 })
      } catch (err) {
        createPageError.value = err?.response?.data?.error || err?.message || 'Failed to create page.'
        toast.error(createPageError.value)
      } finally {
        creatingPage.value = false
      }
    }

    const hydrateActivity = async () => {
      siteStore.loadPersistedCliOutputs(route.params.id)
      try {
        await siteStore.fetchSiteActivity(route.params.id, { limit: 100 })
        const askUpdates = await siteStore.fetchUpdateRequests(route.params.id, {
          limit: 10,
          status: 'answered',
          requestType: 'question'
        })
        mergeAskHistoryEntries((askUpdates || []).map(makeAskHistoryEntry).reverse())
      } catch (error) {
        console.warn('Unable to hydrate persisted site activity:', error)
      }
      // Hydrate the unified conversation log alongside the legacy Ask-history
      // fetch. Runs independently so a failure here doesn't abort the activity
      // hydration above.
      await refreshConversationTurns()
    }

    const handleDocClickForDropdowns = (e) => {
      if (!e.target.closest('.custom-select-wrap')) closeAllDropdowns()
    }

    onMounted(() => {
      loadConversationResetAt()
      loadSite()
      hydrateActivity()
      setupSSE()
      loadPages()
      previewRuntimePoller.value = setInterval(() => {
        loadPreviewRuntimeStatus({ syncPreviewUrl: true })
        maybeAutoRefreshPreview()
      }, 10000)
      window.addEventListener('message', handleInlineEditMessage)
      document.addEventListener('click', handleDocClickForDropdowns)
      // Track canvas-frame width so the preview can auto-fit-scale instead of
      // reflowing when the sidebar resizes. ResizeObserver is the cheapest way
      // to react to sidebar toggles + window resizes uniformly.
      nextTick(() => {
        if (canvasFrameEl.value && typeof ResizeObserver !== 'undefined') {
          canvasFrameResizeObserver = new ResizeObserver((entries) => {
            for (const entry of entries) {
              canvasFrameWidth.value = entry.contentRect.width || 1280
              canvasFrameHeight.value = entry.contentRect.height || 800
            }
          })
          canvasFrameResizeObserver.observe(canvasFrameEl.value)
        }
      })
      // Pre-fill prompt from query param (e.g. "Fix in editor" from SEO guidance)
      if (route.query.prompt) {
        updateForm.prompt = String(route.query.prompt)
        if (route.query.mode === 'change') setActiveSheetTab('update')
        nextTick(() => { promptInputEl.value?.focus() })
      }
    })

    onUnmounted(() => {
      siteStore.disconnectSSE()
      if (previewRuntimePoller.value) {
        clearInterval(previewRuntimePoller.value)
      }
      window.removeEventListener('message', handleInlineEditMessage)
      document.removeEventListener('click', handleDocClickForDropdowns)
      if (inlineBridgeHandshakeTimer.value) {
        clearTimeout(inlineBridgeHandshakeTimer.value)
        inlineBridgeHandshakeTimer.value = null
      }
      if (canvasFrameResizeObserver) {
        canvasFrameResizeObserver.disconnect()
        canvasFrameResizeObserver = null
      }
    })

    const cliOutput = computed(() => (siteStore.cliOutputs[route.params.id] || []))
    const recentUpdates = computed(() => updates.value.slice(0, 3))
    const isPreviewDeploying = computed(() => previewStatus.value === 'deploying')

    const EVENT_MESSAGES = {
      creation_started: 'Starting your site setup',
      template_clone_started: 'Cloning the template',
      template_clone_completed: 'Template ready',
      style_application_started: 'Applying the design',
      style_global_started: 'Applying global design tokens',
      style_global_completed: 'Global design applied',
      style_application_completed: 'Design applied',
      style_eval_started: 'Evaluating style match',
      style_eval_completed: 'Style evaluated',
      content_generation_started: 'Generating content',
      content_generation_completed: 'Content generated',
      content_eval_started: 'Evaluating content quality',
      content_eval_completed: 'Content evaluated',
      component_generation_started: 'Analysing and generating components',
      component_generation_completed: 'Components generated',
      html_import_conversion_started: 'Converting the imported HTML',
      html_import_conversion_completed: 'Imported HTML converted',
      clone_capture_started: 'Capturing site pages',
      clone_capture_completed: 'Pages captured',
      clone_conversion_started: 'Converting to Nuxt3',
      clone_conversion_completed: 'Conversion complete',
      repo_import_started: 'Importing repository',
      repo_import_completed: 'Repository imported',
      inspiration_analysis_started: 'Analysing inspiration',
      inspiration_analysis_completed: 'Analysis complete',
      brand_extraction_started: 'Extracting brand data',
      brand_extraction_completed: 'Brand data ready',
      template_created: 'Template created',
      preview_warming: 'Starting preview runtime',
      creation_completed: 'Your site is ready!',
      request_submitted: 'Website update queued',
      classification_completed: 'Request classified as simple website update',
      // Ask mode events — question-answer language only, never implementation
      // verbs (Ask-mode AC006/AC011).
      question_answered: 'Answered a question (no changes made)',
      implementation_started: 'Applying your changes',
      implementation_completed: 'Changes applied',
      ai_task_started: 'Planning the implementation',
      ai_task_completed: 'Implementation plan ready',
      cli_execution_started: 'Running build tools',
      cli_execution_progress: 'Build tools are running',
      preview_deploying: 'Deploying your preview',
      preview_ready: 'Preview is ready',
      awaiting_feedback: 'Preview ready - awaiting your approval to publish',
      awaiting_clarification: 'Waiting for your clarification before proceeding',
      awaiting_creation_feedback: 'Preview ready — does this look good?',
      creation_feedback_approved: 'Great! Finalizing your site...',
      creation_feedback_rejected: 'Applying improvements...',
      sandbox_published: 'Sandbox published',
      published: 'Site published',
      reverted: 'Changes reverted',
    }

    const ERROR_MESSAGES = {
      creation_failed: 'There was a problem creating your site. Progress is blocked until the issue is resolved.',
      html_import_conversion_failed: 'HTML import conversion failed. Progress is blocked until the issue is resolved.',
      ai_task_failed: 'The implementation plan failed. Progress is blocked until the issue is resolved.',
      implementation_failed: 'The change implementation failed. Progress is blocked until the issue is resolved.',
      cli_execution_error: 'A build error occurred. Progress is blocked until the issue is resolved.',
      preview_failed: 'Preview deployment failed. Progress is blocked until the issue is resolved.',
      preview_restart_failed: 'Preview restart failed. Progress is blocked until the issue is resolved.',
      publish_failed: 'Publishing failed. Progress is blocked until the issue is resolved.',
      awaiting_upgrade: 'This request needs a more advanced workflow before it can continue.',
      error: 'Something went wrong. Progress is blocked until the issue is resolved.',
      destructive_update_blocked:
        'The update tried to delete tracked files and was blocked for safety. Restore the deleted intent, '
        + 'then re-submit a non-destructive update (e.g., edit content in place rather than removing files).',
      update_failed: 'The update failed. Review the activity log, adjust the prompt, and try again.',
    }

    const EVENT_PROGRESS = {
      creation_started: 5,
      template_clone_started: 10,
      template_clone_completed: 15,
      repo_import_started: 10,
      repo_import_completed: 25,
      html_import_conversion_started: 25,
      html_import_conversion_completed: 45,
      clone_capture_started: 40,
      clone_capture_completed: 55,
      clone_conversion_started: 60,
      clone_conversion_completed: 80,
      inspiration_analysis_started: 20,
      inspiration_analysis_completed: 30,
      brand_extraction_started: 30,
      brand_extraction_completed: 40,
      style_application_started: 35,
      style_global_started: 40,
      style_global_completed: 45,
      style_page_started: 50,
      style_page_completed: 60,
      style_application_completed: 65,
      style_eval_started: 68,
      style_eval_completed: 72,
      component_generation_started: 70,
      component_generation_completed: 82,
      content_generation_started: 75,
      content_generation_completed: 85,
      content_eval_started: 88,
      content_eval_completed: 92,
      awaiting_creation_feedback: 90,
      creation_feedback_approved: 92,
      creation_feedback_rejected: 50,
      template_created: 92,
      preview_warming: 94,
      preview_deploying: 96,
      preview_ready: 100,
      creation_completed: 100,
      request_submitted: 5,
      classification_completed: 8,
      implementation_started: 10,
      implementation_progress: 50,
      ai_task_started: 25,
      ai_task_completed: 55,
      cli_execution_started: 65,
      cli_execution_progress: 75,
      implementation_completed: 85,
      awaiting_feedback: 100,
      sandbox_published: 100,
      published: 100,
      reverted: 100,
      question_answered: 100,
    }

    const SKIP_EVENTS = new Set(['connected', 'cagent_implementation_output'])
    const RUN_START_EVENTS = new Set(['request_submitted', 'creation_started', 'implementation_started'])
    const REPEATABLE_EVENTS = new Set(['style_page_started', 'style_page_completed', 'implementation_progress'])
    const ERROR_EVENT_SET = new Set(Object.keys(ERROR_MESSAGES))
    const COMPLETE_EVENTS = new Set(['creation_completed', 'preview_ready', 'awaiting_feedback', 'sandbox_published', 'published', 'reverted', 'question_answered'])

    const getActivityTimestamp = (entry) => {
      const value = new Date(entry?.timestamp || 0).getTime()
      return Number.isFinite(value) ? value : 0
    }

    const getActivityRunKey = (entry) => entry?.updateRequestId || entry?.sessionId || null

    const activeActivityOutput = computed(() => {
      const output = [...cliOutput.value].sort((a, b) => getActivityTimestamp(a) - getActivityTimestamp(b))
      if (output.length === 0) return []

      let latestStart = null
      for (let index = output.length - 1; index >= 0; index -= 1) {
        const eventType = output[index].eventType || output[index].stream
        if (RUN_START_EVENTS.has(eventType)) {
          latestStart = { entry: output[index], index }
          break
        }
      }

      if (!latestStart) return output.slice(-20)

      const runKey = getActivityRunKey(latestStart.entry)
      if (runKey) {
        return output.filter((entry) => getActivityRunKey(entry) === runKey)
      }
      return output.slice(latestStart.index)
    })

    const activitySteps = computed(() => {
      const output = activeActivityOutput.value
      const seen = new Set()
      const steps = []
      for (const entry of output) {
        const evType = entry.eventType || entry.stream
        if (!evType || SKIP_EVENTS.has(evType)) continue
        if (!REPEATABLE_EVENTS.has(evType)) {
          const dedupeKey = `${evType}:${entry.updateRequestId || entry.sessionId || ''}`
          if (seen.has(dedupeKey)) continue
          seen.add(dedupeKey)
        }
        const message = ERROR_MESSAGES[evType]
          || (evType === 'implementation_progress' ? entry.plainMessage : null)
          || EVENT_MESSAGES[evType]
          || entry.plainMessage
          || 'Working on your site'
        // Preserve updateRequestId on each step so we can render in-flight
        // activity inline under its owning conversation turn (Change-mode)
        // and keep the standalone Live updates section scoped to events
        // that don't belong to any turn (creation flow).
        steps.push({
          eventType: evType,
          message,
          timestamp: entry.timestamp,
          updateRequestId: entry.updateRequestId || null,
          question: entry.question || null
        })
      }
      return steps
    })

    const refreshedConversationEventKeys = ref(new Set())
    watch(cliOutput, (entries) => {
      const terminalEvents = new Set(['question_answered', 'awaiting_clarification', 'awaiting_feedback', 'published', 'reverted', 'implementation_failed'])
      const nextSeen = new Set(refreshedConversationEventKeys.value)
      let shouldRefresh = false
      for (const entry of entries || []) {
        const eventType = entry.eventType || entry.stream
        if (!terminalEvents.has(eventType)) continue
        const key = `${eventType}:${entry.updateRequestId || entry.sessionId || entry.timestamp || ''}`
        if (nextSeen.has(key)) continue
        nextSeen.add(key)
        shouldRefresh = true
      }
      if (!shouldRefresh) return
      refreshedConversationEventKeys.value = nextSeen
      refreshConversationTurns().catch((error) => {
        console.warn('Failed to refresh conversation after terminal event:', error)
      })
    }, { deep: true })

    // Build a map of turn id → activity steps belonging to that turn. Used by
    // the conversation log to render the in-flight feed inline under the
    // active Change turn instead of in a sibling Live updates panel.
    const activityStepsByTurn = computed(() => {
      const map = {}
      for (const step of activitySteps.value) {
        const tid = step.updateRequestId
        if (!tid) continue
        if (!map[tid]) map[tid] = []
        map[tid].push(step)
      }
      return map
    })

    const activityStepsForTurn = (turnId) => activityStepsByTurn.value[turnId] || []

    // Auto-open-diff is intentionally disabled. Earlier iterations tried to
    // pop the diff open the moment a Change turn's preview was ready, but
    // repeated user reports show the surprise-reveal is more annoying than
    // helpful — and it kept resurfacing across edge cases (Approve, reload,
    // re-runs). The user clicks the per-turn "Show diff" button when they
    // want to see the patch; we don't open it for them.

    // In-turn activity feed is collapsed by default — only the latest step
    // is visible. Clicking the "Show all N steps" toggle expands the full
    // chronological list for that turn; "Collapse" hides it again. Per-turn
    // state so expanding one turn doesn't expand its neighbors.
    const expandedActivityTurnIds = ref(new Set())
    const isTurnActivityExpanded = (turnId) => expandedActivityTurnIds.value.has(turnId)
    const toggleTurnActivity = (turnId) => {
      if (!turnId) return
      const next = new Set(expandedActivityTurnIds.value)
      if (next.has(turnId)) next.delete(turnId)
      else next.add(turnId)
      expandedActivityTurnIds.value = next
    }
    const visibleActivityStepsForTurn = (turnId) => {
      const all = activityStepsForTurn(turnId)
      if (all.length <= 1 || isTurnActivityExpanded(turnId)) return all
      return all.slice(-1)
    }

    // A Change turn is "awaiting publish" when the workflow has fired the
    // awaiting_feedback event — the row status itself stays at 'implementing'
    // until the user pushes Approve (or Reject), so we read the activity
    // stream rather than the row status. Belt-and-braces: also surface the
    // button if the row IS at preview_ready (some flows transition there).
    //
    // Once a terminal action lands (a local "published" or "reverted" step
    // appended by approveTurn / rejectTurn, OR the row reaches published/
    // reverted via SSE), the buttons go away — the decision is made.
    const TERMINAL_ACTION_EVENTS = new Set(['published', 'reverted'])
    // Approve / Reject only surface when the workflow has reached AwaitFeedback
    // — signaled exclusively by the awaiting_feedback SSE event. We do NOT
    // trigger on preview_ready or on the row's preview_ready status because
    // those can fire MID-FLIGHT (the blog-post path publishes a preview_ready
    // for the placeholder stub before the real content lands, and the row
    // can transition early too). Approving against that early signal would
    // freeze a half-finished change. awaiting_feedback is the only event the
    // workflow emits at the very end, so it's the safe gate.
    const isTurnAwaitingPublish = (turn) => {
      if (!turn || turn.mode !== 'change') return false
      if (turn.status === 'published' || turn.status === 'reverted') return false
      const steps = activityStepsForTurn(turn.id)
      let seenAwaitingFeedback = false
      for (const step of steps) {
        if (TERMINAL_ACTION_EVENTS.has(step.eventType)) return false
        if (step.eventType === 'awaiting_feedback') seenAwaitingFeedback = true
      }
      return seenAwaitingFeedback
    }

    // Clarification gate: a Change turn paused with status 'awaiting_clarification'
    // is "open" — the next chat submit goes to /clarify (not createUpdateRequest)
    // and the conversation renders the question + a "Just proceed" button.
    // Once the user answers or chooses to proceed, the turn transitions back
    // to 'implementing' and this returns false again.
    const isTurnAwaitingClarification = (turn) => {
      if (!turn || turn.mode !== 'change') return false
      if (isTurnAwaitingPublish(turn)) return false
      const hasQuestion = !!clarificationQuestionForTurn(turn.id)
      if (!hasQuestion) return false
      // Row-status check first — backend flips it on the awaiting_clarification
      // event. SSE may lag, so also check for the event in this turn's stream.
      if (turn.status === 'awaiting_clarification') return true
      const steps = activityStepsForTurn(turn.id)
      let saw = false
      for (const step of steps) {
        if (step.eventType === 'awaiting_clarification') saw = true
        // Any later step that signals progression resolves the gate.
        if (saw && (
          step.eventType === 'implementation_progress' ||
          step.eventType === 'ai_task_started' ||
          step.eventType === 'preview_ready' ||
          step.eventType === 'awaiting_feedback' ||
          step.eventType === 'published' ||
          step.eventType === 'reverted'
        )) return false
      }
      return saw
    }

    const clarifyingTurn = computed(() => {
      // Most-recent Change turn that's awaiting clarification. Only one can
      // be open at a time — the chat input routes its next submit here.
      for (let i = conversationTurns.value.length - 1; i >= 0; i--) {
        const t = conversationTurns.value[i]
        if (isTurnAwaitingClarification(t)) return t
      }
      return null
    })

    const clarificationQuestionForTurn = (turnId) => {
      const turn = conversationTurns.value.find(t => t.id === turnId)
      const cleanQuestion = (value) => {
        if (typeof value !== 'string') return null
        const cleaned = value
          .trim()
          .replace(/["'`*]+$/g, '')
          .replace(/[}\]]+$/g, '')
          .replace(/["'`*]+$/g, '')
          .trim()
        return cleaned || null
      }
      const steps = activityStepsForTurn(turnId)
      for (let i = steps.length - 1; i >= 0; i--) {
        if (steps[i].eventType === 'awaiting_clarification') {
          const question = steps[i].question || steps[i].data?.question || null
          const cleaned = cleanQuestion(question)
          if (cleaned) return cleaned
        }
      }
      const persisted = turn?.clarificationQuestion
      return cleanQuestion(persisted)
    }

    // Activity steps that DON'T belong to any conversation turn — used by the
    // (now creation-only) Live updates panel. Site creation events don't carry
    // an updateRequestId because they're tracked by a creation session id,
    // not an update row.
    const orphanActivitySteps = computed(() =>
      activitySteps.value.filter(step => !step.updateRequestId)
    )

    // Live updates section is scoped to the site-creation lifecycle. Once the
    // site reaches `active`, the section disappears regardless of stale orphan
    // events still lingering in cliOutput — Change-mode activity renders inline
    // under its turn now, so the section has no purpose post-creation.
    const liveUpdatesVisible = computed(() => {
      const status = site.value?.status
      return status === 'creating' || status === 'failed' || !status
    })

    const latestStep = computed(() => {
      const steps = activitySteps.value
      return steps.length > 0 ? steps[steps.length - 1] : null
    })

    const latestSummary = computed(() => {
      if (!latestStep.value) {
        return site.value?.status === 'creating'
          ? 'Getting ready to build your site…'
          : 'Waiting to start work…'
      }
      return latestStep.value.message
    })

    const isActivityError = computed(() => latestStep.value && ERROR_EVENT_SET.has(latestStep.value.eventType))
    const isActivityComplete = computed(() => latestStep.value && COMPLETE_EVENTS.has(latestStep.value.eventType))

    const getNumericProgress = (entry) => {
      const value = entry?.progress ?? entry?.percent ?? entry?.percentage
      const numeric = Number(value)
      return Number.isFinite(numeric) ? Math.max(0, Math.min(100, Math.round(numeric))) : null
    }

    const activityProgress = computed(() => {
      if (isActivityComplete.value) return 100

      const explicit = activeActivityOutput.value
        .map(getNumericProgress)
        .filter((value) => value !== null)
      if (explicit.length > 0) {
        return explicit[explicit.length - 1]
      }

      const inferred = activitySteps.value.reduce((max, step) => {
        const progress = EVENT_PROGRESS[step.eventType]
        return Number.isFinite(progress) ? Math.max(max, progress) : max
      }, site.value?.status === 'creating' ? 5 : 0)

      if (isActivityError.value) return Math.max(inferred, 1)
      if (activitySteps.value.length > 0) return Math.min(Math.max(inferred, 5), 99)
      return 0
    })

    const activityProgressLabel = computed(() => {
      if (isActivityError.value) return 'Blocked'
      if (isActivityComplete.value) return 'Complete'
      return 'In progress'
    })

    // Auto-expand the answer panel when activity starts streaming
    watch(() => activitySteps.value.length, (len, prev) => {
      if (len > 0 && prev === 0) askHistoryCollapsed.value = false
    })

    const formatCliTimestamp = (value) => {
      const date = new Date(value)
      return format(date, 'HH:mm:ss')
    }

    const handleBlogCreate = async (data) => {
      try {
        leftPanelOpen.value = true
        activeSheetTab.value = 'activity'
        const response = await siteStore.createUpdateRequest(route.params.id, {
          prompt: data.prompt,
          attachments: []
        })
        appendSubmittedActivity(response?.updateRequest)

        showBlogModal.value = false
        updates.value = await siteStore.fetchUpdateRequests(route.params.id, { limit: 3 })
      } catch (error) {
        console.error('Failed to create blog post:', error)
      }
    }

    const handleSeoOptimization = async (data) => {
      try {
        leftPanelOpen.value = true
        activeSheetTab.value = 'activity'
        const response = await siteStore.createUpdateRequest(route.params.id, {
          prompt: data.prompt,
          attachments: [],
          aeoGuidance: data.aeoGuidance || '',
          geoGuidance: data.geoGuidance || ''
        })
        appendSubmittedActivity(response?.updateRequest)

        showSeoModal.value = false
        updates.value = await siteStore.fetchUpdateRequests(route.params.id, { limit: 3 })
      } catch (error) {
        console.error('Failed to create SEO optimization:', error)
      }
    }

    const handleLandingPageCreate = async (data) => {
      try {
        leftPanelOpen.value = true
        activeSheetTab.value = 'activity'
        const response = await siteStore.createUpdateRequest(route.params.id, {
          prompt: data.prompt,
          attachments: [],
          requestType: 'landing_page_creation'
        })
        appendSubmittedActivity(response?.updateRequest)
        showLandingPageModal.value = false
        updates.value = await siteStore.fetchUpdateRequests(route.params.id, { limit: 10 })
      } catch (error) {
        const msg = error.response?.data?.error || error.message || 'Failed to create landing page'
        if (typeof data.onError === 'function') data.onError(msg)
        else toast.error(msg)
      }
    }

    const handleSearchEngineSubmission = async (data) => {
      try {
        leftPanelOpen.value = true
        activeSheetTab.value = 'activity'
        const response = await siteStore.createUpdateRequest(route.params.id, {
          prompt: data.prompt,
          attachments: []
        })
        appendSubmittedActivity(response?.updateRequest)

        showSearchEngineModal.value = false
        updates.value = await siteStore.fetchUpdateRequests(route.params.id, { limit: 3 })
      } catch (error) {
        console.error('Failed to submit to search engines:', error)
      }
    }

    // ─── Inline visual editor ────────────────────────────────────────────────
    // Posts a structured message to the preview iframe. Uses '*' for
    // targetOrigin because the preview is served from a different origin
    // (Cloudflare Pages / preview runtime) and we only send non-sensitive
    // UI commands.
    const postToIframe = (msg) => {
      const iframe = previewIframeEl.value
      if (!iframe || !iframe.contentWindow) return
      try {
        iframe.contentWindow.postMessage({ source: 'bwai-editor', ...msg }, '*')
      } catch (err) {
        console.warn('postToIframe failed:', err)
      }
    }

    const goBack = () => {
      if (!canGoBack.value) return
      isNavigatingHistory.value = true
      previewHistoryIndex.value--
      postToIframe({ type: 'bwai:navigate-back' })
    }

    const goForward = () => {
      if (!canGoForward.value) return
      isNavigatingHistory.value = true
      previewHistoryIndex.value++
      postToIframe({ type: 'bwai:navigate-forward' })
    }

    const sanitizeRichTextHtml = (html = '') => {
      const template = document.createElement('template')
      template.innerHTML = html
      const allowed = new Set(['STRONG', 'B', 'EM', 'I', 'SPAN', 'BR', 'U', 'MARK'])
      const walk = (node) => {
        for (const child of Array.from(node.childNodes)) {
          if (child.nodeType === Node.ELEMENT_NODE) {
            if (!allowed.has(child.tagName)) {
              child.replaceWith(...Array.from(child.childNodes))
              continue
            }
            for (const attr of Array.from(child.attributes)) {
              if (attr.name !== 'style') child.removeAttribute(attr.name)
            }
            const color = child.style.color
            const backgroundColor = child.style.backgroundColor
            const fontWeight = child.style.fontWeight
            const fontStyle = child.style.fontStyle
            const textDecoration = child.style.textDecoration
            child.removeAttribute('style')
            if (color) child.style.color = color
            if (backgroundColor) child.style.backgroundColor = backgroundColor
            if (fontWeight) child.style.fontWeight = fontWeight
            if (fontStyle) child.style.fontStyle = fontStyle
            if (textDecoration) child.style.textDecoration = textDecoration
          }
          walk(child)
        }
      }
      walk(template.content)
      return template.innerHTML
    }

    const normalizeColorInput = (value) => {
      if (!value || typeof value !== 'string') return '#111827'
      if (/^#[0-9a-fA-F]{6}$/.test(value)) return value
      const match = value.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/i)
      if (!match) return '#111827'
      return `#${[match[1], match[2], match[3]]
        .map((part) => Math.max(0, Math.min(255, Number(part))).toString(16).padStart(2, '0'))
        .join('')}`
    }

    // Robust hex normalizer: uses browser's own color parser so oklch, hsl,
    // named colors, and rgb() all resolve correctly. Returns null on failure so
    // callers can fall back to lastCustomColor instead of clamping to black.
    const normalizeToHex = (color) => {
      if (!color || typeof color !== 'string') return null
      if (/^#[0-9a-fA-F]{6}$/.test(color)) return color.toLowerCase()
      try {
        const el = document.createElement('span')
        el.style.color = color
        if (!el.style.color) return null // browser rejected as invalid
        document.body.appendChild(el)
        const rgb = window.getComputedStyle(el).color
        document.body.removeChild(el)
        const m = rgb.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
        if (!m) return null
        return '#' + [m[1], m[2], m[3]].map(x => Number(x).toString(16).padStart(2, '0')).join('')
      } catch {
        return null
      }
    }

    const normalizePaletteColors = (colors) => {
      const seen = new Set()
      return colors
        .map((color) => normalizeToHex(color))
        .filter((color) => {
          if (!color || seen.has(color)) return false
          seen.add(color)
          return true
        })
    }

    const syncRichEditor = async (html) => {
      await nextTick()
      if (!inlineEditorEl.value) return
      inlineEditorEl.value.innerHTML = sanitizeRichTextHtml(html || '')
      pendingHtml.value = inlineEditorEl.value.innerHTML
      pendingText.value = inlineEditorEl.value.textContent || ''
    }

    const handleRichTextInput = () => {
      if (!inlineEditorEl.value) return
      pendingHtml.value = sanitizeRichTextHtml(inlineEditorEl.value.innerHTML)
      if (pendingHtml.value !== inlineEditorEl.value.innerHTML) {
        inlineEditorEl.value.innerHTML = pendingHtml.value
      }
      pendingText.value = inlineEditorEl.value.textContent || ''
      livePreviewStyles()
    }

    const getTextOffset = (root, targetNode, targetOffset) => {
      if (targetNode?.nodeType === Node.ELEMENT_NODE) {
        let offsetBeforeElement = 0
        const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
        let node = walker.nextNode()
        while (node) {
          if (targetNode.contains(node)) break
          offsetBeforeElement += node.textContent?.length || 0
          node = walker.nextNode()
        }

        let offsetInsideElement = 0
        const children = Array.from(targetNode.childNodes || [])
        for (const child of children.slice(0, targetOffset)) {
          offsetInsideElement += child.textContent?.length || 0
        }
        return offsetBeforeElement + offsetInsideElement
      }
      let offset = 0
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
      let node = walker.nextNode()
      while (node) {
        if (node === targetNode) {
          return offset + targetOffset
        }
        offset += node.textContent?.length || 0
        node = walker.nextNode()
      }
      if (targetNode === root) return Math.min(targetOffset, offset)
      return offset
    }

    const createRangeFromTextOffsets = (root, start, end) => {
      const textLength = root.textContent?.length || 0
      const safeStart = Math.max(0, Math.min(start, textLength))
      const safeEnd = Math.max(safeStart, Math.min(end, textLength))
      const range = document.createRange()
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT)
      let node = walker.nextNode()
      let offset = 0
      let startSet = false

      while (node) {
        const nodeLength = node.textContent?.length || 0
        const nextOffset = offset + nodeLength
        if (!startSet && safeStart <= nextOffset) {
          range.setStart(node, Math.max(0, safeStart - offset))
          startSet = true
        }
        if (startSet && safeEnd <= nextOffset) {
          range.setEnd(node, Math.max(0, safeEnd - offset))
          return range
        }
        offset = nextOffset
        node = walker.nextNode()
      }

      range.selectNodeContents(root)
      range.collapse(false)
      return range
    }

    const saveRichTextSelection = () => {
      const selection = window.getSelection()
      if (!selection || selection.rangeCount === 0 || !inlineEditorEl.value) return
      const range = selection.getRangeAt(0)
      if (!inlineEditorEl.value.contains(range.commonAncestorContainer)) return
      savedRichTextSelection.value = {
        start: getTextOffset(inlineEditorEl.value, range.startContainer, range.startOffset),
        end: getTextOffset(inlineEditorEl.value, range.endContainer, range.endOffset)
      }
    }

    const restoreRichTextSelection = () => {
      const savedSelection = savedRichTextSelection.value
      if (!savedSelection || !inlineEditorEl.value) return false
      const selection = window.getSelection()
      if (!selection) return false
      const range = createRangeFromTextOffsets(inlineEditorEl.value, savedSelection.start, savedSelection.end)
      inlineEditorEl.value.focus()
      selection.removeAllRanges()
      selection.addRange(range)
      return true
    }

    const formatRichText = (command) => {
      restoreRichTextSelection() || inlineEditorEl.value?.focus()
      document.execCommand(command, false, null)
      handleRichTextInput()
      saveRichTextSelection()
    }

    const unwrapEmptySpans = (root) => {
      for (const span of Array.from(root.querySelectorAll('span'))) {
        if (!span.textContent) span.remove()
      }
    }

    const stripColorStyles = (root) => {
      const elements = root.nodeType === Node.ELEMENT_NODE ? [root, ...Array.from(root.querySelectorAll('[style]'))] : Array.from(root.querySelectorAll?.('[style]') || [])
      for (const el of elements) {
        if (!el.style?.color) continue
        el.style.removeProperty('color')
        if (!el.getAttribute('style')?.trim()) {
          el.removeAttribute('style')
        }
      }
    }

    // Applies COLOR to the saved text selection inside the rich-text editor.
    // Selection is restored from character offsets, not a live DOM Range, so it
    // survives sanitization and previously inserted formatting spans.
    const applyColorToSelection = (color) => {
      if (!inlineEditorEl.value || !color) return
      let savedSelection = savedRichTextSelection.value
      if (!savedSelection) {
        saveRichTextSelection()
        savedSelection = savedRichTextSelection.value
      }
      if (!savedSelection || savedSelection.start === savedSelection.end) {
        inlineEditorEl.value.focus()
        return
      }

      const range = createRangeFromTextOffsets(inlineEditorEl.value, savedSelection.start, savedSelection.end)
      if (range.collapsed) return

      const span = document.createElement('span')
      span.style.color = color
      const contents = range.extractContents()
      stripColorStyles(contents)
      span.appendChild(contents)
      range.insertNode(span)
      unwrapEmptySpans(inlineEditorEl.value)

      const sanitizedHtml = sanitizeRichTextHtml(inlineEditorEl.value.innerHTML)
      if (sanitizedHtml !== inlineEditorEl.value.innerHTML) {
        inlineEditorEl.value.innerHTML = sanitizedHtml
      }
      pendingHtml.value = inlineEditorEl.value.innerHTML
      pendingText.value = inlineEditorEl.value.textContent || ''
      savedRichTextSelection.value = { ...savedSelection }
      restoreRichTextSelection()
      livePreviewStyles()
    }

    const applyTextColor = (color, keepPickerOpen = false) => {
      if (!color) return
      const hex = normalizeToHex(color) || normalizeColorInput(color)
      currentTextColor.value = hex
      customColor.value = hex
      lastCustomColor.value = hex
      try { sessionStorage.setItem('bwai_inline_last_color', hex) } catch {}
      // Keep a deduplicated most-recent-first history (capped at 5)
      recentColors.value = [hex, ...recentColors.value.filter(c => c !== hex)].slice(0, 5)
      showColorPicker.value = !!keepPickerOpen
      applyColorToSelection(hex)
    }

    const applyCurrentColor = () => {
      applyColorToSelection(currentTextColor.value)
    }

    const promoteToSectionTarget = () => {
      if (!selectedElement.value?.sectionTarget) return
      const target = selectedElement.value.sectionTarget
      const targetStyles = { ...(target.styles || {}) }
      targetStyles.color = normalizeToHex(targetStyles.color) || '#111827'
      targetStyles.backgroundColor = normalizeToHex(targetStyles.backgroundColor) || '#ffffff'
      selectedElement.value = {
        ...target,
        isSectionTarget: true,
        sectionTarget: null,
        baselineText: target.text || '',
        baselineHtml: target.html || '',
        baselineStyles: targetStyles,
        originalStyles: targetStyles
      }
      resetPendingStyles(selectedElement.value.originalStyles)
      pendingText.value = target.text || ''
      pendingHtml.value = target.html || target.text || ''
      inlineEditError.value = ''
      livePreviewStyles()
    }

    const toggleColorPicker = () => {
      if (!showColorPicker.value) {
        saveRichTextSelection()
        customColor.value = normalizeToHex(currentTextColor.value) || currentTextColor.value
      }
      showColorPicker.value = !showColorPicker.value
    }

    const resetPendingStyles = (base = null) => {
      pendingStyles.fontFamily = base?.fontFamily || ''
      pendingStyles.fontSize = base?.fontSize || ''
      pendingStyles.bold = base?.bold || false
      pendingStyles.italic = base?.italic || false
      pendingStyles.color = base?.color || ''
      pendingStyles.backgroundColor = base?.backgroundColor || ''
    }

    const clearSelection = () => {
      selectedElement.value = null
      inlineEditError.value = ''
      inlineEditSubmitting.value = false
      resetPendingStyles()
      pendingText.value = ''
      pendingHtml.value = ''
      postToIframe({ type: 'bwai:clear-selection' })
    }

    const clearInlineBridgeHandshakeTimer = () => {
      if (inlineBridgeHandshakeTimer.value) {
        clearTimeout(inlineBridgeHandshakeTimer.value)
        inlineBridgeHandshakeTimer.value = null
      }
    }

    // Fail-safe: mark the inline bridge unavailable but keep the editor shell
    // (preview, refresh, navigation) usable. We do NOT force editMode OFF —
    // the toggle stays user-driven — but every write/preview side effect is
    // gated on inlineBridgeReady so nothing leaks through.
    const markInlineBridgeUnavailable = (reason = 'unavailable') => {
      inlineBridgeReady.value = false
      inlineBridgeFailed.value = true
      inlineBridgeFailureReason.value = reason
      clearInlineBridgeHandshakeTimer()
      clearSelection()
    }

    const startInlineBridgeHandshake = () => {
      clearInlineBridgeHandshakeTimer()
      inlineBridgeFailed.value = false
      inlineBridgeFailureReason.value = ''
      inlineBridgeReady.value = false
      inlineBridgeHandshakeTimer.value = setTimeout(() => {
        if (!inlineBridgeReady.value) {
          markInlineBridgeUnavailable('handshake_timeout')
        }
      }, INLINE_BRIDGE_HANDSHAKE_TIMEOUT_MS)
    }

    const toggleEditMode = () => {
      const next = !editMode.value
      if (next) {
        leftPanelWasOpenBeforeEdit.value = leftPanelOpen.value
      }
      editMode.value = next
      rightPanelOpen.value = next
      if (next) {
        rightPanelMode.value = 'edit'
        leftPanelOpen.value = false
      }
      // Always clear any in-flight selection when toggling.
      if (!next) {
        clearSelection()
        inlineEditorExpanded.value = false
      }
      if (!next && leftPanelWasOpenBeforeEdit.value) {
        leftPanelOpen.value = true
      }
      if (!next) {
        clearInlineBridgeHandshakeTimer()
      } else {
        startInlineBridgeHandshake()
      }
      postToIframe({ type: 'bwai:edit-mode', enabled: next })
    }

    const livePreviewStyles = () => {
      if (!editMode.value || !selectedElement.value) return
      if (!inlineBridgeReady.value) return
      postToIframe({
        type: 'bwai:preview-styles',
        selector: selectedElement.value?.selector,
        styles: { ...pendingStyles },
        html: isSectionStyleTarget.value ? undefined : (pendingHtml.value !== (selectedElement.value?.baselineHtml ?? '') ? pendingHtml.value : undefined),
        text: isSectionStyleTarget.value ? undefined : (pendingText.value !== (selectedElement.value?.originalText ?? selectedElement.value?.text ?? '') ? pendingText.value : undefined)
      })
    }

    const resetStylesAction = () => {
      if (!editMode.value || !selectedElement.value) return
      if (!inlineBridgeReady.value) return
      // Reset restores the element's BASELINE style (BUI-US-32 AC#7),
      // not the currently effective style — so it reverts any committed overrides too.
      const baseline = selectedElement.value.baselineStyles
        || selectedElement.value.originalStyles
        || null
      resetPendingStyles(baseline)
      if (selectedElement.value.baselineText !== undefined) {
        pendingText.value = selectedElement.value.baselineText
        pendingHtml.value = selectedElement.value.baselineHtml || selectedElement.value.baselineText || ''
        syncRichEditor(pendingHtml.value)
      }
      inlineEditError.value = ''
      livePreviewStyles()
    }

    const cancelStylesAction = () => {
      if (!editMode.value || !selectedElement.value) return
      if (!inlineBridgeReady.value) {
        clearSelection()
        return
      }
      // Restore original styles in the iframe and close the option row.
      postToIframe({
        type: 'bwai:preview-styles',
        selector: selectedElement.value.selector,
        styles: selectedElement.value.originalStyles || {}
      })
      clearSelection()
    }

    // Client-side validation mirrors the backend guardrails so we can show
    // a precise error message in the option row before submitting (BUI-US-32 AC#11).
    const FONT_SIZE_RX = /^\d+(\.\d+)?(px|rem|em|%)$/
    const COLOR_RX = /^(#[0-9a-fA-F]{3,8}|rgba?\([^)]+\)|hsla?\([^)]+\)|[a-zA-Z]+)$/
    const validatePendingStyles = () => {
      const errors = []
      const fs = (pendingStyles.fontSize || '').trim()
      if (fs && !FONT_SIZE_RX.test(fs)) {
        errors.push('Font size must look like 16px, 1.25rem, 1em, or 90%.')
      }
      const colorEntries = [
        ['Color', pendingStyles.color],
        ['Background color', pendingStyles.backgroundColor]
      ]
      for (const [label, raw] of colorEntries) {
        const color = (raw || '').trim()
        if (color && !COLOR_RX.test(color)) {
          errors.push(`${label} must be a hex value, rgb(...), hsl(...), or a named color.`)
        }
      }
      const ff = (pendingStyles.fontFamily || '').trim()
      if (ff && /[<>{};]/.test(ff)) {
        errors.push('Font family contains disallowed characters.')
      }
      return errors
    }

    const wouldDeleteSelectedContent = () => {
      if (!selectedElement.value) return false
      const original = (selectedElement.value.baselineText || selectedElement.value.text || '').trim()
      if (!original) return false
      return (pendingText.value || '').trim() === ''
    }

    const buildInlineEditPayload = () => {
      if (!selectedElement.value) return null
      const styles = {}
      const original = selectedElement.value.originalStyles || {}
      const keys = ['fontFamily', 'fontSize', 'color', 'backgroundColor']
      for (const k of keys) {
        const v = (pendingStyles[k] || '').toString().trim()
        if (v && v !== (original[k] || '')) styles[k] = v
      }
      if (!isSectionStyleTarget.value) {
        if (pendingStyles.bold !== !!original.bold) styles.bold = pendingStyles.bold
        if (pendingStyles.italic !== !!original.italic) styles.italic = pendingStyles.italic
      }

      const originalText = (selectedElement.value.text || '').toString()
      const nextText = (pendingText.value || '').toString()
      const originalHtml = (selectedElement.value.baselineHtml || '').toString()
      const nextHtml = sanitizeRichTextHtml(pendingHtml.value || '')
      const textChanged = !isSectionStyleTarget.value && nextText !== originalText
      const htmlChanged = !isSectionStyleTarget.value && nextHtml !== originalHtml

      // Guardrail: empty-text replacements are blocked — they would erase content.
      if (textChanged && nextText.trim() === '') return null

      const hasStyleChanges = Object.keys(styles).length > 0
      if (!textChanged && !htmlChanged && !hasStyleChanges) return null

      const edit = {
        selector: selectedElement.value.cssSelector || selectedElement.value.selector,
        sourceId: selectedElement.value.sourceId || ''
      }
      if (htmlChanged) {
        edit.html = nextHtml
        edit.text = nextText
        edit.originalText = selectedElement.value.baselineText
        edit.originalHtml = selectedElement.value.baselineHtml
      } else if (textChanged) {
        edit.text = nextText
        edit.originalText = selectedElement.value.baselineText
        edit.originalHtml = selectedElement.value.baselineHtml
      }
      if (hasStyleChanges) edit.styles = styles
      return { edits: [edit] }
    }

    const applyStylesAction = async () => {
      if (!editMode.value) return
      if (!inlineBridgeReady.value) {
        inlineEditError.value = 'Inline editing is unavailable for this preview.'
        return
      }
      if (!selectedElement.value) return
      const targetSelector = selectedElement.value.selector
      inlineEditError.value = ''
      const validationErrors = validatePendingStyles()
      if (validationErrors.length > 0) {
        inlineEditError.value = validationErrors.join(' ')
        return
      }
      if (wouldDeleteSelectedContent()) {
        inlineEditError.value = 'This edit would remove all text from the selected element. Use Cancel or keep at least some text.'
        return
      }
      const payload = buildInlineEditPayload()
      if (!payload) {
        inlineEditError.value = 'No changes to apply.'
        return
      }
      if (!editMode.value || !selectedElement.value || selectedElement.value.selector !== targetSelector) return
      const edit = payload.edits[0]
      // Optimistic iframe update for instant visual feedback while backend writes files + commits.
      postToIframe({
        type: 'bwai:apply-styles',
        selector: targetSelector,
        styles: { ...pendingStyles },
        ...(edit.html !== undefined ? { html: edit.html } : {}),
        ...(edit.text !== undefined ? { text: edit.text } : {})
      })
      inlineEditSubmitting.value = true
      try {
        const result = await siteStore.submitInlineEdits(route.params.id, payload)
        if (!editMode.value || !selectedElement.value || selectedElement.value.selector !== targetSelector) {
          return
        }
        // Track the draft commit so Confirm and Undo know what to operate on.
        pendingDraftCommits.value.push({
          updateRequestId: result?.updateRequest?.id,
          draftCommitHash: result?.draftCommitHash,
          selector: targetSelector
        })
        committedSessionEdits[targetSelector] = {
          text: pendingText.value || selectedElement.value?.text || '',
          html: pendingHtml.value || selectedElement.value?.baselineHtml || '',
          styles: { ...pendingStyles }
        }
        clearSelection()
      } catch (error) {
        const code = error?.response?.data?.code
        const backendMsg = error?.response?.data?.error || error?.message || 'Failed to apply inline edit.'
        if (code === 'invalid_inline_edits') {
          inlineEditError.value = `${backendMsg}`
        } else {
          inlineEditError.value = backendMsg
        }
      } finally {
        inlineEditSubmitting.value = false
      }
    }

    const confirmAndPushAction = async () => {
      if (!hasPendingDraftCommits.value) return
      confirmInlineEditsError.value = ''
      confirmingInlineEdits.value = true
      try {
        await siteStore.confirmInlineEdits(route.params.id)
        // Success path ONLY — pending drafts cleared only after a real push.
        pendingDraftCommits.value = []
      } catch (error) {
        // AC012: map backend code → actionable copy. Lock / stale / race
        // conflicts get explicit guidance ("wait", "refresh + retry") instead
        // of a generic "failed to push changes". No success state shown.
        const code = error?.response?.data?.code
        const heldByEmail = error?.response?.data?.details?.heldByEmail || null
        const backendMsg = error?.response?.data?.error || error?.message || 'Failed to push changes.'
        confirmInlineEditsError.value = formatLockActionMessage(code, heldByEmail, backendMsg)
      } finally {
        confirmingInlineEdits.value = false
      }
    }

    const undoLatestAction = async () => {
      if (!hasPendingDraftCommits.value || undoingInlineEdit.value) return
      undoInlineEditError.value = ''
      undoingInlineEdit.value = true
      try {
        const latestDraft = pendingDraftCommits.value[pendingDraftCommits.value.length - 1]
        await siteStore.undoInlineEdit(route.params.id)
        // Success path ONLY — pop the local draft after a real revert.
        pendingDraftCommits.value.pop()
        if (latestDraft?.selector) {
          delete committedSessionEdits[latestDraft.selector]
        }
        clearSelection()
        // Refresh iframe so HMR-missed reverts become visible and selection
        // rehydrates from the actual current DOM state.
        await refreshPreview({ silent: true })
      } catch (error) {
        // AC012: actionable undo-conflict copy (revert-conflict vs lock-race).
        const code = error?.response?.data?.code
        const heldByEmail = error?.response?.data?.details?.heldByEmail || null
        const backendMsg = error?.response?.data?.error || error?.message || 'Failed to undo.'
        undoInlineEditError.value = formatLockActionMessage(code, heldByEmail, backendMsg)
      } finally {
        undoingInlineEdit.value = false
      }
    }

    // Recovery for the "No active edit session" stuck state: discard every
    // pending draft + drain the stale lock, then clear all local edit state.
    const resettingEditSession = ref(false)
    const resetEditSession = async () => {
      if (resettingEditSession.value) return
      resettingEditSession.value = true
      try {
        await siteStore.resetInlineEdits(route.params.id)
        // Clear local edit state so the phantom "unsaved" UI disappears.
        pendingDraftCommits.value = []
        Object.keys(committedSessionEdits).forEach((k) => { delete committedSessionEdits[k] })
        confirmInlineEditsError.value = ''
        undoInlineEditError.value = ''
        siteStore.clearLockConflict()
        clearSelection()
        await siteStore.fetchInlineEditLockStatus(route.params.id).catch(() => {})
        // Reflect the reverted files in the preview.
        await refreshPreview({ silent: true })
      } catch (error) {
        const code = error?.response?.data?.code
        const heldByEmail = error?.response?.data?.details?.heldByEmail || null
        const backendMsg = error?.response?.data?.error || error?.message || 'Could not reset the edit session.'
        confirmInlineEditsError.value = formatLockActionMessage(code, heldByEmail, backendMsg)
      } finally {
        resettingEditSession.value = false
      }
    }

    const handleInlineEditMessage = (event) => {
      const data = event?.data
      if (!data || data.source !== 'bwai-preview' || typeof data.type !== 'string') return

      // Route-change + page-list messages are always consumed (regardless of
      // edit mode) so the social-post composer can default to the live preview
      // route. These early branches do NOT affect any inline-edit state, so
      // they are safe to handle before the OFF-mode short-circuit below.
      if (data.type === 'bwai:route-changed' && typeof data.path === 'string') {
        // Store the full path (pathname+query+hash) for refresh URL restoration (AC006).
        lastKnownPreviewPath.value = data.path
        // currentPreviewRoute carries only the pathname for social-post routing.
        try { currentPreviewRoute.value = new URL(data.path, 'http://x').pathname } catch (_) { currentPreviewRoute.value = data.path }
        // Push to history stack unless we triggered this navigation ourselves.
        if (!isNavigatingHistory.value) {
          previewHistoryStack.value = previewHistoryStack.value.slice(0, previewHistoryIndex.value + 1)
          if (previewHistoryStack.value[previewHistoryIndex.value] !== data.path) {
            previewHistoryStack.value.push(data.path)
            previewHistoryIndex.value = previewHistoryStack.value.length - 1
          }
        }
        isNavigatingHistory.value = false
        return
      }
      if (data.type === 'bwai:pages' && Array.isArray(data.pages)) {
        const normalized = data.pages
          .map(p => (typeof p === 'string' ? { path: p, label: p } : p))
          .filter(p => p && typeof p.path === 'string')
        if (normalized.length) availablePreviewPages.value = normalized
        return
      }

      if (!editMode.value && data.type !== 'bwai:ready') return

      if (data.type === 'bwai:element-selected') {
        if (!editMode.value) return
        if (!inlineBridgeReady.value || inlineBridgeFailed.value) return
        if (!isSupportedEditableTag(data.tagName)) return
        // Merge session-scoped committed edits so reselecting the same element
        // surfaces previously applied changes in this preview session.
        const committed = committedSessionEdits[data.selector] || null
        const baseline = {
          fontFamily: data.styles?.fontFamily || '',
          fontSize: data.styles?.fontSize || '',
          bold: !!data.styles?.bold,
          italic: !!data.styles?.italic,
          color: data.styles?.color || ''
        }
        selectedElement.value = {
          selector: data.selector,
          cssSelector: data.cssSelector || data.selector,
          sourceId: typeof data.sourceId === 'string' ? data.sourceId : '',
          sectionTarget: data.sectionTarget && typeof data.sectionTarget === 'object'
            ? data.sectionTarget
            : null,
          isSectionTarget: false,
          tagName: data.tagName || '',
          text: committed?.text !== undefined
            ? committed.text
            : (typeof data.text === 'string' ? data.text : ''),
          html: committed?.html !== undefined
            ? committed.html
            : (typeof data.html === 'string' ? data.html : ''),
          baselineText: typeof data.text === 'string' ? data.text : '',
          baselineHtml: typeof data.html === 'string' ? data.html : '',
          // baselineStyles = first-observed raw element styles (target of Reset).
          baselineStyles: baseline,
          // originalStyles = currently effective styles (baseline merged with
          // any committed override) — used as the diff source by Apply.
          originalStyles: {
            fontFamily: committed?.styles?.fontFamily ?? baseline.fontFamily,
            fontSize: committed?.styles?.fontSize ?? baseline.fontSize,
            bold: committed?.styles?.bold ?? baseline.bold,
            italic: committed?.styles?.italic ?? baseline.italic,
            color: committed?.styles?.color ?? baseline.color
          }
        }
        resetPendingStyles(selectedElement.value.originalStyles)
        inlineEditorExpanded.value = true
        // Bring the just-revealed inline editor into view — selecting an element
        // in the center canvas otherwise leaves its controls off-screen in the
        // right panel, which is the core "not intuitive" complaint.
        nextTick(() => {
          try { inlineEditorEl.value?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }) } catch (_) {}
        })
        pendingText.value = selectedElement.value.text || ''
        pendingHtml.value = selectedElement.value.html || selectedElement.value.text || ''
        // Only overwrite the color indicator if the element carries an explicit
        // non-default color. Otherwise restore the user's last custom pick so
        // their chosen color persists across element selections.
        const elementHex = pendingStyles.color ? (normalizeToHex(pendingStyles.color) || normalizeColorInput(pendingStyles.color)) : null
        const hasExplicitColor = elementHex && elementHex !== '#111827' && elementHex !== '#000000' && elementHex !== '#1a1a1a'
        customColor.value = hasExplicitColor ? elementHex : (normalizeToHex(lastCustomColor.value) || lastCustomColor.value)
        currentTextColor.value = customColor.value
        syncRichEditor(pendingHtml.value)
        inlineEditError.value = ''
      }

      if (data.type === 'bwai:selection-cleared') {
        clearSelection()
      }

      if (data.type === 'bwai:ready') {
        if (data.themeColors && typeof data.themeColors === 'object' && !Array.isArray(data.themeColors)) {
          // New format: { accents: string[], neutrals: string[] }
          siteThemeColors.value = {
            accents: normalizePaletteColors(Array.isArray(data.themeColors.accents) ? data.themeColors.accents : []),
            neutrals: normalizePaletteColors(Array.isArray(data.themeColors.neutrals) ? data.themeColors.neutrals : [])
          }
        } else if (Array.isArray(data.themeColors)) {
          // Legacy flat-array fallback
          siteThemeColors.value = { accents: normalizePaletteColors(data.themeColors), neutrals: [] }
        }
        if (import.meta.env.DEV) {
          console.debug('[BWAI inline colors]', siteThemeColors.value)
        }
        // Bridge handshake confirmed — clear any pending failure state.
        inlineBridgeReady.value = true
        inlineBridgeFailed.value = false
        inlineBridgeFailureReason.value = ''
        clearInlineBridgeHandshakeTimer()
        // Sync edit mode to the iframe whenever it re-announces ready.
        postToIframe({ type: 'bwai:edit-mode', enabled: editMode.value })
	      }

      if (data.type === 'bwai:bridge-error') {
        markInlineBridgeUnavailable(typeof data.reason === 'string' ? data.reason : 'preview_reported_error')
      }

      // bwai:ready carries the initial route; seed both the social-post route
      // and the refresh-preservation path from it on first handshake.
      if (data.type === 'bwai:ready' && typeof data.path === 'string') {
        if (!lastKnownPreviewPath.value) lastKnownPreviewPath.value = data.path
        if (!currentPreviewRoute.value) {
          try { currentPreviewRoute.value = new URL(data.path, 'http://x').pathname } catch (_) { currentPreviewRoute.value = data.path }
        }
      }
    }

    const deriveRouteFromUrl = (url) => {
      if (!url || typeof url !== 'string') return ''
      try {
        const u = new URL(url)
        return u.pathname || '/'
      } catch (_) {
        return ''
      }
    }

    // Combine base preview URL origin with the last known in-preview path
    // (pathname + query + hash). Falls back to the base URL when no path
    // is known yet so refresh still works on first load (AC004, AC005).
    const buildPreviewUrlWithPath = (base, knownPath) => {
      if (!base) return base
      if (!knownPath) return base
      try {
        return new URL(knownPath, new URL(base).origin).href
      } catch (_) {
        return base
      }
    }

    // Computed src for the preview iframe — always the base URL origin plus the
    // last path the user navigated to inside the preview (AC001, AC003, AC004).
    const previewIframeSrc = computed(() => {
      const base = site.value?.preview_url
      if (!base) return ''
      return buildPreviewUrlWithPath(base, lastKnownPreviewPath.value) || base
    })

    const handlePreviewIframeLoad = () => {
      // Reset bridge readiness — the new iframe content must re-handshake.
      inlineBridgeReady.value = false
      // Session-scoped committed edits are cleared on preview reload so they
      // remain scoped to the current preview session (BUI-US-32 AC#5).
      for (const k of Object.keys(committedSessionEdits)) delete committedSessionEdits[k]
      if (editMode.value) {
        startInlineBridgeHandshake()
      }
      // Enforce safe inspect mode on fresh preview availability unless the user toggled it.
      postToIframe({ type: 'bwai:edit-mode', enabled: editMode.value })
    }

    return {
      site,
      brandLogo,
      updates,
      recentUpdates,
      updateForm,
      submitting,
      statusClass,
      formatDate,
      submitUpdate,
      submitAsk,
      submitAssistant,
      assistantCopy,
      askHistory,
      askError,
      askHistoryCollapsed,
      conversation,
      turnModeLabel,
      turnModeClass,
      activityStepsForTurn,
      visibleActivityStepsForTurn,
      isTurnActivityExpanded,
      toggleTurnActivity,
      isTurnAwaitingPublish,
      isTurnAwaitingClarification,
      clarifyingTurn,
      clarificationQuestionForTurn,
      submitClarification,
      confirmPendingChange,
      cancelPendingChange,
      classifyConversationPrompt,
      conversationPaneEl,
      conversationHasMore,
      conversationLoadingMore,
      loadPreviousConversationTurns,
      handleConversationScroll,
      resetConversation,
      approveTurn,
      rejectTurn,
      revertToTurn,
      isTurnActionBusy,
      orphanActivitySteps,
      liveUpdatesVisible,
      canvasFrameEl,
      previewShellStyle,
      previewDimensions,
      aspectRatio,
      setAspectFromDevice,
      openCustomAspect,
      applyCustomAspect,
      customAspectInput,
      customAspectError,
      showCustomAspectInput,
      isResizingSidebar,
      startSidebarResize,
      liveUpdatesCollapsed,
      turnDiffs,
      turnDiffState,
      isTurnDiffOpen,
      toggleTurnDiff,
      viewLatestAskAnswer,
      lastSubmittedPrompt,
      renderMarkdown,
      canRetryAsk,
      approveFeedback,
      refreshingPreview,
      handleRefreshPreview,
      publishing,
      publishingSandbox,
      syncingGitHub,
      handleSyncGitHub,
      handlePublishSandbox,
      handlePublish,
      retrying,
      retrySite,
      showCli,
      leftPanelOpen,
      activeSheetTab,
      cliOutput,
      formatCliTimestamp,
      isPreviewDeploying,
      previewReloadKey,
      activitySteps,
      latestStep,
      latestSummary,
      isActivityError,
      isActivityComplete,
      activityProgress,
      activityProgressLabel,
      handleAttachmentUpload,
      removeAttachment,
      formatBytes,
      promptSuggestions,
      workflowSearchQuery,
      filteredWorkflowSuggestions,
      applySuggestion,
      showSuggestions,
      showBlogModal,
      handleBlogCreate,
      showSeoModal,
      handleSeoOptimization,
      showLandingPageModal,
      handleLandingPageCreate,
      showSearchEngineModal,
      handleSearchEngineSubmission,
      currentPreviewRoute,
      lastKnownPreviewPath,
      canGoBack,
      canGoForward,
      goBack,
      goForward,
      previewIframeSrc,
      buildPreviewUrlWithPath,
      availablePreviewPages,
      pagesSearchQuery,
      filteredPages,
      deletingPage,
      deletePage,
      showCreatePageModal,
      creatingPage,
      createPageError,
      createPageTouchedSlug,
      createPageForm,
      openCreatePageModal,
      closeCreatePageModal,
      syncCreatePageSlug,
      submitCreatePage,
      showPromptUndo,
      undoSuggestion,
      attachmentInput,
      promptInputEl,
      triggerAttachmentUpload,
      setActiveSheetTab,
      openLeftPanel,
      editMode,
      toggleEditMode,
      selectedElement,
      pendingStyles,
      fontFamilyOptions,
      fontSizeOptions,
      fontStyleChoice,
      pendingHtml,
      inlineEditorEl,
      paletteAccents,
      paletteNeutrals,
      recentColors,
      customColor,
      showColorPicker,
      currentTextColor,
      saveRichTextSelection,
      handleRichTextInput,
      formatRichText,
      applyTextColor,
      applyCurrentColor,
      toggleColorPicker,
      previewIframeEl,
      handlePreviewIframeLoad,
      inlineEditError,
      inlineEditSubmitting,
      inlineBridgeReady,
      inlineBridgeFailed,
      inlineBridgeFailureReason,
      showContextualOptionRow,
      isSectionStyleTarget,
      isSupportedEditableTag,
      friendlyTagLabel,
      elementAiPrompt,
      elementAiSubmitting,
      submitElementAiEdit,
      seoPages,
      seoDefaults,
      seoLoading,
      seoError,
      selectedSeoPath,
      seoSaving,
      seoSavedPath,
      seoForm,
      loadSeoPages,
      openSeoPage,
      saveSeoPage,
      seoScoreColor,
      promoteToSectionTarget,
      pendingText,
      livePreviewStyles,
      applyStylesAction,
      resetStylesAction,
      cancelStylesAction,
      clearSelection,
      hasPendingDraftCommits,
      pendingDraftCommits,
      confirmingInlineEdits,
      confirmInlineEditsError,
      confirmAndPushAction,
      undoingInlineEdit,
      undoInlineEditError,
      undoLatestAction,
      resetEditSession,
      resettingEditSession,
      // Edit-lock UI (AC009/AC010/AC012)
      isLockedByAnotherEditor,
      lockOwnerLabel,
      lockExpiresAtLabel,
      lockConflictMessage,
      serverPendingCount,
      recoveredPendingCount,
      rightPanelOpen,
      rightPanelMode,
      openSeoPanel,
      zoomLevel,
      zoomIn,
      zoomOut,
      inlineEditorExpanded,
      deviceMode,
      siteStore,
      openDropdownId,
      toggleDropdown,
      closeAllDropdowns,
      isDropdownOpen,
      siteCommits,
      historySearchQuery,
      filteredCommits,
      loadingCommits,
      commitsError,
      restoringCommit,
      restoreSuccess,
      restoreError,
      loadSiteCommits,
      restoreToVersion,
      formatRelativeTime,
      creationFeedbackPending,
      creationFeedbackSubmitted,
      creationFeedbackSubmitting,
      creationFeedbackComment,
      creationFeedbackError,
      showCreationFeedbackInput,
      submitCreationFeedback,
    }
  }
}
</script>

<style scoped>
/* ── Shell ─────────────────────────────────────────────────────── */
.editor-shell {
  /* Sits inside the app shell, under the 52px main nav. */
  height: calc(100vh - 52px);
  display: flex;
  flex-direction: column;
  background: var(--surface-base);
  color: var(--text-primary);
}

/* ── Header ────────────────────────────────────────────────────── */
.editor-header {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 20px;
  background: var(--surface-1);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border-bottom: var(--glass-border);
}
.editor-header-left { display: flex; align-items: center; gap: 12px; }
.editor-header-right { display: flex; align-items: center; gap: 10px; }
.editor-back-btn {
  color: var(--text-muted);
  transition: color 0.15s;
}
.editor-back-btn:hover { color: var(--text-primary); }
.editor-site-name { font-size: 15px; font-weight: 600; color: var(--text-primary); }
.editor-status-pill {
  padding: 2px 10px;
  border-radius: 20px;
  font-size: 11px;
  font-weight: 500;
  background: var(--accent-soft);
  color: var(--accent);
  border: var(--glass-border);
}
.editor-icon-btn {
  color: var(--text-muted);
  transition: color 0.15s;
}
.editor-icon-btn:hover { color: var(--text-primary); }
.editor-text-link { color: var(--accent); }
.editor-text-link:hover { opacity: 0.8; }
.editor-text-link-blue { color: #38bdf8; }
.editor-text-link-blue:hover { opacity: 0.8; }
.editor-btn-sandbox {
  font-size: 14px;
  font-weight: 500;
  padding: 5px 12px;
  border-radius: 8px;
  background: var(--glass-bg);
  color: #38bdf8;
  border: 1px solid rgba(56, 189, 248, 0.35);
  transition: all 0.15s;
}
.editor-btn-sandbox:hover { background: rgba(56,189,248,0.08); }

/* ── Body ──────────────────────────────────────────────────────── */
.editor-body {
  flex: 1;
  display: flex;
  overflow: hidden;
}

/* ── Left Panel ────────────────────────────────────────────────── */
.editor-left-panel {
  flex-shrink: 0;
  background: var(--surface-1);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border-right: var(--glass-border);
  transition: width 0.3s;
  overflow: hidden;
}
.editor-left-panel--open { width: 420px; }
.editor-left-panel--collapsed { width: 48px; }

.editor-collapse-btn {
  padding: 6px;
  border-radius: 8px;
  color: var(--text-muted);
  transition: color 0.15s, background 0.15s;
}
.editor-collapse-btn:hover {
  color: var(--accent);
  background: var(--accent-soft);
}

.editor-tab-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-bottom: var(--glass-border);
  flex-shrink: 0;
}
.editor-tab {
  padding: 5px 12px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-muted);
  transition: all 0.15s;
}
.editor-tab:hover { color: var(--text-secondary); background: var(--glass-bg-hover); }
.editor-tab--active { background: var(--accent-soft); color: var(--accent); }

.editor-mode-toggle {
  display: inline-flex;
  border-radius: 10px;
  padding: 3px;
  background: var(--surface-2);
  border: var(--glass-border);
}
.editor-mode-btn {
  padding: 5px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  color: var(--text-muted);
  transition: all 0.15s;
}
.editor-mode-btn:hover { color: var(--text-secondary); }
.editor-mode-btn--active {
  background: var(--glass-bg-active);
  color: var(--text-primary);
  box-shadow: var(--elev-sm);
}

.editor-attach-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 32px;
  width: 32px;
  border-radius: 50%;
  border: var(--glass-border);
  color: var(--text-muted);
  transition: all 0.15s;
}
.editor-attach-btn:hover { color: var(--text-primary); background: var(--glass-bg-hover); }

.editor-lock-banner {
  border-radius: 8px;
  padding: 8px 12px;
  background: rgba(251,191,36,0.08);
  border: 1px solid rgba(251,191,36,0.25);
  color: #fbbf24;
  font-size: 14px;
}
.editor-conflict-banner {
  border-radius: 8px;
  padding: 8px 12px;
  background: rgba(248,113,113,0.08);
  border: 1px solid rgba(248,113,113,0.25);
  color: var(--danger);
  font-size: 14px;
}

.editor-ask-card {
  border-radius: 10px;
  padding: 12px;
  font-size: 14px;
  background: var(--surface-2);
  border: var(--glass-border);
}
.editor-empty-ask {
  border-radius: 8px;
  border: 1px dashed rgba(255,255,255,0.12);
  padding: 12px;
  font-size: 12px;
  color: var(--text-muted);
}

.editor-workflow-card {
  padding: 12px;
  background: var(--glass-bg);
  border: var(--glass-border);
  border-radius: 10px;
  transition: all 0.15s;
}
.editor-workflow-card:hover {
  background: var(--glass-bg-hover);
  border: var(--glass-border-hi);
}

.editor-activity-card {
  border-radius: 10px;
  padding: 12px;
  background: var(--surface-2);
  border: var(--glass-border);
}
.editor-activity-card--error {
  background: rgba(248,113,113,0.06);
  border: 1px solid rgba(248,113,113,0.2);
}
.editor-activity-empty {
  border-radius: 10px;
  padding: 24px;
  text-align: center;
  border: 1px dashed rgba(255,255,255,0.1);
}

.editor-cli-log {
  background: #0d0d14;
  border-radius: 8px;
  padding: 12px;
  max-height: 256px;
  overflow-y: auto;
  border: var(--glass-border);
}

/* ── Preview Area ──────────────────────────────────────────────── */
.editor-preview-area {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  background: var(--surface-base);
}
.editor-preview-toolbar {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  background: var(--surface-1);
  border-bottom: var(--glass-border);
}
.editor-toolbar-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  padding: 4px 12px;
  border-radius: 8px;
  color: var(--text-secondary);
  background: var(--glass-bg);
  border: var(--glass-border);
  transition: all 0.15s;
  cursor: pointer;
}
.editor-toolbar-btn:hover {
  color: var(--text-primary);
  background: var(--glass-bg-hover);
  border: var(--glass-border-hi);
}
.editor-toolbar-btn--active {
  background: var(--accent-soft);
  color: var(--accent);
  border-color: rgba(124,109,250,0.35);
}

/* ── Inline Edit Panel ─────────────────────────────────────────── */
.editor-inline-panel {
  width: 360px;
  flex-shrink: 0;
  border-left: var(--glass-border);
  background: var(--surface-1);
  display: flex;
  flex-direction: column;
}
.editor-inline-panel-header {
  padding: 12px 16px;
  border-bottom: var(--glass-border);
}
.editor-select-hint {
  border-radius: 10px;
  border: 1px dashed rgba(124,109,250,0.3);
  background: rgba(124,109,250,0.06);
  padding: 16px;
  font-size: 14px;
  color: var(--accent);
}
.editor-toolbar-row {
  background: var(--surface-2);
  padding: 6px 8px;
  border: var(--glass-border);
}
.editor-toolbar-row:first-child { border-radius: 8px 8px 0 0; }
.editor-inline-select {
  font-size: 11px;
  border-radius: 6px;
  padding: 3px 6px;
  background: var(--glass-bg-active);
  border: var(--glass-border);
  color: var(--text-primary);
  outline: none;
}
.editor-inline-select:focus { border: var(--glass-border-hi); }

/* ── Custom select dropdowns ─────────────────────────────────── */
.custom-select-wrap { position: relative; display: inline-block; }
.custom-select-trigger {
  display: flex; align-items: center; justify-content: space-between;
  gap: 6px; cursor: pointer; text-align: left; white-space: nowrap;
  user-select: none;
}
.custom-select-trigger:hover { border-color: rgba(255,255,255,.25); color: var(--text-primary); }
.custom-select-chevron { flex-shrink: 0; color: var(--text-muted); transition: transform .15s; }
.custom-select-menu {
  position: absolute; top: calc(100% + 4px); left: 0; min-width: 100%;
  background: var(--surface-1, #1a1a2e); border: var(--glass-border-hi);
  border-radius: 8px; box-shadow: 0 8px 24px rgba(0,0,0,.4);
  z-index: 200; overflow: hidden; max-height: 220px; overflow-y: auto;
}
.custom-select-option {
  display: block; width: 100%; text-align: left; padding: 7px 12px;
  font-size: 12px; color: var(--text-secondary); background: none;
  border: none; cursor: pointer; transition: background .1s, color .1s;
  white-space: nowrap;
}
.custom-select-option:hover { background: rgba(255,255,255,.06); color: var(--text-primary); }
.custom-select-option.active { color: var(--accent); background: var(--accent-soft); }
.editor-inline-input {
  font-size: 11px;
  border-radius: 6px;
  padding: 2px 6px;
  background: var(--glass-bg-active);
  border: var(--glass-border);
  color: var(--text-primary);
  outline: none;
}
.editor-inline-input:focus { border: var(--glass-border-hi); }
.editor-fmt-btn {
  padding: 2px 8px;
  font-size: 11px;
  border-radius: 4px;
  color: var(--text-secondary);
  transition: all 0.1s;
}
.editor-fmt-btn:hover {
  background: var(--glass-bg-active);
  color: var(--text-primary);
}
.editor-inline-editable {
  min-height: 100px;
  width: 100%;
  font-size: 14px;
  border: var(--glass-border);
  border-radius: 0 0 8px 8px;
  padding: 8px 12px;
  background: var(--glass-bg-active);
  color: var(--text-primary);
  outline: none;
}
.editor-inline-editable:focus {
  border: var(--glass-border-hi);
  box-shadow: 0 0 0 3px rgba(124,109,250,0.12);
}
.editor-color-picker {
  position: absolute;
  z-index: 50;
  left: 0;
  top: 100%;
  margin-top: 4px;
  width: 240px;
  background: var(--surface-1);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  border: var(--glass-border);
  border-radius: 12px;
  box-shadow: var(--elev-lg);
  padding: 12px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* ── 3-column shell override ───────────────────────────────────── */
.editor-shell {
  display: grid;
  /* Editor actions are teleported into the app nav, so there is no editor
     toolbar row here — sidebar + canvas fill the whole area. */
  grid-template-rows: 1fr;
  /* Third column collapsed: the inline-edit panel now floats over the canvas
     as a popup, so the canvas gets the full remaining width. */
  grid-template-columns: var(--sidebar-width, 260px) 1fr 0;
  /* Fills the app-shell main area beneath the 52px global nav. */
  height: calc(100vh - 52px);
  overflow: hidden;
}

#topbar {
  grid-column: 1 / -1;
  grid-row: 1;
  display: flex; align-items: center; gap: 8px;
  padding: 0 16px;
  background: rgba(15,15,24,0.85);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: var(--glass-border);
  box-shadow: var(--elev-sm);
}

.logo { display: flex; align-items: center; gap: 8px; font-weight: 700; font-size: 14px; color: var(--text-primary); margin-right: 4px; }
.logo-icon { width: 160px; height: 28px; border-radius: 0; display: block; object-fit: contain; }
.topbar-divider { width: 1px; height: 20px; background: rgba(255,255,255,0.1); margin: 0 4px; flex-shrink: 0; }
.breadcrumb { display: flex; align-items: center; gap: 4px; color: var(--text-muted); font-size: 12px; }
.breadcrumb a { color: var(--text-secondary); cursor: pointer; }
.breadcrumb a:hover { color: var(--text-primary); }
.breadcrumb span { color: var(--text-primary); font-weight: 500; }
.breadcrumb-sep { color: var(--text-muted); }
.topbar-actions { margin-left: auto; display: flex; align-items: center; gap: 6px; }
.btn { display: inline-flex; align-items: center; gap: 6px; padding: 6px 12px; border-radius: var(--radius-sm, 8px); font-size: 12px; font-weight: 500; cursor: pointer; border: none; transition: all 0.15s; white-space: nowrap; font-family: inherit; }
.btn-ghost { background: transparent; color: var(--text-secondary); }
.btn-ghost:hover { background: var(--glass-bg-hover); color: var(--text-primary); }
.btn-glass { background: var(--glass-bg); color: var(--text-primary); border: var(--glass-border); }
.btn-glass:hover { background: var(--glass-bg-hover); border: var(--glass-border-hi); }
.btn-accent { background: var(--accent); color: #fff; box-shadow: 0 0 16px var(--accent-glow); }
.btn-accent:hover { background: #6d5ef0; box-shadow: 0 0 24px var(--accent-glow); }
.status-pill { display: flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; background: rgba(52,211,153,0.1); border: 1px solid rgba(52,211,153,0.2); color: var(--success); }
.status-dot { width: 6px; height: 6px; border-radius: 50%; background: var(--success); box-shadow: 0 0 6px var(--success); }
.status-pill-other { padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; background: var(--glass-bg); border: var(--glass-border); color: var(--text-secondary); }

#sidebar {
  grid-column: 1;
  grid-row: 1;
  overflow: hidden; display: flex; flex-direction: column;
  border-right: var(--glass-border);
  background: var(--surface-1);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  position: relative;
}

/* Sidebar drag-resize handle — sits on the right edge, full height. JS
   updates --sidebar-width on the shell on drag (min 200, max 600). The
   handle is invisible until hovered/dragged so it doesn't fight the
   sidebar's right border visually. */
.sidebar-resize-handle {
  position: absolute;
  top: 0; bottom: 0; right: -3px;
  width: 6px;
  cursor: ew-resize;
  user-select: none;
  z-index: 5;
  background: transparent;
  transition: background 0.12s;
}
.sidebar-resize-handle:hover,
.sidebar-resize-handle.sidebar-resize-handle-active {
  background: var(--accent);
  opacity: 0.5;
}
body.sidebar-resizing { cursor: ew-resize !important; user-select: none; }
body.sidebar-resizing * { cursor: ew-resize !important; }
.sidebar-tabs { display: flex; border-bottom: var(--glass-border); padding: 6px 8px 0; gap: 2px; flex-shrink: 0; }
.tab { flex: 1; padding: 7px 4px; border-radius: 8px 8px 0 0; text-align: center; font-size: 11px; font-weight: 500; color: var(--text-muted); cursor: pointer; transition: all 0.15s; border: 1px solid transparent; border-bottom: none; }
.tab:hover { color: var(--text-secondary); background: var(--glass-bg-hover); }
.tab.active { color: var(--text-primary); background: var(--glass-bg-active); border-color: rgba(255,255,255,0.1); }
.sidebar-body { flex: 1; overflow-y: auto; padding: 12px; }
.sidebar-body::-webkit-scrollbar { width: 4px; }
.sidebar-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.1); border-radius: 4px; }
.section-label { font-size: 11px; font-weight: 600; letter-spacing: 0.08em; color: var(--text-muted); text-transform: uppercase; padding: 0 2px; margin-bottom: 8px; margin-top: 16px; }
.section-label:first-child { margin-top: 4px; }
.metrics-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin-bottom: 4px; }
.metric-card { background: var(--glass-bg); border: var(--glass-border); border-radius: 12px; padding: 10px; transition: all 0.15s; }
.metric-card:hover { background: var(--glass-bg-hover); border: var(--glass-border-hi); }
.metric-label { font-size: 9px; font-weight: 600; letter-spacing: 0.06em; text-transform: uppercase; color: var(--text-muted); margin-bottom: 6px; }
.metric-value { font-size: 22px; font-weight: 800; line-height: 1; margin-bottom: 4px; }
.metric-value.green { color: var(--success); }
.metric-value.amber { color: var(--warning); }
.metric-delta { font-size: 11px; display: flex; align-items: center; gap: 3px; color: var(--text-muted); }
.suggestion-card { background: var(--glass-bg); border: var(--glass-border); border-radius: 12px; padding: 10px 12px; margin-bottom: 6px; cursor: pointer; transition: all 0.15s; display: flex; align-items: flex-start; gap: 8px; }
.suggestion-card:hover { background: var(--glass-bg-hover); border: var(--glass-border-hi); box-shadow: var(--elev-sm); transform: translateY(-1px); }
.suggestion-icon { font-size: 16px; flex-shrink: 0; margin-top: 1px; }
.suggestion-body { flex: 1; min-width: 0; }
.suggestion-title { font-size: 11px; font-weight: 600; color: var(--text-primary); margin-bottom: 3px; }
.suggestion-meta { display: flex; align-items: center; justify-content: flex-end; margin-top: 6px; }
.apply-btn { font-size: 11px; padding: 3px 9px; border-radius: 6px; background: var(--accent-soft); color: var(--accent); border: 1px solid rgba(124,109,250,0.25); cursor: pointer; transition: all 0.12s; font-weight: 600; }
.apply-btn:hover { background: rgba(124,109,250,0.2); border-color: rgba(124,109,250,0.4); }
.quick-actions { display: flex; flex-wrap: wrap; gap: 5px; padding-bottom: 4px; }
.quick-pill { padding: 5px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; cursor: pointer; background: var(--glass-bg); border: var(--glass-border); color: var(--text-secondary); transition: all 0.15s; white-space: nowrap; }
.quick-pill:hover { background: var(--glass-bg-hover); border: var(--glass-border-hi); color: var(--text-primary); }

/* Sidebar AI Prompt */
.sidebar-prompt-wrap { display: flex; flex-direction: column; gap: 6px; padding: 8px 10px; background: var(--glass-bg); border: var(--glass-border); border-radius: 10px; margin-bottom: 4px; }
.sidebar-mode-pills { display: flex; gap: 4px; }
.sidebar-mode-pills button { flex: 1; padding: 4px 0; border-radius: 6px; font-size: 11px; font-weight: 600; cursor: pointer; background: transparent; border: 1px solid transparent; color: var(--text-muted); transition: all 0.15s; }
.sidebar-mode-pills button.active { background: var(--accent-soft); border-color: rgba(124,109,250,0.3); color: var(--accent); }
.sidebar-mode-pills button:not(.active):hover { background: var(--glass-bg-hover); color: var(--text-secondary); }
.sidebar-attachments { display: flex; flex-direction: column; gap: 4px; padding: 4px 0; }

/* Sidebar Live Changes (legacy class, kept for backwards compat) */
.sidebar-live-changes { padding: 8px 10px; background: var(--glass-bg); border: var(--glass-border); border-radius: 10px; margin-bottom: 4px; }

/* Conversation panel sections — Live updates still has the glass-box
   treatment (it's a distinct piece of UI scoped to site creation). The
   conversation log itself is rendered flat (no border, no background) so
   turns blend into the darker sidebar surface instead of sitting in a
   nested panel. The `-flat` modifier opts out of the box. */
.conversation-section { padding: 8px 10px; background: var(--glass-bg); border: var(--glass-border); border-radius: 10px; margin-bottom: 8px; }
.conversation-section.conversation-section-flat {
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  margin-bottom: 0;
}
.conversation-section:last-child { margin-bottom: 0; }
.conversation-section-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 6px; margin-top: 14px; }
.conversation-section-header .section-label { margin: 0; }
.conversation-section-status { font-size: 11px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: var(--text-muted); margin-bottom: 8px; }
.conversation-section-status-inline { display: inline-block; margin-left: 6px; padding: 1px 8px; font-size: 11px; font-weight: 600; color: var(--text-muted); background: var(--glass-bg); border: 1px solid rgba(255,255,255,0.08); border-radius: 999px; vertical-align: middle; text-transform: none; letter-spacing: 0; }
.conversation-turn-count { display: inline-block; margin-left: 6px; padding: 1px 6px; font-size: 9px; font-weight: 700; color: var(--text-muted); background: var(--glass-bg); border: 1px solid rgba(255,255,255,0.08); border-radius: 999px; vertical-align: middle; }
.conversation-reset-btn { padding: 2px 7px; border-radius: 999px; border: 1px solid rgba(255,255,255,0.12); background: rgba(255,255,255,0.04); color: var(--text-muted); font-size: 11px; font-weight: 700; cursor: pointer; }
.conversation-reset-btn:hover { color: var(--text-primary); background: rgba(255,255,255,0.08); }
.conversation-reset-btn:disabled { opacity: .55; cursor: default; }

/* Conversation Log */
.conversation-log::-webkit-scrollbar { width: 4px; }
.conversation-log::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.12); border-radius: 4px; }
.conversation-turn { padding-bottom: 10px; border-bottom: 1px solid rgba(255,255,255,0.06); }
.conversation-turn:last-child { border-bottom: none; padding-bottom: 0; }
.conversation-pill { font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; flex-shrink: 0; padding: 2px 6px; border-radius: 999px; line-height: 1.4; }
.conversation-pill.pill-ask { background: rgba(109,93,255,0.12); color: var(--accent); border: 1px solid rgba(109,93,255,0.25); }
.conversation-pill.pill-change { background: rgba(52,211,153,0.10); color: var(--success); border: 1px solid rgba(52,211,153,0.25); }
.conversation-pill.pill-assistant { background: rgba(255,255,255,0.08); color: var(--text-primary); border: 1px solid rgba(255,255,255,0.16); }
.conversation-assistant-actions { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; }
.conversation-assistant-hint { font-size: 12px; color: var(--text-muted); }

/* Chat-style layout: conversation fills the panel, prompt is pinned at the bottom */
.sidebar-body-chat { overflow: hidden; display: flex; flex-direction: column; padding: 0; flex: 1; min-height: 0; }
.sidebar-chat-pane { flex: 1 1 0%; min-height: 0; display: flex; flex-direction: column; padding: 12px 12px 24px; gap: 8px; }
.conversation-pane { flex: 1 1 0%; min-height: 0; overflow-y: auto; overflow-x: hidden; padding-right: 4px; }
.conversation-pane::-webkit-scrollbar { width: 4px; }
.conversation-pane::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.10); border-radius: 4px; }
.prompt-pane { flex-shrink: 0; padding-top: 10px; border-top: 1px solid rgba(255,255,255,0.06); position: relative; }
.prompt-pane .sidebar-prompt-wrap { margin-bottom: 0; }

/* In-turn activity feed (Change-mode in-flight messages) */
.conversation-turn-activity { display: flex; flex-direction: column; gap: 4px; margin-top: 4px; }
.conversation-turn-activity-row { display: flex; align-items: flex-start; gap: 8px; font-size: 11px; line-height: 1.5; color: var(--text-muted); }
.conversation-turn-activity-dot { flex-shrink: 0; margin-top: 5px; width: 6px; height: 6px; border-radius: 50%; display: inline-block; background: rgba(255,255,255,0.2); }
.conversation-turn-activity-dot.dot-active { background: var(--accent); box-shadow: 0 0 0 2px rgba(109,93,255,0.25); animation: pulse-dot 1.2s ease-in-out infinite; }
.conversation-turn-activity-dot.dot-done { background: var(--success); }
.conversation-turn-activity-dot.dot-error { background: var(--danger); }
.conversation-turn-activity-latest { color: var(--text-primary); }
.conversation-turn-activity-toggle {
  display: inline-flex; align-items: center; gap: 4px;
  align-self: flex-start;
  padding: 2px 6px;
  font-size: 11px;
  color: var(--text-muted);
  background: none;
  border: none;
  cursor: pointer;
  border-radius: 4px;
}
.conversation-turn-activity-toggle:hover { color: var(--text-secondary); background: rgba(255,255,255,0.04); }

/* Awaiting-publish actions: Approve + Reject buttons appear under a Change
   turn while the workflow is waiting for user feedback. */
.conversation-turn-publish-row { display: flex; flex-direction: column; gap: 6px; margin-top: 10px; padding-top: 8px; border-top: 1px dashed rgba(255,255,255,0.08); }
.conversation-turn-action-buttons { display: flex; gap: 6px; flex-wrap: wrap; }
.conversation-turn-publish-btn {
  padding: 7px 14px;
  font-size: 12px; font-weight: 600;
  color: #fff;
  background: var(--accent);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  box-shadow: 0 0 12px var(--accent-glow);
  transition: filter .15s, opacity .15s;
}
.conversation-turn-publish-btn:hover:not(:disabled) { filter: brightness(1.08); }
.conversation-turn-publish-btn:disabled { opacity: 0.6; cursor: progress; }
.conversation-turn-reject-btn {
  padding: 7px 14px;
  font-size: 12px; font-weight: 600;
  color: #f87171;
  background: rgba(248,113,113,0.08);
  border: 1px solid rgba(248,113,113,0.30);
  border-radius: 8px;
  cursor: pointer;
  transition: background .15s, opacity .15s;
}
.conversation-turn-reject-btn:hover:not(:disabled) { background: rgba(248,113,113,0.18); }
.conversation-turn-reject-btn:disabled { opacity: 0.6; cursor: progress; }
.conversation-turn-publish-hint { font-size: 11px; color: var(--text-muted); }

/* Clarification block: surfaces the question the worker paused to ask, and
   a "just proceed" inline link as an escape hatch. The chat input below the
   conversation is the primary reply path (no inline reply field). */
.conversation-turn-clarification {
  display: flex; flex-direction: column; gap: 4px;
  margin-top: 10px; padding: 10px 12px;
  background: rgba(99, 132, 255, 0.08);
  border: 1px solid rgba(99, 132, 255, 0.28);
  border-radius: 8px;
}
.conversation-turn-clarification-question {
  font-size: 12px; color: var(--text); line-height: 1.5;
}
.conversation-turn-clarification-prefix {
  font-weight: 600; color: var(--accent); margin-right: 6px;
}
.conversation-turn-clarification-hint {
  font-size: 11px; color: var(--text-muted); line-height: 1.5;
}
.conversation-turn-clarification-proceed {
  background: none; border: none; padding: 0;
  color: var(--accent); cursor: pointer; font: inherit; text-decoration: underline;
}
.conversation-turn-clarification-proceed:disabled { opacity: 0.6; cursor: progress; }

/* Previous-turn revert button — subdued styling so completed turns don't
   shout at the user. Lives on every Change turn with a recorded commit. */
.conversation-turn-revert-row { margin-top: 8px; }
.conversation-turn-revert-btn {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px;
  font-size: 11px; font-weight: 500;
  color: var(--text-muted);
  background: rgba(255,255,255,0.04);
  border: 1px solid rgba(255,255,255,0.10);
  border-radius: 999px;
  cursor: pointer;
  transition: background .12s, color .12s;
}
.conversation-turn-revert-btn:hover:not(:disabled) { background: rgba(255,255,255,0.10); color: var(--text-primary); }
.conversation-turn-revert-btn:disabled { opacity: 0.6; cursor: progress; }

/* Custom aspect-ratio popover (canvas toolbar) */
.custom-aspect-wrap { position: relative; display: inline-flex; }
.custom-aspect-popover {
  position: absolute;
  top: calc(100% + 6px);
  left: 0;
  z-index: 6;
  padding: 10px;
  background: var(--surface-1);
  border: var(--glass-border);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.35);
  min-width: 200px;
}
.custom-aspect-label { display: block; font-size: 11px; font-weight: 600; color: var(--text-muted); text-transform: uppercase; letter-spacing: .07em; margin-bottom: 6px; }
.custom-aspect-row { display: flex; gap: 6px; }
.custom-aspect-input {
  flex: 1;
  padding: 6px 8px;
  font-size: 12px;
  color: var(--text-primary);
  background: var(--glass-bg);
  border: var(--glass-border);
  border-radius: 6px;
  font-family: ui-monospace, monospace;
  outline: none;
}
.custom-aspect-input:focus { border-color: var(--accent); }
.custom-aspect-apply {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: #fff;
  background: var(--accent);
  border: none;
  border-radius: 6px;
  cursor: pointer;
}
.custom-aspect-apply:hover { filter: brightness(1.08); }
.custom-aspect-error { margin: 6px 0 0; font-size: 11px; color: var(--danger); }

/* Vibrant attachment button — matches Apply button height + accent-tinted icon */
.prompt-attach-btn {
  display: inline-flex; align-items: center; justify-content: center;
  width: 36px; height: 36px;
  padding: 0;
  background: rgba(109,93,255,0.14);
  color: var(--accent);
  border: 1px solid rgba(109,93,255,0.32);
  border-radius: 8px;
  font-size: 16px; line-height: 1;
  cursor: pointer;
  transition: background .12s, border-color .12s, transform .08s;
  flex-shrink: 0;
}
.prompt-attach-btn:hover { background: rgba(109,93,255,0.22); border-color: rgba(109,93,255,0.50); }
.prompt-attach-btn:active { transform: scale(0.96); }

/* Empty state shown before the user has started a conversation */
.conversation-empty { padding: 24px 12px; text-align: center; color: var(--text-muted); }
.conversation-empty-title { font-size: 14px; font-weight: 600; color: var(--text-secondary); margin: 0 0 6px; }
.conversation-empty-body { font-size: 11px; line-height: 1.5; margin: 0; }

/* Quick-actions popover — anchors above the prompt input */
.quick-actions-popover { position: absolute; left: 0; right: 0; bottom: calc(100% + 6px); padding: 10px; background: var(--surface-1); border: var(--glass-border); border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,0.35); z-index: 4; }
.ai-prompt-kbd-active { background: var(--accent-soft) !important; color: var(--accent) !important; border-color: rgba(109,93,255,0.35) !important; }

/* Per-turn diff toggle + state UI */
.conversation-turn-diff-row { margin-top: 8px; }
.conversation-diff-toggle {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 4px 10px;
  font-size: 11px; font-weight: 600;
  color: var(--accent);
  background: rgba(109,93,255,0.08);
  border: 1px solid rgba(109,93,255,0.20);
  border-radius: 999px;
  cursor: pointer; transition: background .12s;
}
.conversation-diff-toggle:hover { background: rgba(109,93,255,0.14); }
.conversation-diff-loading { padding: 6px 10px; font-size: 11px; color: var(--text-muted); margin-top: 6px; }
.conversation-diff-error { padding: 6px 10px; font-size: 11px; color: var(--danger); background: rgba(248,113,113,0.06); border-radius: 6px; margin-top: 6px; display: flex; align-items: center; gap: 8px; }
.conversation-diff-retry { font-size: 11px; text-decoration: underline; color: var(--danger); background: none; border: none; cursor: pointer; padding: 0; }
.tree-item { display: flex; align-items: center; gap: 6px; padding: 6px 8px; border-radius: 8px; cursor: pointer; transition: all 0.12s; color: var(--text-secondary); font-size: 12px; }
.tree-item:hover { background: var(--glass-bg-hover); color: var(--text-primary); }
.tree-item.active { background: var(--accent-soft); color: var(--accent); border: 1px solid rgba(124,109,250,0.2); }
.tree-item .badge { font-size: 9px; padding: 2px 6px; border-radius: 10px; font-weight: 600; }
.badge-live { background: rgba(52,211,153,0.12); color: var(--success); }
.page-row { position: relative; }
.page-row-label { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.page-delete-btn { opacity: 0; width: 18px; height: 18px; border-radius: 4px; border: none; background: rgba(248,113,113,0.12); color: var(--danger); font-size: 14px; line-height: 1; cursor: pointer; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.12s; padding: 0; font-family: inherit; }
.page-row:hover .page-delete-btn { opacity: 1; }
.page-delete-btn:hover { background: rgba(248,113,113,0.25); }
.page-delete-btn:disabled { opacity: 0.5; cursor: default; }
.badge-draft { background: var(--glass-bg-active); color: var(--text-muted); }
.tree-child { padding-left: 20px; }
.wf-search-wrap { padding: 10px 0 6px; position: relative; }
.wf-search { width: 100%; padding: 6px 10px 6px 30px; background: var(--glass-bg); border: var(--glass-border); border-radius: 8px; color: var(--text-primary); font-size: 12px; outline: none; }
.wf-search::placeholder { color: var(--text-muted); }
.wf-search:focus { border: var(--glass-border-hi); }
.wf-search-icon { position: absolute; left: 8px; top: 50%; transform: translateY(-50%); color: var(--text-muted); font-size: 12px; margin-top: 3px; }
.workflow-card { display: flex; align-items: flex-start; padding: 10px 12px; border-radius: 8px; background: var(--glass-bg); border: var(--glass-border); cursor: pointer; transition: all 0.15s; margin-bottom: 6px; }
.workflow-card:hover { background: var(--glass-bg-hover); border: var(--glass-border-hi); box-shadow: var(--elev-sm); }
.workflow-body { flex: 1; min-width: 0; }
.workflow-title { font-size: 12px; font-weight: 600; color: var(--text-primary); margin-bottom: 2px; }
.workflow-desc { font-size: 11px; color: var(--text-muted); line-height: 1.45; }
.workflow-meta { display: flex; align-items: center; justify-content: space-between; margin-top: 5px; }
.workflow-badge { font-size: 9px; padding: 2px 6px; border-radius: 10px; background: var(--glass-bg-active); color: var(--text-muted); font-weight: 600; letter-spacing: 0.04em; text-transform: uppercase; }
.workflow-badge.guided { background: rgba(56,189,248,0.12); color: #38bdf8; }
.workflow-hint { font-size: 11px; color: var(--accent); opacity: 0; transition: opacity 0.15s; }
.workflow-card:hover .workflow-hint { opacity: 1; }
.workflow-empty { padding: 14px 12px; text-align: center; color: var(--text-muted); font-size: 12px; border-radius: 8px; background: var(--glass-bg); border: var(--glass-border); margin-bottom: 8px; }
.version-entry { padding: 8px 10px; border-radius: 8px; background: var(--glass-bg); border: var(--glass-border); margin-bottom: 6px; }
.version-entry-current { border-color: rgba(124,109,250,0.35); background: rgba(124,109,250,0.07); }
.version-meta { display: flex; align-items: center; gap: 6px; margin-bottom: 3px; }
.version-sha { font-size: 11px; font-family: monospace; background: rgba(124,109,250,0.15); color: var(--accent); padding: 1px 5px; border-radius: 4px; }
.version-time { font-size: 11px; color: var(--text-muted); margin-left: auto; }
.version-msg { font-size: 11px; color: var(--text-secondary); line-height: 1.4; margin-bottom: 4px; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
.version-bullets { margin: 0 0 6px; padding: 0 0 0 14px; list-style: disc; }
.version-bullets li { font-size: 11px; color: var(--text-muted); line-height: 1.5; margin-bottom: 1px; }
.version-bullets li:last-child { margin-bottom: 0; }
.version-restore-btn { font-size: 11px; padding: 3px 8px; border-radius: 5px; background: rgba(124,109,250,0.15); border: 1px solid rgba(124,109,250,0.3); color: var(--accent); cursor: pointer; transition: all 0.12s; }
.version-restore-btn:hover:not(:disabled) { background: rgba(124,109,250,0.25); }
.version-restore-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.version-current-badge { font-size: 11px; padding: 2px 7px; border-radius: 10px; background: rgba(34,197,94,0.15); color: #4ade80; border: 1px solid rgba(34,197,94,0.25); }
.ai-chip { margin: 10px 0 4px; padding: 10px 12px; background: linear-gradient(135deg,rgba(124,109,250,.15),rgba(167,139,250,.1)); border: 1px solid rgba(124,109,250,.25); border-radius: 12px; display: flex; align-items: center; gap: 8px; cursor: pointer; transition: all 0.15s; }
.ai-chip:hover { background: linear-gradient(135deg,rgba(124,109,250,.22),rgba(167,139,250,.15)); border-color: rgba(124,109,250,.4); }
.ai-dot { width: 26px; height: 26px; border-radius: 8px; flex-shrink: 0; background: linear-gradient(135deg,var(--accent),#a78bfa); display: flex; align-items: center; justify-content: center; font-size: 14px; box-shadow: 0 0 10px var(--accent-glow); }
.ai-text-block { flex: 1; }
.ai-title { font-size: 11px; font-weight: 600; color: var(--text-primary); }
.ai-sub { font-size: 11px; color: var(--text-muted); margin-top: 1px; }

#canvas {
  grid-column: 2;
  grid-row: 1;
  display: flex; flex-direction: column;
  background: rgba(8,8,16,0.5);
  overflow: hidden;
}
.canvas-toolbar { display: flex; align-items: center; gap: 6px; padding: 8px 16px; background: rgba(15,15,24,0.6); border-bottom: var(--glass-border); backdrop-filter: blur(12px); flex-shrink: 0; }
.canvas-toolbar-sep { width: 1px; height: 20px; background: var(--glass-border, rgba(255,255,255,0.1)); margin: 0 4px; flex-shrink: 0; }
.canvas-add-btn { display: inline-flex; align-items: center; gap: 4px; padding: 5px 10px; white-space: nowrap; flex-shrink: 0; }
.device-btn { padding: 5px 8px; border-radius: 8px; font-size: 14px; cursor: pointer; transition: all 0.12s; background: transparent; border: none; color: var(--text-muted); }
.device-btn:hover { color: var(--text-primary); background: var(--glass-bg-hover); }
.device-btn.active { color: var(--accent); background: var(--accent-soft); }
.canvas-url { flex: 1; margin: 0 8px; background: var(--glass-bg); border: var(--glass-border); border-radius: 8px; padding: 5px 10px; color: var(--text-secondary); font-size: 11px; display: flex; align-items: center; gap: 6px; overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.canvas-url-lock { color: var(--success); font-size: 11px; }
.zoom-control { display: flex; align-items: center; gap: 4px; background: var(--glass-bg); border: var(--glass-border); border-radius: 8px; padding: 4px 8px; color: var(--text-secondary); font-size: 11px; }
.zoom-btn { cursor: pointer; padding: 0 4px; font-size: 14px; line-height: 1; }
.zoom-btn:hover { color: var(--text-primary); }
.canvas-error-banner { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 6px 16px; font-size: 11px; color: var(--danger); background: rgba(248,113,113,.08); border-bottom: 1px solid rgba(248,113,113,.2); flex-shrink: 0; }
.canvas-error-reset { flex-shrink: 0; font-size: 11px; font-weight: 600; color: var(--danger); text-decoration: underline; text-underline-offset: 2px; cursor: pointer; background: none; border: none; padding: 2px 4px; }
.canvas-error-reset:hover:not(:disabled) { color: var(--text-primary); }
.canvas-error-reset:disabled { opacity: .6; cursor: default; text-decoration: none; }
.canvas-frame { flex: 1; overflow: auto; display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 0; }
.preview-shell { display: flex; flex-direction: column; flex-shrink: 0; overflow: hidden; background: #fff; box-shadow: 0 4px 24px rgba(0,0,0,0.18); }
.preview-shell iframe { flex: 1; min-height: 0; width: 100%; height: 100%; border: 0; display: block; }
.preview-chrome { display: flex; align-items: center; gap: 8px; padding: 10px 14px; background: #1e1e2e; border-bottom: 1px solid rgba(255,255,255,.06); }
.chrome-dots { display: flex; gap: 5px; }
.chrome-dot { width: 10px; height: 10px; border-radius: 50%; }
.chrome-url-bar { flex: 1; padding: 4px 10px; background: rgba(255,255,255,.07); border-radius: 5px; font-size: 11px; color: rgba(255,255,255,.5); overflow: hidden; white-space: nowrap; text-overflow: ellipsis; }
.canvas-deploying-toast { position: absolute; top: 60px; left: 50%; transform: translateX(-50%); padding: 6px 14px; border-radius: 20px; font-size: 11px; background: var(--accent-soft); color: var(--accent); border: var(--glass-border); }
.canvas-editing-banner { position: absolute; top: 12px; left: 12px; display: flex; align-items: center; gap: 7px; padding: 5px 8px 5px 12px; border-radius: 20px; font-size: 11px; color: var(--text-primary); background: var(--surface-1); backdrop-filter: var(--glass-blur); border: var(--glass-border-hi); box-shadow: var(--elev-md); z-index: 6; }
.canvas-editing-banner strong { color: var(--accent); font-weight: 600; }
.canvas-editing-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); box-shadow: 0 0 0 3px var(--accent-soft); flex-shrink: 0; }
.canvas-editing-sep { color: var(--text-muted); }
.canvas-editing-hint { color: var(--text-muted); }
.canvas-editing-close { display: inline-flex; align-items: center; justify-content: center; width: 18px; height: 18px; border-radius: 50%; color: var(--text-muted); font-size: 11px; line-height: 1; transition: background 0.15s, color 0.15s; }
.canvas-editing-close:hover { background: var(--glass-bg-hover); color: var(--text-primary); }
.inline-editing-header { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 8px; background: var(--accent-soft); border: 1px solid rgba(124,109,250,.2); }
.element-ai-edit { display: flex; flex-direction: column; gap: 8px; padding: 10px; border-radius: 10px; background: var(--accent-soft); border: 1px solid rgba(124,109,250,.2); }
.element-ai-label { display: flex; align-items: center; gap: 6px; font-size: 12px; font-weight: 600; color: var(--text-primary); }
.element-ai-label svg { color: var(--accent); flex-shrink: 0; }
.element-ai-label strong { color: var(--accent-light); }
.seo-row { display: flex; align-items: center; gap: 8px; padding: 8px 10px; border-radius: 8px; border: var(--glass-border); background: var(--glass-bg); cursor: pointer; transition: background 0.15s; }
.seo-row:hover { background: var(--glass-bg-hover); }
.seo-row.active { border-color: rgba(124,109,250,.35); background: var(--accent-soft); }
.seo-row-title { font-size: 12px; font-weight: 600; color: var(--text-primary); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.seo-row-meta { font-size: 11px; color: var(--text-secondary); }
.seo-badge { font-size: 9px; text-transform: uppercase; letter-spacing: .05em; font-weight: 700; padding: 2px 6px; border-radius: 999px; flex-shrink: 0; }
.seo-badge-ok { background: rgba(52,211,153,.12); color: var(--success); }
.seo-badge-warn { background: rgba(251,191,36,.12); color: var(--warning); }
.seo-editor { display: flex; flex-direction: column; gap: 4px; padding: 10px; margin: 2px 0 6px; border-radius: 8px; border: var(--glass-border); background: var(--surface-1); }
.seo-label { font-size: 11px; font-weight: 500; color: var(--text-secondary); margin-top: 6px; display: flex; align-items: center; justify-content: space-between; }
.seo-count { font-size: 10px; color: var(--text-muted); font-weight: 400; }
.seo-count.over { color: var(--warning); }
.seo-check { display: inline-flex; align-items: center; gap: 5px; font-size: 12px; color: var(--text-secondary); cursor: pointer; }
.seo-saved { font-size: 11px; color: var(--success); }
.seo-scores { display: flex; gap: 3px; }
.seo-score { font-size: 9px; font-weight: 700; letter-spacing: .02em; padding: 1px 5px; border-radius: 5px; color: var(--c); background: color-mix(in srgb, var(--c) 14%, transparent); white-space: nowrap; }
.seo-kw-row { display: flex; flex-wrap: wrap; gap: 4px; margin-top: 4px; }
.seo-kw { font-size: 9px; color: var(--text-muted); background: var(--glass-bg); border: var(--glass-border); padding: 1px 6px; border-radius: 999px; }
/* SEO right-panel layout (settings-style) */
.seo-panel { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.seo-panel-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 8px; padding: 14px 16px 12px; border-bottom: var(--glass-border); flex-shrink: 0; }
.seo-panel-title { font-size: 14px; font-weight: 700; color: var(--text-primary); letter-spacing: -0.01em; }
.seo-panel-sub { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
.seo-icon-btn { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; border-radius: 7px; border: var(--glass-border); background: var(--glass-bg); color: var(--text-secondary); cursor: pointer; transition: background .15s, color .15s; }
.seo-icon-btn:hover:not(:disabled) { background: var(--glass-bg-hover); color: var(--text-primary); }
.seo-icon-btn:disabled { opacity: .5; cursor: default; }
.seo-spin { animation: seo-spin 0.9s linear infinite; }
@keyframes seo-spin { to { transform: rotate(360deg); } }
.seo-panel-body { flex: 1; overflow-y: auto; padding: 12px 16px 20px; }
.seo-empty { padding: 24px 4px; color: var(--text-muted); font-size: 12px; text-align: center; }
.seo-list { display: flex; flex-direction: column; gap: 8px; }
.seo-section-label { font-size: 10px; text-transform: uppercase; letter-spacing: .06em; font-weight: 700; color: var(--text-muted); margin: 14px 0 2px; padding-bottom: 5px; border-bottom: var(--glass-border); }
.seo-section-label:first-child { margin-top: 2px; }
.seo-actions { display: flex; gap: 8px; align-items: center; padding-top: 14px; margin-top: 6px; border-top: var(--glass-border); }
.seo-hint { font-size: 10px; color: var(--text-muted); margin-top: 8px; line-height: 1.5; }
.btn-active { background: var(--accent-soft); border-color: rgba(124,109,250,.4); color: var(--accent); }
.inline-editing-dot { width: 7px; height: 7px; border-radius: 50%; background: var(--accent); flex-shrink: 0; }
.inline-staged-note { font-size: 11px; line-height: 1.5; padding: 7px 10px; border-radius: 8px; color: var(--text-secondary); background: var(--accent-soft); border: 1px solid rgba(124,109,250,.2); }
.inline-staged-note strong { color: var(--text-primary); }
.canvas-no-preview { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; text-align: center; padding: 40px; }

/* Inline-edit panel is now a FLOATING POPUP over the canvas (appears while
   editing inline) instead of a docked right rail — the canvas gets the space. */
/* Editor actions teleported into the app nav — lay out inline, no bar chrome. */
.editor-nav-actions { display: flex; align-items: center; gap: 8px; }
.editor-nav-actions .breadcrumb { font-size: 13px; }
.editor-nav-actions .breadcrumb span { color: var(--text-primary); font-weight: 600; }

#context-panel {
  position: fixed;
  /* Clears the app nav (52px) + the canvas toolbar (~44px) above the canvas. */
  top: 108px;
  right: 16px;
  width: 340px;
  max-height: calc(100vh - 160px);
  z-index: 60;
  overflow: hidden; display: flex; flex-direction: column;
  border: var(--glass-border);
  border-radius: 14px;
  background: var(--surface-1);
  backdrop-filter: var(--glass-blur);
  -webkit-backdrop-filter: var(--glass-blur);
  box-shadow: 0 12px 40px rgba(0,0,0,0.45), 0 0 0 1px rgba(255,255,255,0.04);
  padding-bottom: 8px;
  animation: context-pop 0.16s ease-out;
}
@keyframes context-pop { from { opacity: 0; transform: translateY(-6px) scale(0.98); } to { opacity: 1; transform: none; } }
@media (prefers-reduced-motion: reduce) { #context-panel { animation: none; } }
.context-popup-close {
  position: absolute; top: 8px; right: 8px; z-index: 2;
  width: 24px; height: 24px; border-radius: 7px;
  display: flex; align-items: center; justify-content: center;
  background: var(--glass-bg); border: var(--glass-border); color: var(--text-muted);
  cursor: pointer; transition: all 0.12s;
}
.context-popup-close:hover { background: var(--glass-bg-hover); color: var(--text-primary); }
.context-empty { flex: 1; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; padding: 24px; text-align: center; }
.context-empty-icon { width: 52px; height: 52px; border-radius: 14px; background: var(--accent-soft); border: 1px solid rgba(124,109,250,.25); display: flex; align-items: center; justify-content: center; font-size: 22px; color: var(--accent); animation: pulse-border 2.5s ease-in-out infinite; }
@keyframes pulse-border { 0%,100%{box-shadow:0 0 0 0 var(--accent-glow);}50%{box-shadow:0 0 0 6px transparent;} }
.context-empty-title { font-size: 12px; font-weight: 600; color: var(--text-secondary); line-height: 1.5; }
.context-empty-sub { font-size: 11px; color: var(--text-muted); line-height: 1.5; }
.context-active { display: flex; flex-direction: column; flex: 1; overflow: hidden; }
.context-header { display: flex; align-items: center; justify-content: space-between; padding: 14px 14px 10px; border-bottom: var(--glass-border); flex-shrink: 0; }
.context-section-name { font-size: 14px; font-weight: 700; color: var(--text-primary); }
.context-subtitle { font-size: 11px; color: var(--text-muted); margin-top: 2px; }
.context-close { width: 22px; height: 22px; border-radius: 6px; display: flex; align-items: center; justify-content: center; cursor: pointer; color: var(--text-muted); font-size: 14px; transition: all 0.12s; }
.context-close:hover { background: var(--glass-bg-hover); color: var(--text-primary); }
.context-body { flex: 1; overflow-y: auto; padding: 14px; }
.context-body::-webkit-scrollbar { width: 4px; }
.context-body::-webkit-scrollbar-thumb { background: rgba(255,255,255,.1); border-radius: 4px; }
.context-textarea { width: 100%; padding: 10px; background: var(--glass-bg); border: var(--glass-border); border-radius: 8px; color: var(--text-primary); font-size: 12px; outline: none; font-family: inherit; line-height: 1.5; display: block; }
.context-textarea:focus { border: var(--glass-border-hi); background: var(--glass-bg-hover); }
.context-apply-btn { width: 100%; padding: 9px 12px; background: var(--accent); color: #fff; border: none; border-radius: 8px; font-size: 12px; font-weight: 600; cursor: pointer; display: flex; align-items: center; justify-content: center; gap: 6px; box-shadow: 0 0 16px var(--accent-glow); transition: all 0.15s; font-family: inherit; }
.context-apply-btn:hover { background: #6d5ef0; }
.context-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-bottom: 12px; }
.context-chip { padding: 5px 10px; border-radius: 20px; font-size: 11px; font-weight: 500; background: var(--glass-bg); border: var(--glass-border); color: var(--text-secondary); display: inline-block; cursor: pointer; transition: all 0.15s; white-space: nowrap; font-family: inherit; }
.context-chip:hover { background: var(--accent-soft); border-color: rgba(124,109,250,.35); color: var(--accent); }
.context-chip-accent { background: var(--accent-soft); border-color: rgba(124,109,250,.25); color: var(--accent); }
.context-chip-accent:hover { background: rgba(124,109,250,.2); border-color: rgba(124,109,250,.45); }

.editor-modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 120;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
}
.editor-modal-backdrop {
  position: absolute;
  inset: 0;
  background: rgba(4,4,10,.72);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
}
.editor-modal-card {
  position: relative;
  width: min(520px, calc(100vw - 32px));
  max-height: calc(100vh - 48px);
  overflow: hidden;
  background: linear-gradient(145deg,rgba(22,22,38,.96),rgba(15,15,24,.94));
  border: var(--glass-border-hi);
  border-radius: 18px;
  box-shadow: var(--elev-lg), 0 0 0 1px rgba(124,109,250,.08);
  display: flex;
  flex-direction: column;
}
.editor-modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 16px 16px 12px;
  border-bottom: var(--glass-border);
}
.editor-modal-body {
  padding: 16px;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.editor-modal-label {
  font-size: 11px;
  color: var(--text-secondary);
  font-weight: 600;
  margin-top: 6px;
}
.editor-modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: var(--glass-border);
  background: rgba(10,10,18,.72);
}

/* Inline Editor collapsible section */
.inline-collapse { flex-shrink: 0; border-top: var(--glass-border); }
.inline-collapse.expanded { flex: 1; overflow: hidden; display: flex; flex-direction: column; }
.inline-collapse-toggle { width: 100%; display: flex; align-items: center; gap: 8px; padding: 10px 14px; background: none; border: none; cursor: pointer; color: var(--text-primary); font-family: inherit; transition: background 0.15s; }
.inline-collapse-toggle:hover { background: var(--glass-bg); }

/* Edit mode nudge */
.inline-edit-nudge { margin-top: 16px; padding: 12px; border-radius: 10px; background: var(--glass-bg); border: var(--glass-border); text-align: center; }
.inline-edit-nudge-text { font-size: 11px; color: var(--text-muted); margin-bottom: 8px; }
.inline-edit-nudge-btn { padding: 6px 14px; border-radius: 20px; font-size: 11px; font-weight: 600; background: var(--accent-soft); border: 1px solid rgba(124,109,250,.3); color: var(--accent); cursor: pointer; font-family: inherit; transition: all 0.15s; }
.inline-edit-nudge-btn:hover { background: rgba(124,109,250,.2); border-color: rgba(124,109,250,.5); }

.context-suggest-chips { display: flex; flex-wrap: wrap; gap: 5px; }
.context-suggest-chip { padding: 4px 10px; border-radius: 20px; font-size: 11px; background: var(--accent-soft); border: 1px solid rgba(124,109,250,.2); color: var(--accent); }
.context-sep { display: flex; align-items: center; gap: 8px; margin: 12px 0; }
.context-sep-line { flex: 1; height: 1px; background: rgba(255,255,255,.08); }
.context-sep-text { font-size: 11px; color: var(--text-muted); white-space: nowrap; }
.activity-step { display: flex; align-items: flex-start; gap: 7px; font-size: 11px; color: var(--text-muted); line-height: 1.4; padding: 4px 8px; border-radius: 6px; background: rgba(52,211,153,.04); border: 1px solid rgba(52,211,153,.1); }
.activity-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
.activity-dot-running { background: var(--accent); animation: pulse-dot 1.5s ease-in-out infinite; }
.activity-dot-done { background: var(--success); box-shadow: 0 0 6px rgba(52,211,153,.5); }
.activity-dot-error { background: var(--danger); }
@keyframes pulse-dot { 0%,100%{opacity:1} 50%{opacity:.4} }

.ai-prompt-bar {
  position: fixed; bottom: 36px; left: 50%; transform: translateX(-50%);
  width: min(560px, calc(100vw - 32px));
  padding: 10px 12px;
  background: rgba(22,22,38,0.92);
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border: var(--glass-border-hi);
  border-radius: 14px;
  box-shadow: var(--elev-lg), 0 0 0 1px rgba(124,109,250,.1);
  display: flex; align-items: center; gap: 10px;
  z-index: 100;
}
.ai-prompt-icon { width: 28px; height: 28px; border-radius: 8px; flex-shrink: 0; background: linear-gradient(135deg,var(--accent),#a78bfa); display: flex; align-items: center; justify-content: center; font-size: 14px; box-shadow: 0 0 10px var(--accent-glow); }
.ai-prompt-input { flex: 1; background: transparent; border: none; outline: none; color: var(--text-primary); font-size: 14px; font-family: inherit; }
.ai-prompt-input::placeholder { color: var(--text-muted); }
.ai-prompt-actions { display: flex; align-items: center; gap: 6px; }
.ai-prompt-kbd { padding: 2px 6px; border-radius: 4px; background: rgba(255,255,255,.08); color: var(--text-muted); font-size: 11px; font-family: monospace; cursor: pointer; border: none; }
.ai-send-btn { width: 28px; height: 28px; border-radius: 8px; background: var(--accent); border: none; cursor: pointer; display: flex; align-items: center; justify-content: center; font-size: 14px; color: #fff; box-shadow: 0 0 12px var(--accent-glow); transition: all 0.15s; flex-shrink: 0; }
.ai-send-btn:hover { background: #6d5ef0; transform: scale(1.05); }
.ai-send-btn:disabled { opacity: 0.5; pointer-events: none; }
.ai-attachments-bar { position: fixed; bottom: 100px; left: 50%; transform: translateX(-50%); display: flex; align-items: center; gap: 10px; padding: 8px 12px; background: rgba(22,22,38,.92); backdrop-filter: blur(24px); border: var(--glass-border-hi); border-radius: 10px; z-index: 99; }
.ai-ask-history { position: fixed; bottom: 96px; left: 50%; transform: translateX(-50%); width: min(560px, calc(100vw - 32px)); max-height: 340px; overflow-y: auto; padding: 12px 14px; background: rgba(22,22,38,0.94); backdrop-filter: blur(24px); -webkit-backdrop-filter: blur(24px); border: var(--glass-border-hi); border-radius: 14px; box-shadow: var(--elev-lg); z-index: 99; }
.conversation-load-more {
  align-self: center;
  padding: 5px 10px;
  border-radius: 999px;
  border: 1px solid rgba(124,109,250,.22);
  background: rgba(124,109,250,.08);
  color: var(--accent);
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
}
.conversation-load-more:disabled { opacity: .55; cursor: default; }
.conversation-turn-confirmation {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid rgba(124,109,250,.24);
  background: rgba(124,109,250,.08);
}
.conversation-turn-confirmation-copy {
  margin: 0;
  color: var(--text-secondary);
  font-size: 12px;
  line-height: 1.5;
}
.ask-answer-body { flex: 1; min-width: 0; font-size: 12px; color: var(--text-secondary); line-height: 1.6; word-break: break-word; }
.ask-answer-body .ask-p { margin: 0 0 4px; }
.ask-answer-body .ask-p:last-child { margin-bottom: 0; }
.ask-answer-body .ask-heading { font-size: 11px; font-weight: 700; color: var(--text-primary); margin: 8px 0 2px; }
.ask-answer-body .ask-list { margin: 4px 0 4px 14px; padding: 0; display: flex; flex-direction: column; gap: 2px; }
.ask-answer-body li { list-style: disc; padding-left: 2px; }
.ask-answer-body ol.ask-list li { list-style: decimal; }
.ask-answer-body .ask-inline-code { background: rgba(255,255,255,.1); padding: 1px 5px; border-radius: 4px; font-size: 11px; font-family: monospace; color: var(--text-primary); }

#statusbar {
  position: fixed; bottom: 0; left: 0; right: 0; z-index: 50;
  display: flex; align-items: center; gap: 12px;
  padding: 0 16px; height: 26px;
  background: rgba(10,10,20,0.9);
  border-top: var(--glass-border);
  font-size: 11px; color: var(--text-muted);
}
.statusbar-sep { color: rgba(255,255,255,.08); }
.statusbar-brand { display: inline-flex; align-items: center; gap: 5px; }
.statusbar-brand img { width: 112px; height: 18px; object-fit: contain; }

/* Remove old 2-column classes that conflict */
.editor-header, .editor-body, .editor-left-panel, .editor-preview-area, .editor-preview-toolbar, .editor-inline-panel { all: unset; }

/* ── Responsive editor ── */
.topbar-sidebar-toggle { display: none; }

/* Sidebar/panel hidden state */
#sidebar.sidebar-hidden { display: none; }
#context-panel.panel-hidden { display: none; }

/* When sidebar hidden, adjust grid (third column is always 0 — panel floats). */
.editor-shell:has(#sidebar.sidebar-hidden) {
  grid-template-columns: 0 1fr 0;
}

@media (max-width: 1100px) {
  .editor-shell { grid-template-columns: 260px 1fr 0; }
  .topbar-sidebar-toggle { display: inline-flex; }
}

@media (max-width: 768px) {
  .editor-shell { grid-template-columns: 0 1fr 0; }
  #sidebar { display: none; }
  #context-panel { display: none; }
  .topbar-sidebar-toggle { display: inline-flex; }
  /* Sidebar as overlay on mobile */
  #sidebar.sidebar-hidden { display: none; }
  #sidebar:not(.sidebar-hidden) {
    display: flex;
    position: fixed;
    top: 52px; left: 0; bottom: 26px;
    width: 260px;
    z-index: 50;
    box-shadow: 4px 0 24px rgba(0,0,0,0.5);
  }
  .ai-prompt-bar { width: calc(100vw - 32px); }
}

@media (max-width: 480px) {
  #topbar { padding: 0 10px; gap: 4px; }
  .breadcrumb { display: none; }
  .topbar-divider { display: none; }
}
</style>
